import { useState, useEffect, useCallback, useMemo } from "react";
import { api, TokenStore } from "./api";

// ─── EMPLOYEE TAB CONFIG ─────────────────────────────────────────────────────
const EMP_TABS  = ["Home", "Attendance", "Leaves", "Profile"];
const EMP_ICONS = ["⬡", "≡", "◷", "◎"];

// ─────────────────────────────────────────────────────────────────────────────
// AttendTrack — Unified App
// Single login page with Employee / HR tabs
// After login shows correct dashboard in same page
// ─────────────────────────────────────────────────────────────────────────────

// EmployeePortal components

// ─── RESPONSIVE HOOK ──────────────────────────────────────────────────────────
function useBreakpoint() {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const fn = () => setW(window.innerWidth);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return { isMobile: w < 768, isTablet: w < 1024, w };
}

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const D = {
  bg: "#080c12", surface: "#111620", border: "rgba(14,165,233,0.12)",
  borderH: "rgba(14,165,233,0.28)", accent: "#0ea5e9", accentD: "#0284c7",
  text: "#e8f4fb", muted: "#4a6275", dim: "#2a3a4a",
  green: "#22c55e", red: "#ef4444", amber: "#f59e0b", purple: "#8b5cf6", blue: "#60a5fa",
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
// Format minutes into "45m" if < 60, or "1h 30m" if >= 60
function fmtMins(mins) {
  if (!mins || mins <= 0) return "0m";
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// Format decimal hours into "9h 45m" or "30m" or "0h"
function fmtHours(hours) {
  if (!hours || hours <= 0) return "0h";
  const totalMins = Math.round(parseFloat(hours) * 60);
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  if (h === 0) return `${m}m`;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// ─── SHARED COMPONENTS ────────────────────────────────────────────────────────
function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return now;
}

function StatusPill({ status }) {
  const cfg = {
    present:  { bg: "#052e16", color: D.green,  label: "Present"  },
    late:     { bg: "#1c0a00", color: D.amber,  label: "Late"     },
    absent:   { bg: "#1a0000", color: D.red,    label: "Absent"   },
    approved: { bg: "#052e16", color: D.green,  label: "Approved" },
    pending:  { bg: "#0c1a2e", color: D.blue,   label: "Pending"  },
    rejected: { bg: "#1a0000", color: D.red,    label: "Rejected" },
    leave:    { bg: "#0c0c2e", color: D.purple, label: "On Leave" },
    week_off: { bg: "#0f0a1e", color: "#a78bfa", label: "Week Off" },
  }[status] || { bg: "#111", color: D.muted, label: status || "—" };
  return (
    <span style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}30`,
      borderRadius: 99, padding: "2px 10px", fontSize: 11, fontWeight: 700,
      fontFamily: "'DM Mono',monospace", letterSpacing: 0.5, whiteSpace: "nowrap" }}>
      {cfg.label}
    </span>
  );
}

function Card({ children, style = {}, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <div onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ background: hov && onClick ? "#161c28" : D.surface,
        border: `1px solid ${style.borderColor || (hov && onClick ? D.borderH : D.border)}`,
        borderRadius: 14, padding: 16, cursor: onClick ? "pointer" : "default",
        transition: "all 0.2s", ...style, borderColor: undefined }}>
      {children}
    </div>
  );
}

function Label({ children }) {
  return <p style={{ fontFamily: "'DM Mono',monospace", color: D.muted, fontSize: 10,
    letterSpacing: 1.5, textTransform: "uppercase", margin: "0 0 6px" }}>{children}</p>;
}

function ErrorBox({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)",
      borderRadius: 10, padding: "10px 14px", display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ color: D.red }}>⚠</span>
      <span style={{ color: D.red, fontFamily: "'Sora',sans-serif", fontSize: 13 }}>{msg}</span>
    </div>
  );
}

function Btn({ children, onClick, disabled, variant = "primary", style = {} }) {
  const vs = {
    primary:   { background: `linear-gradient(135deg,${D.accent},${D.accentD})`, color: "#fff", border: "none", boxShadow: "0 4px 14px rgba(14,165,233,0.3)" },
    secondary: { background: "rgba(255,255,255,0.04)", color: D.muted, border: `1px solid ${D.border}`, boxShadow: "none" },
    danger:    { background: "rgba(239,68,68,0.1)", color: D.red, border: "1px solid rgba(239,68,68,0.25)", boxShadow: "none" },
    success:   { background: "linear-gradient(135deg,#22c55e,#16a34a)", color: "#042a1c", border: "none", boxShadow: "0 4px 14px rgba(34,197,94,0.25)" },
  };
  const s = vs[variant] || vs.primary;
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ ...s, borderRadius: 10, padding: "11px 20px",
        fontFamily: "'DM Mono',monospace", fontSize: 13, fontWeight: 700,
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
        transition: "all 0.2s", display: "inline-flex", alignItems: "center",
        justifyContent: "center", gap: 8, ...style }}>
      {children}
    </button>
  );
}

// ─── LOGIN PAGE ───────────────────────────────────────────────────────────────
function LoginPage({ onEmployeeLogin, onHRLogin, defaultTab = "employee" }) {
  const { isMobile } = useBreakpoint();
  const [mode, setMode]       = useState(defaultTab);
  const [email, setEmail]     = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [focusedField, setFocusedField] = useState(null);
  const [tenantId, setTenantId] = useState(() => TokenStore.getTenantId() || "");

  const switchMode = (m) => { setMode(m); setError(""); setEmail(""); setPassword(""); };

  const handleLogin = async () => {
    if (!tenantId.trim()) { setError("Please enter your Organisation ID"); return; }
    if (!email || !password) { setError("Please enter email and password"); return; }
    setLoading(true); setError("");
    try {
      // Save tenant before login so header is sent correctly
      TokenStore.saveTenantId(tenantId.trim());
      const data = await api.login(email, password);

      if (mode === "employee") {
        // Block only hr_admin from employee tab
        // store_manager can also use employee portal for their own attendance
        if (data.role_type === "hr_admin") {
          setError("This is an HR Admin account. Please use the HR / Manager tab.");
          setLoading(false);
          return;
        }
        TokenStore.saveUser({ email, role_type: data.role_type });
        onEmployeeLogin({
          name:        data.name || email.split("@")[0].replace(/[._]/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
          email:       data.email || email,
          role:        "Employee",
          department:  "",
          office:      "",
          shift:       "09:00 – 18:00",
          joining_date:"",
          avatar:      (data.name || email).slice(0, 2).toUpperCase(),
          role_type:   data.role_type,
          plan:        data.plan,
          tenant:      { name: "Your Organisation" },
        });
      } else {
        // HR/Manager tab — only allow hr_admin and store_manager
        if (data.role_type !== "hr_admin" && data.role_type !== "store_manager") {
          setError("This account does not have HR or Manager access. Please use the Employee tab.");
          setLoading(false);
          return;
        }
        TokenStore.saveUser({ email, role_type: data.role_type });
        onHRLogin({
          name:        data.name || email.split("@")[0].replace(/[._]/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
          email:       data.email || email,
          role:        data.role_type === "hr_admin" ? "HR Admin" : "Store Manager",
          role_type:   data.role_type,
          avatar:      (data.name || email).slice(0, 2).toUpperCase(),
          plan:        data.plan,
          assignedOffices: [],
          tenant:      { name: "Your Organisation", tenant_id: "" },
        });
      }
    } catch (err) {
      setError(err.detail || err.error || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: D.bg, display: "flex", alignItems: "center",
      justifyContent: "center", padding: isMobile ? "20px 16px" : "32px",
      position: "relative", overflow: "hidden", fontFamily: "'Sora',sans-serif" }}>
      <div style={{ position: "absolute", inset: 0,
        backgroundImage: `linear-gradient(rgba(14,165,233,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(14,165,233,0.04) 1px,transparent 1px)`,
        backgroundSize: "36px 36px" }} />
      <div style={{ position: "absolute", top: "5%", left: "50%", transform: "translateX(-50%)",
        width: isMobile ? 280 : 560, height: isMobile ? 280 : 560, borderRadius: "50%",
        background: "radial-gradient(circle,rgba(14,165,233,0.07) 0%,transparent 65%)", pointerEvents: "none" }} />

      <div style={{ position: "relative", width: "100%", maxWidth: 420, zIndex: 1 }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ width: isMobile ? 48 : 56, height: isMobile ? 48 : 56, borderRadius: 16,
            background: `linear-gradient(135deg,${D.accent},${D.accentD})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 12px", boxShadow: "0 0 28px rgba(14,165,233,0.3)", fontSize: isMobile ? 20 : 24 }}>⬡</div>
          <h1 style={{ fontFamily: "'DM Mono',monospace", color: D.text, fontSize: isMobile ? 16 : 20, margin: "0 0 4px" }}>
            AttendTrack
          </h1>
          <p style={{ fontFamily: "'Sora',sans-serif", color: D.muted, fontSize: 12, margin: 0 }}>
            Attendance Portal
          </p>
        </div>

        {/* Mode switcher */}
        <div style={{ display: "flex", background: "rgba(255,255,255,0.03)",
          border: `1px solid ${D.border}`, borderRadius: 12, padding: 4, marginBottom: 18, gap: 4 }}>
          {[
            { key: "employee", icon: "👤", label: "Employee" },
            { key: "hr",       icon: "◈",  label: "HR / Manager" },
          ].map(({ key, icon, label }) => (
            <button key={key} onClick={() => switchMode(key)}
              style={{ flex: 1, padding: isMobile ? "9px 6px" : "10px 8px", borderRadius: 9,
                border: "none", cursor: "pointer", transition: "all 0.25s",
                background: mode === key ? `linear-gradient(135deg,${D.accent},${D.accentD})` : "transparent",
                color: mode === key ? "#fff" : D.muted,
                fontFamily: "'DM Mono',monospace", fontSize: isMobile ? 11 : 12,
                fontWeight: mode === key ? 700 : 400,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                boxShadow: mode === key ? "0 2px 10px rgba(14,165,233,0.25)" : "none" }}>
              <span>{icon}</span>{label}
            </button>
          ))}
        </div>

        {/* Card */}
        <div style={{ background: D.surface, border: `1px solid ${D.borderH}`,
          borderRadius: 20, padding: isMobile ? "18px 16px" : "26px 28px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }}>
          <h2 style={{ fontFamily: "'DM Mono',monospace", color: D.text,
            fontSize: 14, margin: "0 0 18px", textAlign: "center" }}>
            {mode === "employee" ? "Employee Sign In" : "HR / Manager Sign In"}
          </h2>

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Error / warning */}
            {error && (() => {
              const isWarn  = /remaining|attempt|locked/i.test(error);
              const clr     = isWarn ? "#f59e0b" : D.red;
              const bgClr   = isWarn ? "rgba(245,158,11,0.08)"  : "rgba(239,68,68,0.08)";
              const bdrClr  = isWarn ? "1px solid rgba(245,158,11,0.3)" : "1px solid rgba(239,68,68,0.25)";
              return (
                <div style={{ background: bgClr, border: bdrClr,
                  borderRadius: 10, padding: "10px 14px", display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: clr }}>{isWarn ? "⏱" : "⚠"}</span>
                  <span style={{ color: clr, fontFamily: "'Sora',sans-serif", fontSize: 13 }}>{error}</span>
                </div>
              );
            })()}

            {/* Organisation ID */}
            <div>
              <Label>Organisation ID <span style={{ color: D.dim, fontSize: 9, textTransform: "none", letterSpacing: 0 }}>(remembered)</span></Label>
              <input type="text" value={tenantId}
                onChange={e => {
                  const val = e.target.value.replace(/[^a-zA-Z0-9_-]/g,"");
                  setTenantId(val); setError("");
                  if (val) TokenStore.saveTenantId(val);
                }}
                onFocus={() => setFocusedField("tenant")} onBlur={() => setFocusedField(null)}
                onKeyDown={e => e.key === "Enter" && handleLogin()}
                placeholder="e.g. COMP2601"
                style={{ width: "100%", boxSizing: "border-box", outline: "none",
                  background: focusedField === "tenant" ? "rgba(14,165,233,0.05)" : "rgba(255,255,255,0.02)",
                  border: `1px solid ${focusedField === "tenant" ? D.borderH : D.border}`,
                  borderRadius: 10, padding: "11px 14px", color: D.text,
                  fontFamily: "'Sora',sans-serif", fontSize: 14, transition: "all 0.2s" }} />
            </div>

            {/* Email */}
            <div>
              <Label>Work Email</Label>
              <input type="email" value={email}
                onChange={e => { setEmail(e.target.value); setError(""); }}
                onFocus={() => setFocusedField("email")} onBlur={() => setFocusedField(null)}
                onKeyDown={e => e.key === "Enter" && handleLogin()}
                placeholder="you@company.com"
                style={{ width: "100%", boxSizing: "border-box", outline: "none",
                  background: focusedField === "email" ? "rgba(14,165,233,0.05)" : "rgba(255,255,255,0.02)",
                  border: `1px solid ${focusedField === "email" ? D.borderH : D.border}`,
                  borderRadius: 10, padding: "11px 14px", color: D.text,
                  fontFamily: "'Sora',sans-serif", fontSize: 14, transition: "all 0.2s" }} />
            </div>

            {/* Password */}
            <div>
              <Label>Password</Label>
              <input type="password" value={password}
                onChange={e => { setPassword(e.target.value); setError(""); }}
                onFocus={() => setFocusedField("pass")} onBlur={() => setFocusedField(null)}
                onKeyDown={e => e.key === "Enter" && handleLogin()}
                placeholder="••••••••"
                style={{ width: "100%", boxSizing: "border-box", outline: "none",
                  background: focusedField === "pass" ? "rgba(14,165,233,0.05)" : "rgba(255,255,255,0.02)",
                  border: `1px solid ${focusedField === "pass" ? D.borderH : D.border}`,
                  borderRadius: 10, padding: "11px 14px", color: D.text,
                  fontFamily: "'Sora',sans-serif", fontSize: 14, transition: "all 0.2s" }} />
            </div>

            <div style={{ textAlign: "right", marginTop: -6 }}>
              <span style={{ color: D.accent, fontSize: 12, cursor: "pointer" }}>Forgot password?</span>
            </div>

            {/* Sign In button */}
            <button onClick={handleLogin} disabled={!email || !password || loading}
              style={{ width: "100%", padding: "13px", borderRadius: 10, border: "none",
                background: !email || !password ? "rgba(255,255,255,0.05)"
                  : loading ? "rgba(14,165,233,0.3)"
                  : `linear-gradient(135deg,${D.accent},${D.accentD})`,
                color: !email || !password ? D.dim : "#fff",
                fontFamily: "'DM Mono',monospace", fontSize: 13, fontWeight: 700,
                cursor: !email || !password ? "not-allowed" : "pointer",
                opacity: !email || !password ? 0.5 : 1,
                transition: "all 0.3s",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              {loading
                ? <><div style={{ width: 14, height: 14, borderRadius: "50%",
                    border: "2px solid #fff", borderTopColor: "transparent",
                    animation: "spin 0.7s linear infinite" }} />Signing in…</>
                : mode === "employee" ? "Sign In as Employee →" : "Sign In as HR / Manager →"
              }
            </button>

            {/* Info note for HR tab */}
            {mode === "hr" && (
              <div style={{ background: "rgba(14,165,233,0.06)", border: `1px solid ${D.border}`,
                borderRadius: 10, padding: "10px 14px", textAlign: "center" }}>
                <p style={{ color: D.muted, fontFamily: "'DM Mono',monospace", fontSize: 11, margin: 0 }}>
                  ◈ You will be taken to the HR Dashboard after sign in
                </p>
              </div>
            )}
          </div>
        </div>

        <p style={{ textAlign: "center", color: D.dim, fontSize: 11, marginTop: 14 }}>
          Powered by <span style={{ color: D.accent, fontFamily: "'DM Mono',monospace" }}>AttendTrack</span>
        </p>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}


function TopNav({ employee, activeTab, setActiveTab, onLogout, tabs }) {
  return (
    <div style={{ background: D.surface, borderBottom: `1px solid ${D.border}`,
      padding: "0 24px", display: "flex", alignItems: "center", height: 56, flexShrink: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginRight: 28 }}>
        <div style={{ width: 26, height: 26, borderRadius: 7,
          background: `linear-gradient(135deg,${D.accent},${D.accentD})`,
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>⬡</div>
        <div>
          <span style={{ fontFamily: "'DM Mono',monospace", color: D.text, fontSize: 12 }}>AttendTrack</span>
          <div style={{ fontSize: 9, color: D.muted, fontFamily: "'DM Mono',monospace" }}>{employee?.tenant?.name || "AttendTrack"}</div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 4, flex: 1 }}>
        {tabs.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            style={{ background: activeTab === tab ? "rgba(14,165,233,0.1)" : "transparent",
              border: `1px solid ${activeTab === tab ? D.borderH : "transparent"}`,
              borderRadius: 8, padding: "6px 14px",
              color: activeTab === tab ? D.accent : D.muted,
              fontFamily: "'DM Mono',monospace", fontSize: 12, cursor: "pointer", transition: "all 0.2s" }}>
            {tab}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ textAlign: "right" }}>
          <p style={{ fontFamily: "'DM Mono',monospace", color: D.text, fontSize: 12, margin: 0 }}>{employee.name}</p>
          <p style={{ color: D.muted, fontSize: 10, margin: 0 }}>{employee.role}</p>
        </div>
        <div style={{ width: 30, height: 30, borderRadius: 8,
          background: "rgba(14,165,233,0.15)", border: `1px solid ${D.border}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: D.accent, fontFamily: "'DM Mono',monospace", fontSize: 12, fontWeight: 700 }}>
          {employee.avatar}
        </div>
        <Btn onClick={onLogout} variant="danger" style={{ padding: "6px 12px", fontSize: 11 }}>Sign Out</Btn>
      </div>
    </div>
  );
}

// ─── MOBILE HEADER ────────────────────────────────────────────────────────────
function MobileHeader({ employee, activeTab, onLogout }) {
  return (
    <div style={{ background: D.surface, borderBottom: `1px solid ${D.border}`,
      padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
        <div style={{ width: 24, height: 24, borderRadius: 6,
          background: `linear-gradient(135deg,${D.accent},${D.accentD})`,
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11 }}>⬡</div>
        <div>
          <p style={{ fontFamily: "'DM Mono',monospace", color: D.accent, fontSize: 11, fontWeight: 700, margin: 0 }}>
            {activeTab}
          </p>
          <p style={{ color: D.muted, fontSize: 9, margin: 0 }}>{employee.name}</p>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ width: 28, height: 28, borderRadius: 7,
          background: "rgba(14,165,233,0.15)", border: `1px solid ${D.border}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: D.accent, fontFamily: "'DM Mono',monospace", fontSize: 10, fontWeight: 700 }}>
          {employee.avatar}
        </div>
        <button onClick={onLogout} style={{ background: "rgba(239,68,68,0.1)",
          border: "1px solid rgba(239,68,68,0.2)", borderRadius: 6,
          padding: "4px 9px", color: D.red, fontFamily: "'DM Mono',monospace", fontSize: 10, cursor: "pointer" }}>
          Out
        </button>
      </div>
    </div>
  );
}

// ─── BOTTOM NAV (mobile) ──────────────────────────────────────────────────────
function BottomNav({ activeTab, setActiveTab, tabs, icons }) {
  return (
    <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 100,
      background: D.surface, borderTop: `1px solid ${D.border}`,
      display: "flex", height: 58 }}>
      {tabs.map((tab, i) => (
        <button key={tab} onClick={() => setActiveTab(tab)}
          style={{ flex: 1, background: "none", border: "none", cursor: "pointer",
            display: "flex", flexDirection: "column", alignItems: "center",
            justifyContent: "center", gap: 3, transition: "all 0.2s",
            borderTop: `2px solid ${activeTab === tab ? D.accent : "transparent"}` }}>
          <span style={{ fontSize: 17, opacity: activeTab === tab ? 1 : 0.4 }}>{icons[i]}</span>
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 8,
            color: activeTab === tab ? D.accent : D.muted, letterSpacing: 0.5 }}>
            {tab.toUpperCase()}
          </span>
        </button>
      ))}
    </div>
  );
}

// ─── HOME TAB ─────────────────────────────────────────────────────────────────
function HomeTab({ employee, checkedIn, checkInTime, checkOutTime, workingHours, earlyExitMins, onCheckIn, onCheckOut, attendanceError, loadingAttendance }) {
  const JOB_REQ_MINS   = 585; // 9h 45m
  const isJobHoursMode = (employee?.late_grace_mins ?? 15) === 0;
  const workedMins     = workingHours ? Math.round(workingHours * 60) : 0;
  const extraMins      = isJobHoursMode && workedMins > JOB_REQ_MINS ? workedMins - JOB_REQ_MINS : 0;
  const shortMins      = isJobHoursMode ? (earlyExitMins || 0) : 0;
  // ── Week Off state ─────────────────────────────────────────────────────────
  const [weekOffStatus,    setWeekOffStatus]    = useState({ year_used:0, year_limit:48, month_used:0, month_limit:4, today_is_week_off:false, can_mark_today:false });
  const [weekOffMarking,   setWeekOffMarking]   = useState(false);
  const [weekOffError,     setWeekOffError]     = useState("");

  // ── Checkout availability: min(checkIn + 10hrs, 9 PM) ─────────────────────
  const [checkoutAvailableAt, setCheckoutAvailableAt] = useState(null);
  const [checkoutAllowed,     setCheckoutAllowed]     = useState(false);

  useEffect(() => {
    // Need both checkedIn AND checkInTime to calculate
    if (!checkedIn || !checkInTime) {
      setCheckoutAvailableAt(null);
      setCheckoutAllowed(false);
      return;
    }

    const calcAvailability = () => {
      const now = new Date();

      // Parse check-in time string "09:30 AM" safely
      let checkInDate = null;
      try {
        const parts = checkInTime.match(/(\d+):(\d+)\s*(AM|PM)/i);
        if (parts) {
          let hrs  = parseInt(parts[1]);
          const mins = parseInt(parts[2]);
          const mer  = parts[3].toUpperCase();
          if (mer === "PM" && hrs !== 12) hrs += 12;
          if (mer === "AM" && hrs === 12) hrs  = 0;
          checkInDate = new Date();
          checkInDate.setHours(hrs, mins, 0, 0);
        }
      } catch(e) { return; }

      if (!checkInDate) return;

      // 10 hours after check-in
      const tenHrsLater = new Date(checkInDate.getTime() + 10 * 60 * 60 * 1000);

      // 9 PM today
      const ninePM = new Date();
      ninePM.setHours(21, 0, 0, 0);

      // Whichever comes first
      const availableAt = tenHrsLater < ninePM ? tenHrsLater : ninePM;
      setCheckoutAvailableAt(availableAt);
      setCheckoutAllowed(now >= availableAt);
    };

    calcAvailability();
    const interval = setInterval(calcAvailability, 60000);
    return () => clearInterval(interval);
  }, [checkedIn, checkInTime]); // eslint-disable-line

  const checkoutAvailableStr = checkoutAvailableAt
    ? checkoutAvailableAt.toLocaleTimeString("en-IN",
        { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" })
    : null;
  const [leaveBalance, setLeaveBalance] = useState([]);
  const [recentHistory, setRecentHistory] = useState([]);
  const [monthStats, setMonthStats] = useState({ present: 0, late: 0, absent: 0, leaves: 0, rate: 0 });
  const [loadingData, setLoadingData] = useState(true); // eslint-disable-line

  useEffect(() => {
    const load = async () => {
      try {
        // Load week off status for this month
        const wo = await api.getWeekOffStatus().catch(() => null);
        if (wo) setWeekOffStatus(wo);

        // Load leave balance
        const bal = await api.getMyLeaveBalance();
        const LEAVE_COLORS = ["#f43f5e", "#f59e0b", "#8b5cf6", "#60a5fa"];
        setLeaveBalance((bal || []).map((b, i) => ({
          type:    b.leave_type?.name || `Leave ${b.leave_type_id}`,
          total:   b.total_days,
          used:    parseFloat(b.used_days)    || 0,
          pending: parseFloat(b.pending_days) || 0,
          color:   LEAVE_COLORS[i % LEAVE_COLORS.length],
        })));

        // Load recent history (last 7 days)
        const today    = new Date();
        const weekAgo  = new Date(today); weekAgo.setDate(weekAgo.getDate() - 7);
        const fmt = d => d.toISOString().split("T")[0];
        const hist = await api.getAttendanceHistory(fmt(weekAgo), fmt(today));
        const STATUS_MAP = { present:"present", late:"late", absent:"absent", leave:"leave", half_day:"present" };
        setRecentHistory((hist || []).slice(0, 7).map(r => ({
          date:     new Date(r.date).toLocaleDateString("en-IN", { weekday:"short", month:"short", day:"numeric" }),
          checkIn:  r.check_in_time  ? new Date(r.check_in_time).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit",hour12:true,timeZone:"Asia/Kolkata"}) : null,
          checkOut: r.check_out_time ? new Date(r.check_out_time).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit",hour12:true,timeZone:"Asia/Kolkata"}) : null,
          hours:    fmtHours(r.working_hours),
          status:   STATUS_MAP[r.status] || r.status || "absent",
          late:     r.late_by_mins || 0,
        })));

        // Month stats
        const present = (hist||[]).filter(r => r.status === "present" || r.status === "late").length;
        const late    = (hist||[]).filter(r => r.status === "late").length;
        const absent  = (hist||[]).filter(r => r.status === "absent").length;
        const leaves  = (hist||[]).filter(r => r.status === "leave").length;
        const total   = (hist||[]).length;
        setMonthStats({ present, late, absent, leaves, rate: total ? Math.round((present / total) * 100) : 0 });
      } catch (e) {
        console.error("HomeTab load error:", e);
      } finally {
        setLoadingData(false);
      }
    };
    load();
  }, []);
  const now = useClock();
  const handleMarkWeekOff = async () => {
    if (!weekOffStatus.can_mark_today || checkedIn || weekOffMarking) return;
    setWeekOffMarking(true); setWeekOffError("");
    try {
      const res = await api.markWeekOff();
      setWeekOffStatus(prev => ({
        ...prev,
        year_used:         res.year_used,
        month_used:        res.month_used,
        today_is_week_off: true,
        can_mark_today:    false,
      }));
    } catch(e) {
      setWeekOffError(e.detail || "Failed to mark week off");
      setTimeout(() => setWeekOffError(""), 4000);
    } finally {
      setWeekOffMarking(false);
    }
  };

  const { isMobile, isTablet } = useBreakpoint();
  const timeStr = now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true, timeZone: "Asia/Kolkata" });
  const dateStr = now.toLocaleDateString("en-IN", { weekday: "long", month: "long", day: "numeric" });
  const greeting = now.getHours() < 12 ? "Morning" : now.getHours() < 17 ? "Afternoon" : "Evening";
  // monthStats now loaded from API above
  const pad = isMobile ? "14px" : "26px 30px";
  const twoCol = !isMobile && !isTablet;

  return (
    <div style={{ padding: pad, display: "flex", flexDirection: "column", gap: 14 }}>
      {!isMobile && (
        <div>
          <h2 style={{ fontFamily: "'DM Mono',monospace", color: D.text, fontSize: 18, margin: "0 0 4px" }}>
            Good {greeting}, {employee.name.split(" ")[0]} 👋
          </h2>
          <p style={{ color: D.muted, fontSize: 13, margin: 0 }}>{dateStr}</p>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: twoCol ? "1fr 1fr" : "1fr", gap: 14 }}>
        {/* Left */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Card>
            <Label>Current Time</Label>
            <p style={{ color: D.accent, fontFamily: "'DM Mono',monospace",
              fontSize: isMobile ? 34 : 40, margin: 0, letterSpacing: -2,
              textShadow: "0 0 24px rgba(14,165,233,0.3)" }}>{timeStr}</p>
            <p style={{ color: D.dim, fontSize: 11, margin: "5px 0 0" }}>
              {isMobile ? dateStr : `Shift: ${employee.shift}`}
            </p>
          </Card>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {/* Check In */}
            {/* Attendance error banner */}
          {attendanceError && (
            <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)",
              borderRadius: 10, padding: "10px 14px", display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: "#ef4444", fontSize: 16 }}>⚠</span>
              <span style={{ color: "#ef4444", fontFamily: "'Sora',sans-serif", fontSize: 13 }}>
                {attendanceError}
              </span>
            </div>
          )}

          {/* Check In button
                States:
                  - No activity yet      → green, clickable
                  - Currently checked in → green dim, disabled (already in)
                  - Checked out for day  → locked, disabled (done for today)
            */}
            <button
              onClick={(!checkedIn && !checkOutTime && !weekOffStatus.today_is_week_off && !loadingAttendance) ? onCheckIn : undefined}
              disabled={checkedIn || !!checkOutTime || weekOffStatus.today_is_week_off || loadingAttendance}
              style={{ padding: isMobile ? "14px 8px" : "18px 12px", borderRadius: 14, border: "none",
                background: loadingAttendance
                  ? "rgba(255,255,255,0.03)"
                  : checkedIn
                    ? "rgba(34,197,94,0.06)"
                    : checkOutTime || weekOffStatus.today_is_week_off
                      ? "rgba(255,255,255,0.03)"
                      : `linear-gradient(135deg,${D.green},#16a34a)`,
                cursor: (!checkedIn && !checkOutTime && !weekOffStatus.today_is_week_off && !loadingAttendance) ? "pointer" : "not-allowed",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                boxShadow: (!checkedIn && !checkOutTime) ? "0 6px 20px rgba(34,197,94,0.2)" : "none",
                opacity: (checkedIn || checkOutTime) ? 0.45 : 1,
                transition: "all 0.3s" }}>
              <span style={{ fontSize: isMobile ? 22 : 26 }}>
                {loadingAttendance ? "⟳" : checkedIn ? "✓" : checkOutTime ? "✗" : weekOffStatus.today_is_week_off ? "◷" : "→"}
              </span>
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: isMobile ? 9 : 10,
                fontWeight: 700,
                color: loadingAttendance ? D.muted : checkedIn ? D.green : checkOutTime ? D.dim : "#042a1c",
                letterSpacing: 0.5 }}>
                {loadingAttendance ? "LOADING…" : checkedIn ? "CHECKED IN" : checkOutTime ? "COMPLETED" : weekOffStatus.today_is_week_off ? "WEEK OFF" : "CHECK IN"}
              </span>
              {checkInTime && (
                <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: D.green }}>
                  {checkInTime}
                </span>
              )}
              {checkOutTime && !checkedIn && (
                <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: D.dim }}>
                  Done for today
                </span>
              )}
            </button>

            {/* Check Out */}
            {/* Check Out button */}
            <button
              onClick={(checkedIn && checkoutAllowed && !loadingAttendance) ? onCheckOut : undefined}
              disabled={!checkedIn || !checkoutAllowed || loadingAttendance}
              style={{ padding: isMobile ? "14px 8px" : "18px 12px", borderRadius: 14, border: "none",
                background: checkOutTime && !checkedIn
                  ? "rgba(239,68,68,0.06)"
                  : (checkedIn && checkoutAllowed)
                    ? "linear-gradient(135deg,#ef4444,#b91c1c)"
                    : "rgba(255,255,255,0.03)",
                cursor: (checkedIn && checkoutAllowed) ? "pointer" : "not-allowed",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                boxShadow: (checkedIn && checkoutAllowed) ? "0 6px 20px rgba(239,68,68,0.2)" : "none",
                opacity: (!checkedIn || (!checkoutAllowed && !checkOutTime)) ? 0.4 : 1,
                transition: "all 0.3s" }}>
              <span style={{ fontSize: isMobile ? 22 : 26 }}>
                {checkOutTime && !checkedIn ? "✓" : "←"}
              </span>
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: isMobile ? 9 : 10,
                fontWeight: 700,
                color: (checkedIn && checkoutAllowed) ? "#fff" : checkOutTime ? D.red : D.dim,
                letterSpacing: 0.5 }}>
                {checkOutTime && !checkedIn ? "CHECKED OUT"
                  : (checkedIn && !checkoutAllowed && checkoutAvailableStr)
                    ? "CHECK OUT"
                    : "CHECK OUT"}
              </span>
              {/* Show availability time when checked in but not yet allowed */}
              {checkedIn && !checkoutAllowed && !checkOutTime && checkoutAvailableStr && (
                <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 8,
                  color: D.muted, letterSpacing: 0.3, textAlign: "center", lineHeight: 1.3 }}>
                  Available after {checkoutAvailableStr}
                </span>
              )}
              {checkOutTime && !checkedIn && (
                <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: "#f87171" }}>
                  {checkOutTime}
                </span>
              )}
            </button>
          </div>

          {/* Job-hours summary — shown after checkout for job-hours mode employees */}
          {checkOutTime && !checkedIn && isJobHoursMode && workingHours !== null && (
            <div style={{ background: shortMins > 0 ? "rgba(245,158,11,0.07)" : "rgba(34,197,94,0.07)",
              border: `1px solid ${shortMins > 0 ? "rgba(245,158,11,0.3)" : "rgba(34,197,94,0.25)"}`,
              borderRadius: 14, padding: "14px 18px",
              display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                <span style={{ fontFamily:"'DM Mono',monospace", fontSize:10,
                  color: D.muted, letterSpacing:1, textTransform:"uppercase" }}>Today's Hours</span>
                <span style={{ fontFamily:"'DM Mono',monospace", fontSize:20,
                  color: shortMins > 0 ? "#f59e0b" : D.green, fontWeight:600 }}>
                  {fmtHours(workingHours)}
                </span>
              </div>
              <div style={{ textAlign:"right" }}>
                {shortMins > 0 && (
                  <div style={{ fontFamily:"'DM Mono',monospace", fontSize:12,
                    color:"#f59e0b", fontWeight:600 }}>
                    ⚠ Short {fmtMins(shortMins)}
                  </div>
                )}
                {extraMins > 0 && (
                  <div style={{ fontFamily:"'DM Mono',monospace", fontSize:12,
                    color: D.green, fontWeight:600 }}>
                    ✓ Extra +{fmtMins(extraMins)}
                  </div>
                )}
                {shortMins === 0 && extraMins === 0 && (
                  <div style={{ fontFamily:"'DM Mono',monospace", fontSize:12, color: D.green }}>
                    ✓ On Time
                  </div>
                )}
                <div style={{ fontFamily:"'Sora',sans-serif", fontSize:11, color:D.muted, marginTop:2 }}>
                  Required: 9h 45m
                </div>
              </div>
            </div>
          )}

          {/* Week Off Button */}
          {!checkOutTime && !checkedIn && (
            <div>
              {weekOffError && (
                <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.25)",
                  borderRadius:10, padding:"8px 12px", marginBottom:8,
                  display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ color:D.red }}>⚠</span>
                  <span style={{ color:D.red, fontSize:12 }}>{weekOffError}</span>
                </div>
              )}
              <button
                onClick={handleMarkWeekOff}
                disabled={!weekOffStatus.can_mark_today || weekOffMarking}
                style={{
                  width: "100%", padding: "12px 16px",
                  borderRadius: 12,
                  background: weekOffStatus.today_is_week_off
                    ? "rgba(139,92,246,0.12)"
                    : !weekOffStatus.can_mark_today
                      ? "rgba(255,255,255,0.03)"
                      : "rgba(139,92,246,0.1)",
                  border: `1px solid ${weekOffStatus.today_is_week_off
                    ? "rgba(139,92,246,0.5)"
                    : !weekOffStatus.can_mark_today
                      ? D.border
                      : "rgba(139,92,246,0.3)"}`,
                  color: weekOffStatus.today_is_week_off
                    ? "#a78bfa"
                    : !weekOffStatus.can_mark_today
                      ? D.dim
                      : "#c4b5fd",
                  fontFamily: "'DM Mono',monospace",
                  fontSize: isMobile ? 11 : 12,
                  fontWeight: 600,
                  cursor: (!weekOffStatus.can_mark_today || weekOffMarking) ? "not-allowed" : "pointer",
                  opacity: !weekOffStatus.can_mark_today && !weekOffStatus.today_is_week_off ? 0.5 : 1,
                  transition: "all 0.2s",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                }}>
                <span style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:16 }}>
                    {weekOffStatus.today_is_week_off ? "✓" : weekOffMarking ? "⟳" : "◷"}
                  </span>
                  {weekOffStatus.today_is_week_off
                    ? "Week Off Marked for Today"
                    : weekOffMarking
                      ? "Marking Week Off…"
                      : !weekOffStatus.can_mark_today
                        ? "No Week Offs Remaining"
                        : "Mark Today as Week Off"}
                </span>
                <span style={{ fontFamily:"'DM Mono',monospace", fontSize:10,
                  background: weekOffStatus.today_is_week_off
                    ? "rgba(139,92,246,0.2)"
                    : "rgba(255,255,255,0.06)",
                  padding:"2px 8px", borderRadius:6 }}>
                  {weekOffStatus.month_limit !== null && weekOffStatus.month_limit !== undefined
                    ? `${weekOffStatus.month_used}/${weekOffStatus.month_limit} mo`
                    : (() => {
                        const limit         = weekOffStatus.year_limit ?? 48;
                        // fy_months_elapsed is FY-aware (e.g. Apr FY: May=2, not 5)
                        const monthsElapsed = weekOffStatus.fy_months_elapsed ?? (new Date().getMonth() + 1);
                        const prorated      = Math.round((monthsElapsed / 12) * limit);
                        return `${weekOffStatus.year_used}/${prorated} (of ${limit}/yr)`;
                      })()}
                </span>
              </button>
            </div>
          )}

          {/* Show week off status if today is week off */}
          {weekOffStatus.today_is_week_off && (
            <div style={{ background:"rgba(139,92,246,0.08)",
              border:"1px solid rgba(139,92,246,0.25)",
              borderRadius:12, padding:"14px 16px",
              display:"flex", alignItems:"center", gap:12 }}>
              <span style={{ fontSize:24 }}>🌿</span>
              <div>
                <p style={{ fontFamily:"'DM Mono',monospace", color:"#a78bfa",
                  fontSize:13, fontWeight:700, margin:"0 0 2px" }}>Today is your Week Off</p>
                <p style={{ fontFamily:"'Sora',sans-serif", color:D.muted,
                  fontSize:11, margin:0 }}>
                  Enjoy your day! {weekOffStatus.month_remaining} week off{weekOffStatus.month_remaining !== 1 ? "s" : ""} remaining this month.
                </p>
              </div>
            </div>
          )}

          {/* Today mini stats */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            {[
              { label: "In",  val: checkInTime  || "—", active: !!checkInTime },
              { label: "Out", val: checkOutTime || "—", active: !!checkOutTime },
              { label: "Hrs", val: checkedIn ? "Live" : checkOutTime ? "8h+" : "—", active: checkedIn || !!checkOutTime },
            ].map(({ label, val, active }) => (
              <div key={label} style={{
                background: active ? "rgba(14,165,233,0.06)" : "rgba(255,255,255,0.02)",
                border: `1px solid ${active ? "rgba(14,165,233,0.2)" : "rgba(255,255,255,0.05)"}`,
                borderRadius: 10, padding: "9px 6px", textAlign: "center" }}>
                <p style={{ fontFamily: "'DM Mono',monospace", color: active ? D.accent : D.dim,
                  fontSize: 11, fontWeight: 700, margin: "0 0 2px" }}>{val}</p>
                <p style={{ color: D.dim, fontSize: 8, margin: 0, textTransform: "uppercase",
                  letterSpacing: 1, fontFamily: "'DM Mono',monospace" }}>{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Right */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Card>
            <Label>This Month</Label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {[
                { label: "Present", val: monthStats.present, color: D.green  },
                { label: "Late",    val: monthStats.late,    color: D.amber  },
                { label: "Absent",  val: monthStats.absent,  color: D.red    },
                { label: "Leave",   val: monthStats.leaves,  color: D.purple },
              ].map(({ label, val, color }) => (
                <div key={label} style={{ background: `${color}08`, border: `1px solid ${color}20`, borderRadius: 10, padding: "10px 12px" }}>
                  <p style={{ fontFamily: "'DM Mono',monospace", color, fontSize: 22, fontWeight: 700, margin: "0 0 2px" }}>{val}</p>
                  <p style={{ color: D.muted, fontSize: 10, margin: 0 }}>{label}</p>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                <span style={{ color: D.muted, fontSize: 11 }}>Attendance Rate</span>
                <span style={{ fontFamily: "'DM Mono',monospace", color: D.accent, fontSize: 12, fontWeight: 700 }}>{monthStats.rate}%</span>
              </div>
              <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: 4, height: 5, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${monthStats.rate}%`,
                  background: `linear-gradient(90deg,${D.green},${D.accent})`, borderRadius: 4 }} />
              </div>
            </div>
          </Card>

          <Card>
            <Label>Leave Balance</Label>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {leaveBalance.map(({ type, total, used, pending, color }) => {
                const avail = total - used - pending;
                return (
                  <div key={type}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ color: D.text, fontSize: 12 }}>{type}</span>
                      <span style={{ fontFamily: "'DM Mono',monospace", color, fontSize: 12, fontWeight: 700 }}>{avail}/{total}</span>
                    </div>
                    <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: 3, height: 4, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${(avail/total)*100}%`, background: color, borderRadius: 3 }} />
                    </div>
                    {pending > 0 && <p style={{ color: D.blue, fontSize: 10, margin: "2px 0 0" }}>{pending}d pending</p>}
                  </div>
                );
              })}
            </div>
          </Card>

          <Card>
            <Label>Recent Days</Label>
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              {recentHistory.slice(0, 3).map((r, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "7px 10px", background: "rgba(255,255,255,0.02)",
                  borderRadius: 8, border: "1px solid rgba(255,255,255,0.04)" }}>
                  <div>
                    <p style={{ fontFamily: "'DM Mono',monospace", color: D.text, fontSize: 11, margin: 0 }}>{r.date}</p>
                    <p style={{ color: D.muted, fontSize: 10, margin: "1px 0 0" }}>
                      {r.checkIn ? `${r.checkIn} → ${r.checkOut}` : "No record"}
                    </p>
                  </div>
                  <StatusPill status={r.status} />
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ─── ATTENDANCE TAB ───────────────────────────────────────────────────────────
function AttendanceTab() {
  const { isMobile } = useBreakpoint();
  const today = new Date().toISOString().split("T")[0];
  const monthStart = today.slice(0,8) + "01";
  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate]     = useState(today);
  const [records, setRecords]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [searched, setSearched] = useState(false);
  const pad = isMobile ? "14px" : "26px 30px";

  const doSearch = useCallback(async () => {
    setLoading(true);
    try {
      const hist = await api.getAttendanceHistory(fromDate, toDate);
      const STATUS_MAP = { present:"present", late:"late", absent:"absent", leave:"leave", half_day:"present" };
      setRecords((hist||[]).map(r => ({
        date:     new Date(r.date).toLocaleDateString("en-IN",{weekday:"short",month:"short",day:"numeric"}),
        checkIn:  r.check_in_time  ? new Date(r.check_in_time).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit",hour12:true,timeZone:"Asia/Kolkata"}) : null,
        checkOut: r.check_out_time ? new Date(r.check_out_time).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit",hour12:true,timeZone:"Asia/Kolkata"}) : null,
        hours:    fmtHours(r.working_hours),
        status:   STATUS_MAP[r.status] || r.status || "absent",
        late:     r.late_by_mins || 0,
      })));
      setSearched(true);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [fromDate, toDate]);

  useEffect(() => { doSearch(); }, []); // eslint-disable-line

  const summary = {
    present: records.filter(r => r.status === "present" || r.status === "late").length,
    late:    records.filter(r => r.status === "late").length,
    absent:  records.filter(r => r.status === "absent").length,
  };

  return (
    <div style={{ padding: pad, display: "flex", flexDirection: "column", gap: 14 }}>
      {!isMobile && (
        <div>
          <h2 style={{ fontFamily: "'DM Mono',monospace", color: D.text, fontSize: 18, margin: "0 0 4px" }}>Attendance History</h2>
          <p style={{ color: D.muted, fontSize: 13, margin: 0 }}>View records by date range</p>
        </div>
      )}

      <Card style={{ padding: "14px 16px" }}>
        <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 10, alignItems: isMobile ? "stretch" : "flex-end" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, flex: 1 }}>
            <div>
              <Label>From</Label>
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                style={{ width: "100%", boxSizing: "border-box", background: D.surface,
                  border: `1px solid ${D.border}`, borderRadius: 8, padding: "8px 10px",
                  color: D.text, fontFamily: "'DM Mono',monospace", fontSize: 12, outline: "none",
                  colorScheme: "dark" }} />
            </div>
            <div>
              <Label>To</Label>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                style={{ width: "100%", boxSizing: "border-box", background: D.surface,
                  border: `1px solid ${D.border}`, borderRadius: 8, padding: "8px 10px",
                  color: D.text, fontFamily: "'DM Mono',monospace", fontSize: 12, outline: "none",
                  colorScheme: "dark" }} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[
              { label: "Today", from: today, to: today },
              { label: "Week", from: (() => { const d = new Date(); d.setDate(d.getDate()-7); return d.toISOString().split("T")[0]; })(), to: today },
              { label: "Month", from: monthStart, to: today },
            ].map(({ label, from, to }) => (
              <button key={label} onClick={() => { setFromDate(from); setToDate(to); setTimeout(doSearch, 0); }}
                style={{ background: "transparent", border: `1px solid ${D.border}`,
                  borderRadius: 6, padding: "6px 12px", color: D.muted,
                  fontFamily: "'DM Mono',monospace", fontSize: 10, cursor: "pointer" }}>{label}</button>
            ))}
          </div>
          <Btn onClick={doSearch} style={{ padding: "9px 16px", fontSize: 12 }}>{ loading ? "Searching…" : "Search" }</Btn>
        </div>
      </Card>

      {searched && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
            {[
              { label: "Present",    val: summary.present,     color: D.green  },
              { label: "Late",       val: summary.late,        color: D.amber  },
              { label: "Absent",     val: summary.absent,      color: D.red    },
              { label: "Total Days", val: records.length,       color: D.accent },
            ].map(({ label, val, color }) => (
              <Card key={label} style={{ padding: "11px 10px", textAlign: "center" }}>
                <p style={{ fontFamily: "'DM Mono',monospace", color, fontSize: 20, fontWeight: 700, margin: "0 0 3px" }}>{val}</p>
                <p style={{ color: D.muted, fontSize: 9, margin: 0, textTransform: "uppercase", letterSpacing: 1, fontFamily: "'DM Mono',monospace" }}>{label}</p>
              </Card>
            ))}
          </div>

          {loading && (
            <div style={{ textAlign:"center", padding:"32px", color:D.muted,
              fontFamily:"'DM Mono',monospace", fontSize:12 }}>Loading…</div>
          )}
          {!loading && records.length === 0 && searched && (
            <div style={{ textAlign:"center", padding:"32px", color:D.muted,
              fontFamily:"'DM Mono',monospace", fontSize:12 }}>No records found for this date range</div>
          )}
          {/* Mobile: cards / Desktop: table */}
          {!loading && records.length > 0 && isMobile ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {records.map((r, i) => (
                <Card key={i} style={{ padding: "12px 14px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <p style={{ fontFamily: "'DM Mono',monospace", color: D.text, fontSize: 12, margin: 0 }}>{r.date}</p>
                    <StatusPill status={r.status} />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                    {[
                      { label: "In",    val: r.checkIn  || "—" },
                      { label: "Out",   val: r.checkOut || "—" },
                      { label: "Hours", val: r.hours },
                    ].map(({ label, val }) => (
                      <div key={label}>
                        <p style={{ color: D.muted, fontSize: 9, margin: "0 0 2px", textTransform: "uppercase", letterSpacing: 1, fontFamily: "'DM Mono',monospace" }}>{label}</p>
                        <p style={{ color: D.text, fontSize: 11, margin: 0, fontFamily: "'DM Mono',monospace" }}>{val}</p>
                      </div>
                    ))}
                  </div>
                  {r.late > 0 && <p style={{ color: D.amber, fontSize: 10, margin: "6px 0 0", fontFamily: "'DM Mono',monospace" }}>⚠ Late by {r.late}m</p>}
                </Card>
              ))}
            </div>
          ) : (
            <Card style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1fr 0.8fr 1fr",
                gap: 8, padding: "10px 18px", background: "#0a0d12", borderBottom: `1px solid ${D.border}` }}>
                {["Date","Check In","Check Out","Hours","Late By","Status"].map(h => (
                  <span key={h} style={{ fontFamily: "'DM Mono',monospace", color: D.muted, fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }}>{h}</span>
                ))}
              </div>
              {records.map((r, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1fr 0.8fr 1fr",
                  gap: 8, padding: "12px 18px", alignItems: "center",
                  background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)",
                  borderBottom: i < records.length - 1 ? `1px solid ${D.border}` : "none" }}>
                  <span style={{ fontFamily: "'DM Mono',monospace", color: D.text, fontSize: 12 }}>{r.date}</span>
                  <span style={{ fontFamily: "'DM Mono',monospace", color: r.checkIn ? D.text : D.dim, fontSize: 12 }}>{r.checkIn || "—"}</span>
                  <span style={{ fontFamily: "'DM Mono',monospace", color: r.checkOut ? D.text : D.dim, fontSize: 12 }}>{r.checkOut || "—"}</span>
                  <span style={{ fontFamily: "'DM Mono',monospace", color: r.hours !== "0h" ? D.accent : D.dim, fontSize: 12 }}>{r.hours}</span>
                  <span style={{ fontFamily: "'DM Mono',monospace", color: r.late ? D.amber : D.dim, fontSize: 12 }}>{r.late ? `+${r.late}m` : "—"}</span>
                  <StatusPill status={r.status} />
                </div>
              ))}
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ─── LEAVES TAB ───────────────────────────────────────────────────────────────
function LeavesTab() {
  const { isMobile } = useBreakpoint();
  const [leaveBalance, setLeaveBalance]   = useState([]);
  const [leaveTypes, setLeaveTypes]       = useState([]);
  const [applications, setApplications]   = useState([]);
  const [loadingData, setLoadingData]     = useState(true); // eslint-disable-line
  const [showForm, setShowForm] = useState(false);

  const LEAVE_COLORS = ["#f43f5e", "#f59e0b", "#8b5cf6", "#60a5fa"];

  const loadData = useCallback(async () => {
    setLoadingData(true);
    try {
      const [bal, apps, types] = await Promise.all([
        api.getMyLeaveBalance(),
        api.getMyLeaveApplications(),
        api.getLeaveTypesForEmployee().catch(() => []),  // employee endpoint
      ]);

      // Build leave types from dedicated endpoint (has correct names)
      const typeMap = {};
      (types||[]).forEach(t => { typeMap[t.id] = t.name; });

      const balMapped = (bal||[]).map((b, i) => ({
        id:      b.leave_type_id,
        type:    typeMap[b.leave_type_id]
                 || b.leave_type?.name
                 || `Leave ${b.leave_type_id}`,
        total:   b.total_days,
        used:    parseFloat(b.used_days)    || 0,
        pending: parseFloat(b.pending_days) || 0,
        color:   LEAVE_COLORS[i % LEAVE_COLORS.length],
      }));
      setLeaveBalance(balMapped);

      // Use dedicated leave types for the apply form dropdown (include is_flexible flag)
      setLeaveTypes(
        (types||[]).length > 0
          ? (types||[]).map(t => ({ id: t.id, type: t.name, is_flexible: !!t.is_flexible }))
          : balMapped.map(b => ({ id: b.id, type: b.type, is_flexible: false }))
      );
      setApplications((apps||[]).map(a => ({
        id:     a.id,
        type:   a.leave_type?.name || `Leave ${a.leave_type_id}`,
        from:   new Date(a.from_date).toLocaleDateString("en-IN",{day:"numeric",month:"short"}),
        to:     new Date(a.to_date).toLocaleDateString("en-IN",{day:"numeric",month:"short"}),
        days:   parseFloat(a.total_days),
        status: a.status,
        reason: a.reason || "",
      })));
    } catch(e) { console.error(e); }
    finally { setLoadingData(false); }
  }, []); // eslint-disable-line

  useEffect(() => { loadData(); }, []); // eslint-disable-line
  const [leaveTypeId, setLeaveTypeId] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate]     = useState("");
  const [reason, setReason]     = useState("");
  const [loading, setLoading]     = useState(false); // eslint-disable-line
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState("");

  const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split("T")[0];

  const handleFromDateChange = val => { setFromDate(val); setFormError(""); if (toDate && val > toDate) setToDate(""); };
  const handleToDateChange   = val => {
    setFormError(""); if (val < fromDate) { setFormError("End date cannot be before start date"); return; } setToDate(val);
  };
  const handleApply = async () => {
    setFormError("");
    if (!leaveTypeId)              { setFormError("Please select a leave type"); return; }
    if (!fromDate || !toDate)      { setFormError("Please select dates"); return; }
    if (!reason.trim())            { setFormError("Please provide a reason"); return; }
    if (fromDate < minDate)        { setFormError("Leave must start from tomorrow — backdating not allowed"); return; }
    if (reason.trim().length < 5)  { setFormError("Please provide a proper reason (min 5 characters)"); return; }
    setLoading(true);
    try {
      await api.applyLeave({
        leave_type_id: leaveTypeId,
        from_date:     fromDate,
        to_date:       toDate,
        reason:        reason.trim(),
      });
      setSubmitted(true);
      await loadData(); // refresh balances
      setTimeout(() => {
        setSubmitted(false); setShowForm(false);
        setFromDate(""); setToDate(""); setReason(""); setFormError("");
      }, 2500);
    } catch(err) {
      setFormError(err.detail || "Failed to submit application");
    } finally {
      setLoading(false);
    }
  };

  const pad = isMobile ? "14px" : "26px 30px";
  const inputSt = { width: "100%", boxSizing: "border-box", outline: "none",
    background: "rgba(255,255,255,0.02)", border: `1px solid ${D.border}`,
    borderRadius: 10, padding: "10px 12px", color: D.text,
    fontFamily: "'Sora',sans-serif", fontSize: 13, colorScheme: "dark" };

  return (
    <div style={{ padding: pad, display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        {!isMobile && (
          <div>
            <h2 style={{ fontFamily: "'DM Mono',monospace", color: D.text, fontSize: 18, margin: "0 0 4px" }}>Leave Management</h2>
            <p style={{ color: D.muted, fontSize: 13, margin: 0 }}>Apply and track your leaves</p>
          </div>
        )}
        <Btn onClick={() => setShowForm(!showForm)} variant={showForm ? "danger" : "primary"}
          style={{ marginLeft: isMobile ? "auto" : 0, padding: "10px 18px" }}>
          {showForm ? "✕ Cancel" : "+ Apply Leave"}
        </Btn>
      </div>

      {/* Balance */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3,1fr)", gap: 10 }}>
        {leaveBalance.map(({ type, total, used, pending, color }) => {
          const avail = total - used - pending;
          return (
            <Card key={type} style={{ padding: "13px 14px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 7 }}>
                <p style={{ color: D.text, fontSize: isMobile ? 11 : 12, margin: 0 }}>{type}</p>
                <span style={{ fontFamily: "'DM Mono',monospace", color, fontSize: 17, fontWeight: 700 }}>{avail}</span>
              </div>
              <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: 3, height: 4, overflow: "hidden", marginBottom: 5 }}>
                <div style={{ height: "100%", width: `${(avail/total)*100}%`, background: color, borderRadius: 3 }} />
              </div>
              <p style={{ color: D.muted, fontSize: 10, margin: 0 }}>{avail} avail · {used} used</p>
              {pending > 0 && <p style={{ color: D.blue, fontSize: 10, margin: "2px 0 0" }}>{pending}d pending</p>}
            </Card>
          );
        })}
      </div>

      {/* Form */}
      {showForm && (
        <Card style={{ padding: isMobile ? "14px" : "20px", border: `1px solid ${D.borderH}` }}>
          <Label>Apply for Leave</Label>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 8 }}>
            {submitted && (
              <div style={{ background: "#042a1c", border: "1px solid rgba(34,197,94,0.3)",
                borderRadius: 10, padding: "10px 14px", display: "flex", alignItems: "center", gap: 8 }}>
                <span>✅</span>
                <span style={{ color: D.green, fontSize: 13 }}>Application submitted!</span>
              </div>
            )}
            <ErrorBox msg={formError} />
            {/* Flexible Leave info banner */}
            {leaveTypeId && leaveTypes.find(t => t.id === leaveTypeId)?.is_flexible && (
              <div style={{ background: "rgba(139,92,246,0.1)", border: "1px solid rgba(139,92,246,0.3)",
                borderRadius: 10, padding: "10px 14px", display: "flex", gap: 8, alignItems: "flex-start" }}>
                <span style={{ fontSize: 16 }}>🔀</span>
                <p style={{ color: "#a78bfa", fontSize: 12, margin: 0, lineHeight: 1.5 }}>
                  <strong>Flexible Leave:</strong> Manager will review and distribute your days as SL / CL / Week-Off / Absent.
                  No balance is deducted until your manager distributes the leave.
                </p>
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
              <div>
                <Label>Leave Type</Label>
                <select value={leaveTypeId || ""} onChange={e => setLeaveTypeId(parseInt(e.target.value))}
                  style={{ ...inputSt, cursor: "pointer" }}>
                  <option value="" disabled style={{ background: "#111620" }}>
                    {leaveTypes.length === 0 ? "Loading leave types…" : "Select leave type"}
                  </option>
                  {leaveTypes.map(l => <option key={l.id} value={l.id} style={{ background: "#111620" }}>{l.type}{l.is_flexible ? " (Flexible)" : ""}</option>)}
                </select>
              </div>
              <div>
                <Label>From Date <span style={{ color: D.dim, fontSize: 9, textTransform: "none", letterSpacing: 0 }}>(tomorrow+)</span></Label>
                <input type="date" value={fromDate} min={minDate} onChange={e => handleFromDateChange(e.target.value)} style={inputSt} />
              </div>
              <div>
                <Label>To Date</Label>
                <input type="date" value={toDate} min={fromDate || minDate} onChange={e => handleToDateChange(e.target.value)} style={inputSt} />
              </div>
              <div>
                <Label>Reason</Label>
                <input type="text" value={reason}
                  onChange={e => { setReason(e.target.value); setFormError(""); }}
                  placeholder="Brief reason for leave" style={inputSt} />
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <Btn onClick={handleApply} style={{ padding: "10px 22px" }}>Submit Application</Btn>
              <span style={{ color: D.dim, fontSize: 11, fontFamily: "'DM Mono',monospace" }}>ⓘ No backdated applications</span>
            </div>
          </div>
        </Card>
      )}

      {/* History */}
      <div>
        <Label>My Applications</Label>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
          {applications.map(leave => (
            <Card key={leave.id} style={{ padding: "12px 14px" }}>
              <div style={{ display: "flex", alignItems: isMobile ? "flex-start" : "center",
                flexDirection: isMobile ? "column" : "row", gap: isMobile ? 7 : 14 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 3 }}>
                    <p style={{ fontFamily: "'DM Mono',monospace", color: D.text, fontSize: 13, margin: 0 }}>{leave.type}</p>
                    <span style={{ color: D.muted, fontSize: 11 }}>{leave.from} – {leave.to} ({leave.days}d)</span>
                  </div>
                  <p style={{ color: D.muted, fontSize: 12, margin: 0 }}>"{leave.reason}"</p>
                </div>
                <StatusPill status={leave.status} />
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── PROFILE TAB ──────────────────────────────────────────────────────────────
function ProfileTab({ employee }) {
  const { isMobile } = useBreakpoint();
  const pad = isMobile ? "14px" : "26px 30px";
  const fields = [
    { icon: "👤", label: "Full Name",  val: employee.name },
    { icon: "✉",  label: "Email",      val: employee.email },
    { icon: "💼", label: "Role",       val: employee.role },
    { icon: "🏢", label: "Department", val: employee.department },
    { icon: "📍", label: "Office",     val: employee.office },
    { icon: "⏰", label: "Shift",      val: employee.shift },
    { icon: "📅", label: "Joined",     val: employee.joining_date },
    { icon: "🏷",  label: "Company",   val: employee?.tenant?.name || "AttendTrack" },
  ];

  return (
    <div style={{ padding: pad, display: "flex", flexDirection: "column", gap: 14 }}>
      {!isMobile && <h2 style={{ fontFamily: "'DM Mono',monospace", color: D.text, fontSize: 18, margin: 0 }}>My Profile</h2>}

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "240px 1fr", gap: 14 }}>
        <Card style={{ textAlign: "center", padding: 22 }}>
          <div style={{ width: 68, height: 68, borderRadius: "50%",
            background: `linear-gradient(135deg,${D.accent},${D.accentD})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 12px", fontSize: 24, fontWeight: 700,
            fontFamily: "'DM Mono',monospace", color: "#fff",
            boxShadow: "0 0 22px rgba(14,165,233,0.3)" }}>
            {employee.avatar}
          </div>
          <h3 style={{ fontFamily: "'DM Mono',monospace", color: D.text, fontSize: 15, margin: "0 0 4px" }}>{employee.name}</h3>
          <p style={{ color: D.muted, fontSize: 12, margin: "0 0 12px" }}>{employee.role}</p>
          <div style={{ background: "rgba(14,165,233,0.08)", border: `1px solid ${D.border}`,
            borderRadius: 8, padding: "6px 10px", marginBottom: 14 }}>
            <p style={{ fontFamily: "'DM Mono',monospace", color: D.accent, fontSize: 11, margin: 0 }}>{employee.department}</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {[
              { label: "Present", val: 18,    color: D.green  },
              { label: "Late",    val: 2,     color: D.amber  },
              { label: "Leaves",  val: 3,     color: D.purple },
              { label: "Rate",    val: "90%", color: D.accent },
            ].map(({ label, val, color }) => (
              <div key={label} style={{ background: "rgba(255,255,255,0.02)",
                border: `1px solid ${D.border}`, borderRadius: 8, padding: "8px 6px" }}>
                <p style={{ fontFamily: "'DM Mono',monospace", color, fontSize: 15, fontWeight: 700, margin: "0 0 2px" }}>{val}</p>
                <p style={{ color: D.muted, fontSize: 9, margin: 0 }}>{label}</p>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <Label>Personal Details</Label>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10, marginTop: 10 }}>
            {fields.map(({ icon, label, val }) => (
              <div key={label} style={{ background: "rgba(255,255,255,0.02)",
                border: `1px solid ${D.border}`, borderRadius: 10, padding: "10px 12px" }}>
                <p style={{ color: D.muted, fontSize: 9, margin: "0 0 3px",
                  textTransform: "uppercase", letterSpacing: 1, fontFamily: "'DM Mono',monospace" }}>
                  {icon} {label}
                </p>
                <p style={{ color: D.text, fontSize: 12, margin: 0 }}>{val}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

// ─── HR REDIRECT ──────────────────────────────────────────────────────────────

function EmployeePortalApp({ initialUser = null, onLogout = null }) {
  const { isMobile } = useBreakpoint();
  // Restore session from localStorage on page reload
  // Each portal now has separate keys (emp_token vs hr_token)
  // so no cross-contamination between portals
  const savedToken    = TokenStore.get();      // reads emp_token

  const [authed, setAuthed]       = useState(!!savedToken);
  const [userType, setUserType]   = useState(savedToken ? "employee" : null); // eslint-disable-line
  const [user, setUser]           = useState(() => {
    if (initialUser) return initialUser;
    if (!savedToken) return null;
    const u     = TokenStore.getUser() || {};
    const email  = u.email || "";
    const name   = u.name  || email.split("@")[0].replace(/[._]/g, " ").replace(/\b\w/g, c => c.toUpperCase());
    return {
      name,
      email,
      role:      "Employee",
      department:"",
      office:    "",
      shift:     "09:00 – 18:00",
      avatar:    name.slice(0, 2).toUpperCase(),
      role_type: u.role_type || "employee",
      plan:      TokenStore.getPlan(),
      tenant:    { name: "Your Organisation" },
      joining_date: "",
    };
  });
  const [activeTab, setActiveTab] = useState("Home");
  const [checkedIn, setCheckedIn] = useState(false);
  const [checkInTime, setCheckInTime]     = useState(null);
  const [checkOutTime, setCheckOutTime]   = useState(null);
  const [workingHours, setWorkingHours]   = useState(null);
  const [earlyExitMins, setEarlyExitMins] = useState(0);
  const [attendanceDate, setAttendanceDate] = useState(() => new Date().toDateString());
  const [attendanceError, setAttendanceError] = useState("");
  const [loadingAttendance, setLoadingAttendance] = useState(true);

  // Auto-reset at midnight — new day = fresh attendance state
  useEffect(() => {
    const interval = setInterval(() => {
      const today = new Date().toDateString();
      if (today !== attendanceDate) {
        setCheckedIn(false);
        setCheckInTime(null);
        setCheckOutTime(null);
        setWorkingHours(null);
        setEarlyExitMins(0);
        setAttendanceDate(today);
      }
    }, 60000); // check every minute
    return () => clearInterval(interval);
  }, [attendanceDate]);

  // eslint-disable-next-line
  const handleEmployeeLogin = emp => {
    TokenStore.saveUser({ email: emp.email, name: emp.name,
                          role_type: emp.role_type, avatar: emp.avatar });
    setUser(emp); setUserType("employee"); setAuthed(true);
  };
  // eslint-disable-next-line
  const handleHRLogin = (hr) => {
    // Save HR user to separate hr_user key via api.js TokenStore
    TokenStore.saveUser({ email: hr.email, role_type: hr.role_type });
    setUser(hr); setUserType("hr"); setAuthed(true);
  };
  const handleLogout = async () => {
    setUser(null); setUserType(null); setAuthed(false);
    setActiveTab("Home"); setCheckedIn(false);
    setCheckInTime(null); setCheckOutTime(null);
    setWorkingHours(null); setEarlyExitMins(0);
    if (onLogout) onLogout(); // parent handles api.logout() and session clear
  };

  const handleCheckIn = async () => {
    if (checkOutTime || checkedIn) return;
    // Note: week off check happens in HomeTab via weekOffStatus.today_is_week_off
    // Backend also blocks check-in if week_off record exists for today
    try {
      // Pass 0,0 for lat/lng — web portal does not verify location
      const rec = await api.checkIn(0, 0);
      const t   = new Date(rec.check_in_time).toLocaleTimeString("en-IN",
                    {hour:"2-digit",minute:"2-digit",hour12:true,timeZone:"Asia/Kolkata"});
      setCheckedIn(true); setCheckInTime(t); setCheckOutTime(null);
    } catch(err) {
      const msg = typeof err.detail === "string"
        ? err.detail
        : Array.isArray(err.detail)
          ? err.detail.map(e => e.msg).join(", ")
          : "Check-in failed. Please try again.";
      setAttendanceError(msg);
      setTimeout(() => setAttendanceError(""), 4000);
    }
  };

  const handleCheckOut = async () => {
    if (!checkedIn || checkOutTime) return;
    try {
      const rec = await api.checkOut(0, 0);
      const t   = new Date(rec.check_out_time).toLocaleTimeString("en-IN",
                    {hour:"2-digit",minute:"2-digit",hour12:true,timeZone:"Asia/Kolkata"});
      setCheckedIn(false);
      setCheckOutTime(t);
      setWorkingHours(rec.working_hours || null);
      setEarlyExitMins(rec.early_exit_mins || 0);
    } catch(err) {
      const msg = typeof err.detail === "string"
        ? err.detail
        : Array.isArray(err.detail)
          ? err.detail.map(e => e.msg).join(", ")
          : "Check-out failed. Please try again.";
      setAttendanceError(msg);
      setTimeout(() => setAttendanceError(""), 4000);
    }
  };

  // Load today's attendance on mount and after login
  // so state is always in sync with the database
  useEffect(() => {
    if (!authed) return;
    if (activeTab !== "Home") return; // only load when Home tab is active
    const loadToday = async () => {
      setLoadingAttendance(true);
      try {
        console.log("[AttendTrack] Loading today attendance...");
        const rec = await api.getTodayAttendance();
        console.log("[AttendTrack] Today record:", JSON.stringify(rec));
        if (rec && rec.check_in_time) {
          const inT = new Date(rec.check_in_time).toLocaleTimeString("en-IN",
                        {hour:"2-digit",minute:"2-digit",hour12:true,timeZone:"Asia/Kolkata"});
          setCheckInTime(inT);
          if (rec.check_out_time) {
            const outT = new Date(rec.check_out_time).toLocaleTimeString("en-IN",
                           {hour:"2-digit",minute:"2-digit",hour12:true,timeZone:"Asia/Kolkata"});
            setCheckedIn(false);
            setCheckOutTime(outT);
            setWorkingHours(rec.working_hours || null);
            setEarlyExitMins(rec.early_exit_mins || 0);
            console.log("[AttendTrack] Status: Checked out at", outT);
          } else {
            setCheckedIn(true);
            setCheckOutTime(null);
            console.log("[AttendTrack] Status: Checked in at", inT);
          }
        } else if (rec && rec.status === "week_off") {
          // Today is a week off — disable check-in
          setCheckedIn(false);
          setCheckInTime(null);
          setCheckOutTime(null);
          console.log("[AttendTrack] Status: Week off today");
        } else {
          setCheckedIn(false);
          setCheckInTime(null);
          setCheckOutTime(null);
          console.log("[AttendTrack] Status: No record today");
        }
      } catch(e) {
        console.log("[AttendTrack] Error loading today:", e?.detail || e?.message || JSON.stringify(e));
        // Only reset if it is truly a 404 (no record) — not auth errors
        setCheckedIn(false);
        setCheckInTime(null);
        setCheckOutTime(null);
      } finally {
        setLoadingAttendance(false);
      }
    };
    loadToday();
  }, [authed, activeTab]); // re-run when switching back to Home tab

  // Load full profile (office, dept, shift, joining date)
  useEffect(() => {
    if (!authed) return;
    api.getMe().then(profile => {
      if (!profile) return;
      setUser(prev => ({
        ...prev,
        role:         profile.role         || prev.role,
        department:   profile.department   || prev.department,
        office:           profile.office_name  ? `${profile.office_name}${profile.office_city ? ", " + profile.office_city : ""}` : prev.office,
        shift:            profile.shift_start && profile.shift_end ? `${profile.shift_start} – ${profile.shift_end}` : prev.shift,
        joining_date:     profile.joining_date || prev.joining_date,
        office_id:        profile.office_id,
        late_grace_mins:  profile.late_grace_mins ?? 15,
      }));
    }).catch(() => {});
  }, [authed]); // eslint-disable-line

  if (!authed) return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Sora:wght@400;500;600&display=swap" rel="stylesheet" />
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"100vh",background:"#080c12",color:"#4a6275",fontFamily:"monospace"}}>Session expired. Please refresh.</div>
    </>
  );

  // HR handled by parent — never reaches here in normal flow

  return (
    <div style={{ minHeight: "100vh", background: D.bg, display: "flex",
      flexDirection: "column", fontFamily: "'Sora',sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Sora:wght@400;500;600&display=swap" rel="stylesheet" />

      {isMobile
        ? <MobileHeader employee={user} activeTab={activeTab} onLogout={handleLogout} />
        : <TopNav employee={user} activeTab={activeTab} setActiveTab={setActiveTab} onLogout={handleLogout} tabs={EMP_TABS} />
      }

      <div style={{ flex: 1, overflowY: "auto", paddingBottom: isMobile ? "68px" : "0" }}>
        {activeTab === "Home"       && <HomeTab employee={user} checkedIn={checkedIn} checkInTime={checkInTime} checkOutTime={checkOutTime} workingHours={workingHours} earlyExitMins={earlyExitMins} onCheckIn={handleCheckIn} onCheckOut={handleCheckOut} attendanceError={attendanceError} loadingAttendance={loadingAttendance} />}
        {activeTab === "Attendance" && <AttendanceTab />}
        {activeTab === "Leaves"     && <LeavesTab />}
        {activeTab === "Profile"    && <ProfileTab employee={user} />}
      </div>

      {isMobile && <BottomNav activeTab={activeTab} setActiveTab={setActiveTab} tabs={EMP_TABS} icons={EMP_ICONS} />}
    </div>
  );
}


// HRDashboard components

// ─── TENANT CONFIG ────────────────────────────────────────────────────────────
// In production, tenant_id is resolved from window.location.hostname (subdomain)
// e.g. "acme.attendtrack.com" → tenant_id = "acme"
// Each tenant has its own branding loaded from the DB via /tenant/config endpoint

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

// Base palette — accent colours come from tenant config
const C = {
  bg: "#0a0d12",
  surface: "#111620",
  surfaceHover: "#161c28",
  // Defaults (acme/gold) — overridden at runtime by makeBrand(tenant)
  border: "rgba(251,191,36,0.1)",
  borderStrong: "rgba(251,191,36,0.25)",
  gold: "#fbbf24",
  goldDim: "#d97706",
  goldGlow: "rgba(251,191,36,0.15)",
  text: "#e8eaf0",
  textMuted: "#5a6478",
  textDim: "#8892a4",
  green: "#22c55e",
  red: "#ef4444",
  amber: "#f59e0b",
  blue: "#60a5fa",
};

// Returns a C-compatible palette with tenant accent colours injected
function makeBrand(tenant) {
  if (!tenant) return C;
  return {
    ...C,
    gold:        tenant.primary        || C.gold,
    goldDim:     tenant.primaryDim     || C.goldDim,
    goldGlow:    tenant.glow           || C.goldGlow,
    border:      tenant.border         || C.border,
    borderStrong:tenant.borderStrong   || C.borderStrong,
  };
}

// ─── UTILS ────────────────────────────────────────────────────────────────────

function Avatar({ initials, size = 36, color = C.gold }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: size * 0.28,
      background: `${color}18`, border: `1.5px solid ${color}40`,
      display: "flex", alignItems: "center", justifyContent: "center",
      color, fontFamily: "'DM Mono', monospace", fontSize: size * 0.32,
      fontWeight: 700, flexShrink: 0, letterSpacing: 0.5
    }}>{initials}</div>
  );
}


function Stat({ label, value, sub, color = C.gold, small = false }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: small ? 18 : 26, fontWeight: 700, color, lineHeight: 1 }}>{value}</span>
      <span style={{ fontFamily: "'Sora', sans-serif", fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1 }}>{label}</span>
      {sub && <span style={{ fontFamily: "'Sora', sans-serif", fontSize: 10, color: C.textMuted }}>{sub}</span>}
    </div>
  );
}


function SectionHeader({ title, sub, action }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
      <div>
        <h3 style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 14, margin: 0, letterSpacing: 0.3 }}>{title}</h3>
        {sub && <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 11, margin: "3px 0 0" }}>{sub}</p>}
      </div>
      {action}
    </div>
  );
}

// ─── MINI DONUT ───────────────────────────────────────────────────────────────

function DonutRing({ present, late, absent, onLeave, total, size = 56 }) {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const pct = (n) => total > 0 ? (n / total) * circ : 0;
  const segments = [
    { val: present, color: C.green },
    { val: late, color: C.amber },
    { val: absent, color: C.red },
    { val: onLeave, color: C.blue },
  ];
  let offset = circ * 0.25;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: "rotate(-90deg)" }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={C.border} strokeWidth={7} />
      {segments.map((s, i) => {
        const dash = pct(s.val);
        const el = (
          <circle key={i} cx={size/2} cy={size/2} r={r} fill="none"
            stroke={s.color} strokeWidth={7}
            strokeDasharray={`${dash} ${circ - dash}`}
            strokeDashoffset={-offset + circ * 0.25}
            strokeLinecap="butt"
          />
        );
        offset += dash;
        return el;
      })}
    </svg>
  );
}

// ─── SCREENS ──────────────────────────────────────────────────────────────────


// ─── EMPLOYEE HISTORY MODAL ──────────────────────────────────────────────────

function EmployeeHistoryModal({ emp, onClose }) {
  const today      = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const fmt        = (d) => d.toISOString().split("T")[0];

  const [mode, setMode]         = useState("month");
  const [fromDate, setFromDate] = useState(fmt(monthStart));
  const [toDate, setToDate]     = useState(fmt(today));
  const [allRecords, setAllRecords] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const hist = await api.getAttendanceReport({
          from_date:   fmt(monthStart),
          to_date:     fmt(today),
          employee_id: emp.id,
        });
        const STATUS_MAP = { present:"present", late:"late", absent:"absent", leave:"leave", half_day:"present" };
        setAllRecords((hist||[]).map(r => ({
          date:    new Date(r.date).toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"}),
          dateObj: new Date(r.date),
          status:  STATUS_MAP[r.status] || "absent",
          checkIn: r.check_in_time  ? new Date(r.check_in_time).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit",hour12:true,timeZone:"Asia/Kolkata"}) : null,
          checkOut:r.check_out_time ? new Date(r.check_out_time).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit",hour12:true,timeZone:"Asia/Kolkata"}) : null,
          lateBy:  r.late_by_mins || 0,
          hrs:     fmtHours(r.working_hours),
        })));
      } catch(e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [emp.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = allRecords.filter(r => {
    const d = r.dateObj;
    if (mode === "month") {
      return d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
    }
    return r.date >= fromDate && r.date <= toDate;
  });

  const stats = {
    present: filtered.filter(r => r.status === "present").length,
    late:    filtered.filter(r => r.status === "late").length,
    absent:  filtered.filter(r => r.status === "absent").length,
    leave:   filtered.filter(r => r.status === "leave").length,
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.8)", zIndex:2000,
      display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}
      onClick={onClose}>
      <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:20,
        width:"100%", maxWidth:680, maxHeight:"85vh", display:"flex", flexDirection:"column",
        overflow:"hidden" }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding:"18px 24px", borderBottom:`1px solid ${C.border}`,
          display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <Avatar initials={emp.avatar} size={36} color={C.gold} />
            <div>
              <p style={{ fontFamily:"'DM Mono',monospace", color:C.text, fontSize:14, margin:0 }}>{emp.name}</p>
              <p style={{ fontFamily:"'Sora',sans-serif", color:C.textMuted, fontSize:11, margin:"2px 0 0" }}>{emp.role} · {emp.dept}</p>
            </div>
          </div>
          <button onClick={onClose} style={{ background:"rgba(255,255,255,0.05)", border:`1px solid ${C.border}`,
            borderRadius:8, padding:"6px 12px", color:C.textMuted,
            fontFamily:"'DM Mono',monospace", fontSize:12, cursor:"pointer" }}>✕ Close</button>
        </div>

        {/* Mode tabs + date range */}
        <div style={{ padding:"12px 24px", borderBottom:`1px solid ${C.border}`, flexShrink:0,
          display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
          {["month","range"].map(m => (
            <button key={m} onClick={() => setMode(m)} style={{
              background: mode===m ? `${C.gold}15` : "transparent",
              border:`1px solid ${mode===m ? C.gold+"40" : C.border}`,
              borderRadius:8, padding:"5px 14px", color: mode===m ? C.gold : C.textMuted,
              fontFamily:"'DM Mono',monospace", fontSize:11, cursor:"pointer" }}>
              {m === "month" ? "This Month" : "Custom Range"}
            </button>
          ))}
          {mode === "range" && (
            <>
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:8,
                  padding:"5px 10px", color:C.text, fontFamily:"'DM Mono',monospace", fontSize:11, outline:"none",
                  colorScheme:"dark" }} />
              <span style={{ color:C.textMuted, fontSize:11 }}>to</span>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:8,
                  padding:"5px 10px", color:C.text, fontFamily:"'DM Mono',monospace", fontSize:11, outline:"none",
                  colorScheme:"dark" }} />
            </>
          )}
        </div>

        {/* Stats */}
        <div style={{ padding:"12px 24px", borderBottom:`1px solid ${C.border}`, flexShrink:0,
          display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 }}>
          {[
            { label:"Present", val:stats.present, color:C.green  },
            { label:"Late",    val:stats.late,    color:C.amber  },
            { label:"Absent",  val:stats.absent,  color:C.red    },
            { label:"Leave",   val:stats.leave,   color:C.purple },
          ].map(({ label, val, color }) => (
            <div key={label} style={{ background:`${color}08`, border:`1px solid ${color}20`,
              borderRadius:10, padding:"8px 12px", textAlign:"center" }}>
              <p style={{ fontFamily:"'DM Mono',monospace", color, fontSize:20,
                fontWeight:700, margin:"0 0 2px" }}>{val}</p>
              <p style={{ color:C.textMuted, fontSize:10, margin:0 }}>{label}</p>
            </div>
          ))}
        </div>

        {/* Records list */}
        <div style={{ flex:1, overflowY:"auto" }}>
          {loading && (
            <div style={{ padding:"32px", textAlign:"center", color:C.textMuted,
              fontFamily:"'DM Mono',monospace", fontSize:12 }}>Loading…</div>
          )}
          {!loading && filtered.length === 0 && (
            <div style={{ padding:"32px", textAlign:"center", color:C.textMuted,
              fontFamily:"'DM Mono',monospace", fontSize:12 }}>No records found</div>
          )}
          {!loading && filtered.map((r, i) => (
            <div key={i} style={{ display:"grid", gridTemplateColumns:"1.2fr 1fr 1fr 1fr 0.8fr",
              gap:8, padding:"10px 24px", alignItems:"center",
              borderBottom:`1px solid ${C.border}`,
              background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
              <span style={{ fontFamily:"'DM Mono',monospace", color:C.text, fontSize:12 }}>{r.date}</span>
              <span style={{ fontFamily:"'DM Mono',monospace", color: r.checkIn  ? C.green : C.textDim, fontSize:12 }}>{r.checkIn  || "—"}</span>
              <span style={{ fontFamily:"'DM Mono',monospace", color: r.checkOut ? C.text  : C.textDim, fontSize:12 }}>{r.checkOut || "—"}</span>
              <span style={{ fontFamily:"'DM Mono',monospace", color: r.hrs !== "0h" ? C.accent : C.textDim, fontSize:12 }}>{r.hrs}</span>
              <StatusPill status={r.status} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SignInScreen({ onSignIn }) {
  const [tenantId, setTenantId] = useState(() => TokenStore.getTenantId() || "");
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [focused, setFocused]   = useState(null);

  const handleSignIn = async () => {
    if (!tenantId.trim()) { setError("Please enter your Organisation ID"); return; }
    if (!email || !password) return;
    setLoading(true); setError("");
    try {
      if (tenantId.trim()) TokenStore.saveTenantId(tenantId.trim());
      const data = await api.login(email, password);
      if (data.role_type === "employee") {
        // Employees should use the employee portal
        setError("This account is an employee account. Please use the Employee Portal to sign in.");
        setLoading(false);
        return;
      }
      // Save to hr_user key (HR portal uses separate storage)
      TokenStore.saveUser({ email, role_type: data.role_type });
      onSignIn({
        name:      data.name || email.split("@")[0].replace(/[._]/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
        email:     data.email || email,
        role:      data.role_type === "hr_admin" ? "HR Admin" : "Store Manager",
        role_type: data.role_type,
        avatar:    (data.name || email).slice(0, 2).toUpperCase(),
        assignedOffices: data.assigned_office_ids || [],
        tenant:    { name: "Your Organisation", tenant_id: "" },
      });
    } catch(err) {
      setError(err.detail || err.error || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center",
      justifyContent: "center", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", inset: 0,
        backgroundImage: `linear-gradient(${C.border} 1px, transparent 1px), linear-gradient(90deg, ${C.border} 1px, transparent 1px)`,
        backgroundSize: "40px 40px" }} />
      <div style={{ position: "absolute", top: "20%", left: "50%", transform: "translateX(-50%)",
        width: 500, height: 500, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(251,191,36,0.08) 0%, transparent 65%)",
        pointerEvents: "none" }} />

      <div style={{ position: "relative", width: 420, zIndex: 1, padding: "0 16px" }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ width: 54, height: 54, borderRadius: 16,
            background: "linear-gradient(135deg, #fbbf24, #d97706)",
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 12px", boxShadow: "0 0 28px rgba(251,191,36,0.3)", fontSize: 24 }}>⬡</div>
          <h1 style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 20, margin: "0 0 4px" }}>
            AttendTrack
          </h1>
          <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 12, margin: 0 }}>
            HR & Manager Portal
          </p>
        </div>

        <Card style={{ padding: 26, border: "1px solid rgba(251,191,36,0.25)" }}>
          <h2 style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 15,
            margin: "0 0 20px", textAlign: "center" }}>Sign In</h2>

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {error && (() => {
              const isWarn = /remaining|attempt|locked/i.test(error);
              const clr    = isWarn ? "#f59e0b" : C.red;
              const bgClr  = isWarn ? "rgba(245,158,11,0.08)"  : "rgba(239,68,68,0.08)";
              const bdrClr = isWarn ? "1px solid rgba(245,158,11,0.3)" : "1px solid rgba(239,68,68,0.25)";
              return (
                <div style={{ background: bgClr, border: bdrClr,
                  borderRadius: 10, padding: "10px 14px", display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: clr }}>{isWarn ? "⏱" : "⚠"}</span>
                  <span style={{ color: clr, fontSize: 13, fontFamily: "'Sora', sans-serif" }}>{error}</span>
                </div>
              );
            })()}

            {/* Tenant ID */}
            <div>
              <label style={{ fontFamily: "'DM Mono', monospace", color: C.textMuted, fontSize: 10,
                letterSpacing: 1.5, textTransform: "uppercase", display: "block", marginBottom: 6 }}>
                Organisation ID
                <span style={{ color: C.textDim, fontSize: 9, marginLeft: 6,
                  textTransform: "none", letterSpacing: 0, fontFamily: "'Sora', sans-serif" }}>
                  (e.g. acme, comp2601)
                </span>
              </label>
              <input type="text" value={tenantId}
                onChange={e => {
                  const val = e.target.value.replace(/[^a-zA-Z0-9_-]/g,"");
                  setTenantId(val);
                  setError("");
                  if (val) TokenStore.saveTenantId(val);
                }}
                onFocus={() => setFocused("tenant")} onBlur={() => setFocused(null)}
                onKeyDown={e => e.key === "Enter" && handleSignIn()}
                placeholder="your-company-id"
                style={{ width: "100%", boxSizing: "border-box",
                  background: focused === "tenant" ? "rgba(251,191,36,0.05)" : "rgba(255,255,255,0.02)",
                  border: `1px solid ${focused === "tenant" ? "rgba(251,191,36,0.4)" : C.border}`,
                  borderRadius: 10, padding: "11px 14px", color: C.text,
                  fontFamily: "'Sora', sans-serif", fontSize: 13, outline: "none",
                  transition: "all 0.2s" }} />
            </div>

            {[
              { key: "email", label: "Work Email",  type: "email",    val: email,    set: setEmail,    ph: "hr@yourcompany.com" },
              { key: "pass",  label: "Password",    type: "password", val: password, set: setPassword, ph: "••••••••" },
            ].map(({ key, label, type, val, set, ph }) => (
              <div key={key}>
                <label style={{ fontFamily: "'DM Mono', monospace", color: C.textMuted, fontSize: 10,
                  letterSpacing: 1.5, textTransform: "uppercase", display: "block", marginBottom: 6 }}>
                  {label}
                </label>
                <input type={type} value={val}
                  onChange={e => { set(e.target.value); setError(""); }}
                  onFocus={() => setFocused(key)} onBlur={() => setFocused(null)}
                  onKeyDown={e => e.key === "Enter" && handleSignIn()}
                  placeholder={ph}
                  style={{ width: "100%", boxSizing: "border-box",
                    background: focused === key ? "rgba(251,191,36,0.05)" : "rgba(255,255,255,0.02)",
                    border: `1px solid ${focused === key ? "rgba(251,191,36,0.4)" : C.border}`,
                    borderRadius: 10, padding: "11px 14px", color: C.text,
                    fontFamily: "'Sora', sans-serif", fontSize: 13, outline: "none",
                    transition: "all 0.2s" }} />
              </div>
            ))}

            <button onClick={handleSignIn} disabled={!email || !password || loading}
              style={{ padding: "13px", borderRadius: 10, border: "none",
                background: !email || !password
                  ? "rgba(255,255,255,0.04)"
                  : loading
                    ? "rgba(251,191,36,0.2)"
                    : "linear-gradient(135deg, #fbbf24, #d97706)",
                color: !email || !password ? C.textDim : "#1a0e00",
                fontFamily: "'DM Mono', monospace", fontSize: 13, fontWeight: 700,
                cursor: !email || !password ? "not-allowed" : "pointer",
                opacity: !email || !password ? 0.5 : 1,
                transition: "all 0.3s",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              {loading
                ? <><div style={{ width: 14, height: 14, borderRadius: "50%",
                    border: "2px solid #1a0e00", borderTopColor: "transparent",
                    animation: "spin 0.7s linear infinite" }} />Signing in…</>
                : "Sign In →"
              }
            </button>
          </div>
        </Card>

        <p style={{ textAlign: "center", color: C.textMuted, fontFamily: "'Sora', sans-serif",
          fontSize: 11, marginTop: 16 }}>
          Powered by <span style={{ color: "#fbbf24", fontFamily: "'DM Mono', monospace" }}>AttendTrack</span>
          <span style={{ margin: "0 8px", opacity: 0.3 }}>·</span>
          <span style={{ opacity: 0.5 }}>Employee portal at <a href="/" style={{ color: "#fbbf24", textDecoration: "none" }}>/</a></span>
        </p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── TOP NAV ──

function HRTopNav({ hr, activeView, setActiveView, onSignOut }) {
  const B = makeBrand(hr.tenant);
  const isHR = hr.role_type === "hr_admin";
  const isMgr = hr.role_type === "store_manager";

  // HR Admin sees everything; Store Manager sees only their assigned scope
  const navItems = [
    { key: "overview",    label: "Overview",        roles: ["hr_admin", "store_manager"] },
    { key: "offices",     label: "Offices",          roles: ["hr_admin", "store_manager"] },
    { key: "leaves",      label: "Leave Requests",   roles: ["hr_admin", "store_manager"] },
    { key: "my-leave",    label: "My Leave",         roles: ["store_manager"] },  // Manager only
    { key: "reports",     label: "Reports",          roles: ["hr_admin", "store_manager"] },
    { key: "assignments", label: "Assignments",      roles: ["hr_admin"] },  // HR only
  ].filter(n => n.roles.includes(hr.role_type));

  const roleBadgeColor = isHR ? B.gold : C.blue;

  return (
    <div style={{ background: C.surface, borderBottom: `1px solid ${B.border}`, padding: "0 32px", display: "flex", alignItems: "center", gap: 0, height: 60, flexShrink: 0 }}>
      {/* Logo + tenant */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginRight: 32 }}>
        <div style={{ width: 30, height: 30, borderRadius: 8, background: `linear-gradient(135deg, ${B.gold}, ${B.goldDim})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>⬡</div>
        <div>
          <span style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 13 }}>AttendTrack <span style={{ color: B.gold, fontSize: 10 }}>HR</span></span>
          <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 1 }}>
            <div style={{ width: 5, height: 5, borderRadius: "50%", background: B.gold }} />
            <span style={{ fontFamily: "'DM Mono', monospace", color: B.gold, fontSize: 9 }}>{hr?.tenant?.tenant_id || "portal"}</span>
            <span style={{ color: C.textMuted, fontSize: 9 }}>·</span>
            <span style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 9 }}>{hr?.plan?.name || "pro"}</span>
          </div>
        </div>
      </div>

      {/* Nav — filtered by role */}
      <div style={{ display: "flex", gap: 4, flex: 1 }}>
        {navItems.map(({ key, label }) => (
          <button key={key} onClick={() => setActiveView(key)} style={{
            background: activeView === key ? B.goldGlow : "transparent",
            border: `1px solid ${activeView === key ? B.borderStrong : "transparent"}`,
            borderRadius: 8, padding: "6px 14px",
            color: activeView === key ? B.gold : C.textMuted,
            fontFamily: "'DM Mono', monospace", fontSize: 12, cursor: "pointer",
            transition: "all 0.2s", letterSpacing: 0.3
          }}>{label}</button>
        ))}
      </div>

      {/* Right — role badge + user */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {/* Role badge */}
        <div style={{ background: `${roleBadgeColor}12`, border: `1px solid ${roleBadgeColor}30`, borderRadius: 6, padding: "3px 10px" }}>
          <span style={{ fontFamily: "'DM Mono', monospace", color: roleBadgeColor, fontSize: 10, letterSpacing: 0.5 }}>
            {isHR ? "◈ HR ADMIN" : "◉ STORE MGR"}
          </span>
        </div>
        <div style={{ textAlign: "right" }}>
          <p style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 12, margin: 0 }}>{hr.name}</p>
          <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 10, margin: 0 }}>
            {hr?.tenant?.name || "AttendTrack"}
            {isMgr && hr.assignedOffices?.length > 0 && ` · ${hr.assignedOffices.length} office${hr.assignedOffices.length > 1 ? "s" : ""}`}
          </p>
        </div>
        <Avatar initials={hr.avatar || "HR"} size={34} color={roleBadgeColor} />
        <button onClick={onSignOut} style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 8, padding: "6px 12px", color: "#ef4444", fontFamily: "'DM Mono', monospace", fontSize: 11, cursor: "pointer" }}>
          Sign Out
        </button>
      </div>
    </div>
  );
}

// ── OVERVIEW ──


// ─── MY ATTENDANCE WIDGET (for HR Admin & Store Manager) ─────────────────────
function MyAttendanceWidget({ hr }) {
  const B = makeBrand(hr?.tenant);

  const [checkedIn,        setCheckedIn]        = useState(false);
  const [checkInTime,      setCheckInTime]      = useState(null);
  const [checkOutTime,     setCheckOutTime]     = useState(null);
  const [loading,          setLoading]          = useState(true);
  const [actionLoading,    setActionLoading]    = useState(false);
  const [error,            setError]            = useState("");
  const [weekOff,          setWeekOff]          = useState({ today_is_week_off: false, month_used: 0, can_mark_today: false });
  const [weekOffMarking,   setWeekOffMarking]   = useState(false);

  // Load today's attendance on mount
  useEffect(() => {
    const load = async () => {
      try {
        // Load week off status
        const wo = await api.getWeekOffStatus().catch(() => null);
        if (wo) setWeekOff(wo);

        const rec = await api.getTodayAttendance();
        if (rec && rec.check_in_time) {
          const inT = new Date(rec.check_in_time).toLocaleTimeString("en-IN",
            { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" });
          setCheckInTime(inT);
          if (rec.check_out_time) {
            const outT = new Date(rec.check_out_time).toLocaleTimeString("en-IN",
              { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" });
            setCheckedIn(false);
            setCheckOutTime(outT);
          } else {
            setCheckedIn(true);
          }
        }
      } catch(e) {
        // 404 = no record yet = fine
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleCheckIn = async () => {
    if (checkedIn || checkOutTime || actionLoading) return;
    setActionLoading(true); setError("");
    try {
      const rec = await api.checkIn(0, 0);
      const t = new Date(rec.check_in_time).toLocaleTimeString("en-IN",
        { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" });
      setCheckedIn(true); setCheckInTime(t);
    } catch(e) {
      setError(e.detail || "Check-in failed");
    } finally { setActionLoading(false); }
  };

  const handleCheckOut = async () => {
    if (!checkedIn || checkOutTime || actionLoading) return;
    setActionLoading(true); setError("");
    try {
      const rec = await api.checkOut(0, 0);
      const t = new Date(rec.check_out_time).toLocaleTimeString("en-IN",
        { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" });
      setCheckedIn(false); setCheckOutTime(t);
    } catch(e) {
      setError(e.detail || "Check-out failed");
    } finally { setActionLoading(false); }
  };

  // Status label
  const statusLabel = loading ? "Loading…"
    : checkOutTime  ? "Completed for today"
    : checkedIn     ? `Checked in at ${checkInTime}`
    : "Not checked in";

  const statusColor = checkOutTime ? C.textMuted
    : checkedIn ? C.green
    : C.amber;

  return (
    <div style={{
      background: `${B.gold}08`,
      border: `1px solid ${B.gold}30`,
      borderRadius: 16,
      padding: "18px 22px",
      display: "flex",
      alignItems: "center",
      gap: 20,
      flexWrap: "wrap",
    }}>
      {/* Title + status */}
      <div style={{ flex: 1, minWidth: 160 }}>
        <p style={{ fontFamily: "'DM Mono',monospace", color: B.gold,
          fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase",
          margin: "0 0 4px" }}>My Attendance</p>
        <p style={{ fontFamily: "'Sora',sans-serif", color: statusColor,
          fontSize: 13, margin: 0 }}>
          {loading ? "⟳ Loading…" : statusLabel}
        </p>
        {error && (
          <p style={{ color: C.red, fontSize: 11, margin: "4px 0 0",
            fontFamily: "'Sora',sans-serif" }}>⚠ {error}</p>
        )}
      </div>

      {/* Times */}
      {(checkInTime || checkOutTime) && (
        <div style={{ display: "flex", gap: 20 }}>
          {checkInTime && (
            <div style={{ textAlign: "center" }}>
              <p style={{ color: C.textMuted, fontSize: 9, fontFamily: "'DM Mono',monospace",
                letterSpacing: 1, textTransform: "uppercase", margin: "0 0 2px" }}>In</p>
              <p style={{ color: C.green, fontFamily: "'DM Mono',monospace",
                fontSize: 13, margin: 0 }}>{checkInTime}</p>
            </div>
          )}
          {checkOutTime && (
            <div style={{ textAlign: "center" }}>
              <p style={{ color: C.textMuted, fontSize: 9, fontFamily: "'DM Mono',monospace",
                letterSpacing: 1, textTransform: "uppercase", margin: "0 0 2px" }}>Out</p>
              <p style={{ color: C.red, fontFamily: "'DM Mono',monospace",
                fontSize: 13, margin: 0 }}>{checkOutTime}</p>
            </div>
          )}
        </div>
      )}

      {/* Buttons */}
      <div style={{ display: "flex", gap: 10 }}>
        {/* Check In */}
        <button
          onClick={handleCheckIn}
          disabled={checkedIn || !!checkOutTime || loading || actionLoading}
          style={{
            padding: "9px 20px", borderRadius: 10, border: "none",
            background: (checkedIn || checkOutTime || loading)
              ? "rgba(34,197,94,0.08)"
              : "linear-gradient(135deg,#22c55e,#16a34a)",
            color: (checkedIn || checkOutTime || loading) ? C.green : "#042a1c",
            fontFamily: "'DM Mono',monospace", fontSize: 12, fontWeight: 700,
            cursor: (checkedIn || checkOutTime || loading) ? "not-allowed" : "pointer",
            opacity: (checkedIn || checkOutTime) ? 0.5 : 1,
            transition: "all 0.2s",
            display: "flex", alignItems: "center", gap: 6,
          }}>
          {actionLoading && !checkedIn ? "⟳" : checkedIn ? "✓" : checkOutTime ? "✗" : "→"}
          {loading ? "…" : checkedIn ? "Checked In" : checkOutTime ? "Done" : "Check In"}
        </button>

        {/* Check Out */}
        <button
          onClick={handleCheckOut}
          disabled={!checkedIn || !!checkOutTime || loading || actionLoading}
          style={{
            padding: "9px 20px", borderRadius: 10, border: "none",
            background: checkedIn
              ? "linear-gradient(135deg,#ef4444,#b91c1c)"
              : "rgba(239,68,68,0.08)",
            color: checkedIn ? "#fff" : C.red,
            fontFamily: "'DM Mono',monospace", fontSize: 12, fontWeight: 700,
            cursor: checkedIn ? "pointer" : "not-allowed",
            opacity: (!checkedIn && !checkOutTime) ? 0.4 : checkOutTime ? 0.5 : 1,
            transition: "all 0.2s",
            display: "flex", alignItems: "center", gap: 6,
          }}>
          {actionLoading && checkedIn ? "⟳" : checkOutTime ? "✓" : "←"}
          {checkOutTime ? "Checked Out" : "Check Out"}
        </button>
      </div>

      {/* Week Off — distinct teal/cyan style for HR/Manager */}
      {!checkedIn && !checkOutTime && (
        <div style={{ marginTop: 8 }}>
          <button
            onClick={async () => {
              if (!weekOff.can_mark_today || weekOffMarking) return;
              setWeekOffMarking(true);
              try {
                const res = await api.markWeekOff();
                setWeekOff(prev => ({ ...prev,
                  today_is_week_off: true, can_mark_today: false,
                  month_used: res.month_used }));
              } catch(e) { setError(e.detail || "Failed to mark week off"); }
              finally { setWeekOffMarking(false); }
            }}
            disabled={!weekOff.can_mark_today || weekOffMarking || weekOff.today_is_week_off}
            style={{
              width: "100%", padding: "8px 16px", borderRadius: 10,
              background: weekOff.today_is_week_off
                ? "rgba(20,184,166,0.15)"
                : weekOff.month_used >= 4
                  ? "rgba(255,255,255,0.03)"
                  : "rgba(20,184,166,0.1)",
              border: `1px solid ${weekOff.today_is_week_off
                ? "rgba(20,184,166,0.5)"
                : weekOff.month_used >= 4
                  ? C.border
                  : "rgba(20,184,166,0.3)"}`,
              color: weekOff.today_is_week_off
                ? "#2dd4bf"
                : weekOff.month_used >= 4 ? C.textDim : "#5eead4",
              fontFamily: "'DM Mono',monospace", fontSize: 11, fontWeight: 600,
              cursor: (!weekOff.can_mark_today || weekOff.today_is_week_off) ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              opacity: weekOff.month_used >= 4 && !weekOff.today_is_week_off ? 0.5 : 1,
            }}>
            <span style={{ display:"flex", alignItems:"center", gap:6 }}>
              <span>{weekOff.today_is_week_off ? "✓" : weekOffMarking ? "⟳" : "🌿"}</span>
              {weekOff.today_is_week_off ? "Week Off Marked"
                : weekOff.month_used >= 4 ? "No Week Offs Remaining"
                : weekOffMarking ? "Marking…"
                : "Mark as Week Off"}
            </span>
            <span style={{ fontSize: 10, opacity: 0.7 }}>{weekOff.month_used}/4</span>
          </button>
        </div>
      )}
    </div>
  );
}

function OverviewScreen({ onGoToOffice, MOCK_OFFICES, LATE_ALERTS, PENDING_LEAVES, hr }) {
  const B = makeBrand(hr?.tenant);
  const totalEmp     = MOCK_OFFICES.reduce((a, o) => a + (o.totalEmployees||0), 0);
  const totalPresent = MOCK_OFFICES.reduce((a, o) => a + (o.presentToday||0), 0);
  const totalLate    = MOCK_OFFICES.reduce((a, o) => a + (o.lateToday||0), 0);
  const totalLeave   = MOCK_OFFICES.reduce((a, o) => a + (o.onLeave||0), 0);
  const totalAbsent  = MOCK_OFFICES.reduce((a, o) => a + (o.absent||0), 0);
  const totalWeekOff = MOCK_OFFICES.reduce((a, o) => a + (o.weekOff||0), 0);
  const attendanceRate = totalEmp > 0 ? Math.round(((totalPresent + totalLate) / totalEmp) * 100) : 0;

  return (
    <div style={{ padding: "28px 32px", display: "flex", flexDirection: "column", gap: 24 }}>
      {/* My Attendance widget — for HR Admin and Store Manager */}
      <MyAttendanceWidget hr={hr} />

      {/* Top stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 14 }}>
        {[
          { label: "Total Employees", value: totalEmp,             color: B.gold,    icon: "◎" },
          { label: "Present Today",   value: totalPresent,         color: C.green,   icon: "✓" },
          { label: "Late Today",      value: totalLate,            color: C.amber,   icon: "⏱" },
          { label: "On Leave",        value: totalLeave,           color: C.blue,    icon: "◷" },
          { label: "Week Off",        value: totalWeekOff,         color: "#a78bfa", icon: "🌿" },
          { label: "Absent",          value: totalAbsent,          color: C.red,     icon: "✕" },
          { label: "Attendance Rate", value: `${attendanceRate}%`, color: B.gold,    icon: "⬡" },
        ].map(({ label, value, color, icon }) => (
          <Card key={label}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <Stat label={label} value={value} color={color} />
              <span style={{ fontSize: 20, color, opacity: 0.5 }}>{icon}</span>
            </div>
          </Card>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        {/* Office breakdown */}
        <div>
          <SectionHeader title="Office Breakdown" sub="Today's attendance by location" />
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {MOCK_OFFICES.map(office => {
              const rate = office.totalEmployees > 0 ? Math.round(((office.presentToday + (office.lateToday||0)) / office.totalEmployees) * 100) : 0;
              return (
                <Card key={office.id} onClick={() => onGoToOffice(office)} style={{ padding: "16px 18px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <DonutRing present={office.presentToday} late={office.lateToday || 0} absent={office.absent || 0} onLeave={office.onLeave || 0} total={office.totalEmployees || 0} size={52} />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <p style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 13, margin: 0 }}>{office.name}</p>
                        <span style={{ fontFamily: "'DM Mono', monospace", color: C.gold, fontSize: 13 }}>{rate}%</span>
                      </div>
                      <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 11, margin: "3px 0 8px" }}>{office.city} · {office.totalEmployees} employees</p>
                      <div style={{ display: "flex", gap: 12 }}>
                        {[
                          { label: "In", val: office.presentToday, color: C.green },
                          { label: "Leave", val: office.onLeave, color: C.blue },
                          { label: "Absent", val: office.absent, color: C.red },
                        ].map(({ label, val, color }) => (
                          <span key={label} style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color }}>
                            {val} <span style={{ color: C.textMuted }}>{label}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                    <span style={{ color: C.textMuted, fontSize: 16 }}>›</span>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Late alerts */}
          <div>
            <SectionHeader title="Late Arrivals Today" sub={`${LATE_ALERTS.length} employees arrived late`}
              action={<span style={{ fontFamily: "'DM Mono', monospace", color: C.amber, fontSize: 11, cursor: "pointer" }}>View all</span>}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {LATE_ALERTS.map((a, i) => (
                <Card key={i} style={{ padding: "12px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <Avatar initials={a.avatar} size={32} color={C.amber} />
                    <div style={{ flex: 1 }}>
                      <p style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 12, margin: 0 }}>{a.employee}</p>
                      <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 11, margin: "2px 0 0" }}>{a.office} · Checked in {a.time}</p>
                    </div>
                    <span style={{ fontFamily: "'DM Mono', monospace", color: C.amber, fontSize: 11, background: "#1c0e00", border: "1px solid rgba(245,158,11,0.25)", borderRadius: 6, padding: "3px 8px" }}>+{a.lateBy}m</span>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Pending leaves */}
          <div>
            <SectionHeader title="Pending Leave Requests" sub={`${PENDING_LEAVES.length} awaiting approval`}
              action={<span style={{ fontFamily: "'DM Mono', monospace", color: C.gold, fontSize: 11, cursor: "pointer" }}>View all</span>}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {PENDING_LEAVES.slice(0, 3).map((l, i) => (
                <Card key={i} style={{ padding: "12px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <Avatar initials={l.avatar} size={32} color={C.blue} />
                    <div style={{ flex: 1 }}>
                      <p style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 12, margin: 0 }}>{l.employee}</p>
                      <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 11, margin: "2px 0 0" }}>{l.type} · {l.from} – {l.to} ({l.days}d)</p>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.25)", borderRadius: 6, padding: "4px 10px", color: C.green, fontFamily: "'DM Mono', monospace", fontSize: 10, cursor: "pointer" }}>✓</button>
                      <button style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 6, padding: "4px 10px", color: C.red, fontFamily: "'DM Mono', monospace", fontSize: 10, cursor: "pointer" }}>✕</button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── OFFICES LIST ──

function OfficesScreen({ onSelectOffice, MOCK_OFFICES }) {
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("name"); // name | rate | employees
  const [cityFilter, setCityFilter] = useState("all");
  const [page, setPage] = useState(0);
  const PER_PAGE = 6;

  const cities = ["all", ...Array.from(new Set(MOCK_OFFICES.map(o => o.city)))];

  const filtered = MOCK_OFFICES
    .filter(o => {
      const matchSearch = o.name.toLowerCase().includes(search.toLowerCase()) || o.city.toLowerCase().includes(search.toLowerCase());
      const matchCity = cityFilter === "all" || o.city === cityFilter;
      return matchSearch && matchCity;
    })
    .sort((a, b) => {
      if (sortBy === "rate") return (b.presentToday / b.totalEmployees) - (a.presentToday / a.totalEmployees);
      if (sortBy === "employees") return b.totalEmployees - a.totalEmployees;
      return a.name.localeCompare(b.name);
    });

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated = filtered.slice(page * PER_PAGE, (page + 1) * PER_PAGE);

  return (
    <div style={{ padding: "28px 32px" }}>
      {/* Top bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h2 style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 18, margin: 0 }}>All Offices</h2>
          <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 12, margin: "4px 0 0" }}>
            {filtered.length} office{filtered.length !== 1 ? "s" : ""} · showing {Math.min(PER_PAGE, filtered.length - page * PER_PAGE)} of {filtered.length}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {/* Sort */}
          <div style={{ display: "flex", gap: 4 }}>
            {[["name","Name"],["rate","Rate"],["employees","Size"]].map(([key, label]) => (
              <button key={key} onClick={() => { setSortBy(key); setPage(0); }} style={{
                background: sortBy === key ? C.goldGlow : "transparent",
                border: `1px solid ${sortBy === key ? C.borderStrong : C.border}`,
                borderRadius: 6, padding: "5px 12px",
                color: sortBy === key ? C.gold : C.textMuted,
                fontFamily: "'DM Mono', monospace", fontSize: 10, cursor: "pointer"
              }}>{label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Search + city filter */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        <input
          placeholder="Search office name or city…"
          value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
          style={{ flex: 1, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: "9px 14px", color: C.text, fontFamily: "'Sora', sans-serif", fontSize: 13, outline: "none" }}
        />
        <div style={{ display: "flex", gap: 6 }}>
          {cities.map(city => (
            <button key={city} onClick={() => { setCityFilter(city); setPage(0); }} style={{
              background: cityFilter === city ? C.goldGlow : "transparent",
              border: `1px solid ${cityFilter === city ? C.borderStrong : C.border}`,
              borderRadius: 6, padding: "5px 12px",
              color: cityFilter === city ? C.gold : C.textMuted,
              fontFamily: "'DM Mono', monospace", fontSize: 10, cursor: "pointer",
              textTransform: city === "all" ? "capitalize" : "none"
            }}>{city === "all" ? "All Cities" : city}</button>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 16 }}>
        {paginated.map(office => {
          const rate = (office.totalEmployees||0) > 0 ? Math.round(((office.presentToday||0) + (office.lateToday||0)) / office.totalEmployees * 100) : 0;
          return (
            <Card key={office.id} onClick={() => onSelectOffice(office)} style={{ padding: 24 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                <div>
                  <h3 style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 16, margin: "0 0 4px" }}>{office.name}</h3>
                  <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 12, margin: "0 0 2px" }}>📍 {office.address}</p>
                  <span style={{ fontFamily: "'DM Mono', monospace", color: C.textMuted, fontSize: 10, background: "rgba(255,255,255,0.04)", border: `1px solid ${C.border}`, borderRadius: 4, padding: "2px 7px" }}>{office.city}</span>
                </div>
                <span style={{ fontFamily: "'DM Mono', monospace", color: rate >= 80 ? C.gold : rate >= 60 ? C.amber : C.red, fontSize: 22, fontWeight: 700 }}>{rate}%</span>
              </div>
              <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: 4, height: 4, marginBottom: 16, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${rate}%`, background: `linear-gradient(90deg, ${C.green}, ${C.gold})`, borderRadius: 4 }} />
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
                {[
                  { label: "Total",    val: office.totalEmployees||0, color: C.gold    },
                  { label: "Present",  val: office.presentToday||0,   color: C.green   },
                  { label: "Late",     val: office.lateToday||0,      color: C.amber   },
                  { label: "Leave",    val: office.onLeave||0,        color: C.blue    },
                  { label: "Absent",   val: office.absent||0,         color: C.red     },
                  { label: "Off",      val: office.weekOff||0,        color: "#2dd4bf" },
                ].map(({ label, val, color }) => (
                  <div key={label} style={{ flex: "1 1 auto", minWidth: 46,
                    background: "rgba(255,255,255,0.02)",
                    border: `1px solid ${C.border}`,
                    borderRadius: 8, padding: "6px 4px", textAlign: "center" }}>
                    <p style={{ fontFamily: "'DM Mono',monospace", color,
                      fontSize: 15, fontWeight: 700, margin: "0 0 2px" }}>{val}</p>
                    <p style={{ fontFamily: "'Sora',sans-serif", color: C.textMuted,
                      fontSize: 8, textTransform: "uppercase",
                      letterSpacing: 0.5, margin: 0 }}>{label}</p>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 11 }}>Geofence: {office.radius}m radius</span>
                <span style={{ fontFamily: "'DM Mono', monospace", color: C.gold, fontSize: 12 }}>View employees →</span>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, marginTop: 24 }}>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{
            background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8,
            padding: "7px 16px", color: page === 0 ? C.textMuted : C.text,
            fontFamily: "'DM Mono', monospace", fontSize: 12, cursor: page === 0 ? "not-allowed" : "pointer", opacity: page === 0 ? 0.4 : 1
          }}>← Prev</button>
          {Array.from({ length: totalPages }, (_, i) => (
            <button key={i} onClick={() => setPage(i)} style={{
              background: page === i ? C.goldGlow : "transparent",
              border: `1px solid ${page === i ? C.borderStrong : C.border}`,
              borderRadius: 8, padding: "7px 13px",
              color: page === i ? C.gold : C.textMuted,
              fontFamily: "'DM Mono', monospace", fontSize: 12, cursor: "pointer", minWidth: 36
            }}>{i + 1}</button>
          ))}
          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page === totalPages - 1} style={{
            background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8,
            padding: "7px 16px", color: page === totalPages - 1 ? C.textMuted : C.text,
            fontFamily: "'DM Mono', monospace", fontSize: 12, cursor: page === totalPages - 1 ? "not-allowed" : "pointer", opacity: page === totalPages - 1 ? 0.4 : 1
          }}>Next →</button>
          <span style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 11, marginLeft: 8 }}>Page {page + 1} of {totalPages}</span>
        </div>
      )}

      {filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px", color: C.textMuted, fontFamily: "'Sora', sans-serif", fontSize: 13 }}>No offices match your search</div>
      )}
    </div>
  );
}

// ── OFFICE DETAIL (Employee Attendance) ──

// ─── PROXY ATTENDANCE MODAL ──────────────────────────────────────────────────

function ProxyAttendanceModal({ emp, markedBy, onClose, onConfirm }) {
  const now = new Date();
  const timeNow = now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  const dateNow = now.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

  const [action, setAction] = useState(emp.checkIn ? "checkout" : "checkin");
  const [time, setTime] = useState(timeNow);
  const [reason, setReason] = useState("");
  const [reasonOther, setReasonOther] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  const CHECKIN_REASONS = [
    "Device not working",
    "No internet connectivity",
    "Employee forgot to check in",
    "System error",
    "Emergency — employee present but unable to mark",
    "Other",
  ];

  const WEEK_OFF_REASONS = [
    "Regular weekly day off",
    "Employee's scheduled off day",
    "Compensatory off",
    "Manager approved day off",
    "Other",
  ];

  const REASONS = action === "week_off" ? WEEK_OFF_REASONS : CHECKIN_REASONS;

  const handleConfirm = () => {
    if (!reason) return;
    const finalReason = reason === "Other" ? reasonOther : reason;
    if (!finalReason) return;
    setConfirmed(true);
    setTimeout(() => {
      onConfirm({ emp, action, time, reason: finalReason, markedBy });
      onClose();
    }, 1800);
  };


  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={onClose}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 20, width: "100%", maxWidth: 520, overflow: "hidden" }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${C.border}`, display: "flex", gap: 14, alignItems: "center" }}>
          <Avatar initials={emp.avatar} size={44} color={C.amber} />
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <h3 style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 15, margin: 0 }}>{emp.name}</h3>
              <span style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.25)", borderRadius: 6, padding: "2px 8px", fontFamily: "'DM Mono', monospace", color: C.amber, fontSize: 9, letterSpacing: 1 }}>PROXY</span>
            </div>
            <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 11, margin: "3px 0 0" }}>{emp.role} · {emp.dept} · Shift: {emp.shift}</p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: C.textMuted, cursor: "pointer", fontSize: 18, padding: 0 }}>✕</button>
        </div>

        {/* Audit banner */}
        <div style={{ background: "rgba(245,158,11,0.06)", borderBottom: `1px solid rgba(245,158,11,0.15)`, padding: "10px 24px", display: "flex", gap: 10, alignItems: "center" }}>
          <span style={{ fontSize: 14 }}>⚠️</span>
          <p style={{ fontFamily: "'Sora', sans-serif", color: C.amber, fontSize: 11, margin: 0 }}>
            This action will be logged as marked by <strong>{markedBy}</strong> · {dateNow}
          </p>
        </div>

        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 18 }}>

          {/* Current status */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            {[
              { label: "Current Status", val: emp.status, color: { present: C.green, late: C.amber, absent: C.red, leave: C.blue }[emp.status] || C.textMuted },
              { label: "Check In", val: emp.checkIn || "—", color: emp.checkIn ? C.text : C.textMuted },
              { label: "Check Out", val: emp.checkOut || "—", color: emp.checkOut ? C.text : C.textMuted },
            ].map(({ label, val, color }) => (
              <div key={label} style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
                <p style={{ fontFamily: "'DM Mono', monospace", color, fontSize: 12, fontWeight: 700, margin: "0 0 3px", textTransform: "capitalize" }}>{val}</p>
                <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 10, margin: 0, textTransform: "uppercase", letterSpacing: 1 }}>{label}</p>
              </div>
            ))}
          </div>

          {/* Action selector */}
          <div>
            <label style={{ fontFamily: "'DM Mono', monospace", color: C.textMuted, fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", display: "block", marginBottom: 8 }}>Action</label>
            <div style={{ display: "flex", gap: 8 }}>
              {[
                { key: "checkin",  label: "Check In",   icon: "→", disabled: !!emp.checkIn, color: C.green,   bg: "rgba(34,197,94,0.1)",    border: "rgba(34,197,94,0.4)" },
                { key: "checkout", label: "Check Out",  icon: "←", disabled: !emp.checkIn || !!emp.checkOut, color: C.red, bg: "rgba(239,68,68,0.1)", border: "rgba(239,68,68,0.4)" },
                { key: "week_off", label: "Week Off",   icon: "◷", disabled: !!emp.checkIn || emp.status === "week_off", color: "#a78bfa", bg: "rgba(139,92,246,0.1)", border: "rgba(139,92,246,0.4)" },
              ].map(({ key, label, icon, disabled, color, bg, border }) => (
                <button key={key} onClick={() => { if (!disabled) { setAction(key); setReason(""); setReasonOther(""); } }} style={{
                  flex: 1, padding: "10px 6px", borderRadius: 10,
                  background: action === key ? bg : "rgba(255,255,255,0.02)",
                  border: `2px solid ${action === key ? border : C.border}`,
                  color: disabled ? C.textMuted : action === key ? color : C.textDim,
                  cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.4 : 1,
                  fontFamily: "'DM Mono', monospace", fontSize: 10, display: "flex", flexDirection: "column", alignItems: "center", gap: 4
                }}>
                  <span style={{ fontSize: 16 }}>{icon}</span>
                  <span>{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Time override — not needed for week off */}
          {action !== "week_off" && <div>
            <label style={{ fontFamily: "'DM Mono', monospace", color: C.textMuted, fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", display: "block", marginBottom: 8 }}>
              Time <span style={{ color: C.textMuted, fontWeight: 400, letterSpacing: 0 }}>— adjust if needed</span>
            </label>
            <input type="time" value={time.replace(" AM","").replace(" PM","").replace(/(\d+):(\d+)/, (_, h, m) => `${h.padStart(2,"0")}:${m}`)}
              onChange={e => {
                const [h, m] = e.target.value.split(":");
                const hour = parseInt(h);
                const ampm = hour >= 12 ? "PM" : "AM";
                const h12 = hour % 12 || 12;
                setTime(`${String(h12).padStart(2,"0")}:${m} ${ampm}`);
              }}
              style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", color: C.text, fontFamily: "'DM Mono', monospace", fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" }}
            />
          </div>}

          {/* Reason — mandatory */}
          <div>
            <label style={{ fontFamily: "'DM Mono', monospace", color: C.textMuted, fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", display: "block", marginBottom: 8 }}>
              Reason <span style={{ color: C.red, fontSize: 10 }}>*</span>
            </label>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {REASONS.map(r => (
                <button key={r} onClick={() => setReason(r)} style={{
                  padding: "10px 14px", borderRadius: 8, textAlign: "left",
                  background: reason === r ? "rgba(96,165,250,0.08)" : "rgba(255,255,255,0.02)",
                  border: `1px solid ${reason === r ? "rgba(96,165,250,0.35)" : C.border}`,
                  color: reason === r ? C.blue : C.textDim,
                  fontFamily: "'Sora', sans-serif", fontSize: 12, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 10
                }}>
                  <div style={{ width: 14, height: 14, borderRadius: "50%", border: `2px solid ${reason === r ? C.blue : C.textMuted}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {reason === r && <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.blue }} />}
                  </div>
                  {r}
                </button>
              ))}
              {reason === "Other" && (
                <textarea
                  placeholder="Describe the reason…"
                  value={reasonOther} onChange={e => setReasonOther(e.target.value)}
                  rows={2}
                  style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", color: C.text, fontFamily: "'Sora', sans-serif", fontSize: 12, outline: "none", resize: "none", width: "100%", boxSizing: "border-box", marginTop: 4 }}
                />
              )}
            </div>
          </div>

          {/* Confirm */}
          <button onClick={handleConfirm} disabled={!reason || (reason === "Other" && !reasonOther) || confirmed} style={{
            padding: "14px", borderRadius: 10,
            background: confirmed ? "rgba(34,197,94,0.15)" : !reason ? "rgba(255,255,255,0.04)" : "linear-gradient(135deg,#f59e0b,#d97706)",
            border: `1px solid ${confirmed ? "rgba(34,197,94,0.3)" : !reason ? C.border : "rgba(245,158,11,0.4)"}`,
            color: confirmed ? C.green : !reason ? C.textMuted : "#1a0e00",
            fontFamily: "'DM Mono', monospace", fontSize: 13, fontWeight: 700,
            cursor: !reason ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            transition: "all 0.3s"
          }}>
            {confirmed
              ? <><div style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid #22c55e", borderTopColor: "transparent", animation: "spin 0.7s linear infinite" }} /> Recording attendance…</>
              : `✓ Confirm ${action === "checkin" ? "Check In" : action === "week_off" ? "Week Off" : "Check Out"} for ${emp.name}`
            }
          </button>

          <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 10, textAlign: "center", margin: 0 }}>
            This entry will appear in the audit log with your name and reason attached
          </p>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── OFFICE DETAIL SCREEN ─────────────────────────────────────────────────────




// ─── EDIT EMPLOYEE MODAL ──────────────────────────────────────────────────────
function EditEmployeeModal({ emp, office, onClose, onSave }) {
  const DEPTS = ["Engineering","Product","Design","Sales","HR","Analytics",
                 "Mobile","Support","Finance","Operations","Retail","Housekeeping"];

  const [form, setForm] = useState({
    name:            emp.name            || "",
    email:           emp.email           || "",
    role:            emp.role            || "",
    department:      emp.dept            || "",
    role_type:       emp.role_type       || "employee",
    shift_start:     emp.shift?.split("–")[0]?.trim() || "09:00",
    shift_end:       emp.shift?.split("–")[1]?.trim() || "18:00",
    late_grace_mins: String(emp.lateGraceMins ?? 15),
    password:        "",
  });
  const [saving, setSaving]   = useState(false);
  const [error,  setError]    = useState("");
  const [success,setSuccess]  = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim())  { setError("Name is required"); return; }
    if (!form.email.trim()) { setError("Email is required"); return; }
    setSaving(true); setError("");
    try {
      const payload = {
        name:            form.name.trim(),
        email:           form.email.trim(),
        role:            form.role.trim(),
        department:      form.department,
        role_type:       form.role_type,
        shift_start:     form.shift_start,
        shift_end:       form.shift_end,
        late_grace_mins: parseInt(form.late_grace_mins) >= 0 ? parseInt(form.late_grace_mins) : 15,
      };
      if (form.password.length > 0) {
        if (form.password.length < 6) { setError("Password min 6 characters"); setSaving(false); return; }
        payload.password = form.password;
      }
      await api.updateEmployee(emp.id, payload);
      setSuccess(true);
      // Update local emp object
      const updated = { ...emp,
        name:          form.name.trim(),
        email:         form.email.trim(),
        role:          form.role.trim(),
        dept:          form.department,
        role_type:     form.role_type,
        shift:         `${form.shift_start}–${form.shift_end}`,
        avatar:        form.name.trim().split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2),
        lateGraceMins: parseInt(form.late_grace_mins) >= 0 ? parseInt(form.late_grace_mins) : 15,
      };
      setTimeout(() => { onSave(updated); onClose(); }, 1000);
    } catch(e) {
      setError(e.detail || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const inputSt = {
    width: "100%", boxSizing: "border-box", outline: "none",
    background: "rgba(255,255,255,0.02)", border: `1px solid ${C.border}`,
    borderRadius: 10, padding: "10px 12px", color: C.text,
    fontFamily: "'Sora',sans-serif", fontSize: 13,
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.8)", zIndex:3000,
      display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}
      onClick={onClose}>
      <div style={{ background:C.surface, border:`1px solid ${C.borderStrong}`,
        borderRadius:20, width:"100%", maxWidth:580,
        maxHeight:"90vh", display:"flex", flexDirection:"column", overflow:"hidden" }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding:"18px 24px", borderBottom:`1px solid ${C.border}`,
          display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <Avatar initials={emp.avatar} size={36} color={C.gold} />
            <div>
              <p style={{ fontFamily:"'DM Mono',monospace", color:C.text, fontSize:15, margin:0 }}>
                Edit Employee
              </p>
              <p style={{ color:C.textMuted, fontSize:11, margin:"2px 0 0",
                fontFamily:"'Sora',sans-serif" }}>{emp.name}</p>
            </div>
          </div>
          <button onClick={onClose} style={{ background:"rgba(255,255,255,0.05)",
            border:`1px solid ${C.border}`, borderRadius:8, padding:"6px 12px",
            color:C.textMuted, fontFamily:"'DM Mono',monospace", fontSize:12, cursor:"pointer" }}>
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ flex:1, overflowY:"auto", padding:"20px 24px" }}>
          {error && (
            <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.25)",
              borderRadius:10, padding:"10px 14px", marginBottom:14,
              display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ color:C.red }}>⚠</span>
              <span style={{ color:C.red, fontSize:13 }}>{error}</span>
            </div>
          )}
          {success && (
            <div style={{ background:"rgba(34,197,94,0.08)", border:"1px solid rgba(34,197,94,0.25)",
              borderRadius:10, padding:"10px 14px", marginBottom:14,
              display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ color:C.green }}>✓</span>
              <span style={{ color:C.green, fontSize:13 }}>Saved successfully!</span>
            </div>
          )}

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            {/* Name */}
            <div style={{ gridColumn:"span 2" }}>
              <ModalField label="Full Name">
                <input value={form.name} onChange={e => set("name", e.target.value)}
                  style={inputSt} placeholder="Full name" />
              </ModalField>
            </div>

            {/* Email */}
            <ModalField label="Work Email">
              <input type="email" value={form.email}
                onChange={e => set("email", e.target.value)}
                style={inputSt} placeholder="email@company.com" />
            </ModalField>

            {/* Job Title */}
            <ModalField label="Job Title">
              <input value={form.role} onChange={e => set("role", e.target.value)}
                style={inputSt} placeholder="e.g. Retail Executive" />
            </ModalField>

            {/* Department */}
            <ModalField label="Department">
              <select value={form.department} onChange={e => set("department", e.target.value)}
                style={{ ...inputSt, cursor:"pointer" }}>
                <option value="">Select department</option>
                {DEPTS.map(d => <option key={d} value={d}
                  style={{ background:"#111620" }}>{d}</option>)}
              </select>
            </ModalField>

            {/* Role Type */}
            <ModalField label="Access Role">
              <select value={form.role_type} onChange={e => set("role_type", e.target.value)}
                style={{ ...inputSt, cursor:"pointer" }}>
                <option value="employee"      style={{ background:"#111620" }}>👤 Employee</option>
                <option value="store_manager" style={{ background:"#111620" }}>◉ Store Manager</option>
                <option value="hr_admin"      style={{ background:"#111620" }}>◈ HR Admin</option>
              </select>
            </ModalField>

            {/* Shift */}
            <ModalField label="Shift Start">
              <input type="time" value={form.shift_start}
                onChange={e => set("shift_start", e.target.value)} style={inputSt} />
            </ModalField>
            <ModalField label="Shift End">
              <input type="time" value={form.shift_end}
                onChange={e => set("shift_end", e.target.value)} style={inputSt} />
            </ModalField>

            {/* Late grace */}
            <ModalField label="Late Grace (minutes)">
              <input type="number" value={form.late_grace_mins} min="0" max="60"
                onChange={e => set("late_grace_mins", e.target.value)} style={inputSt} />
            </ModalField>

            {/* New password */}
            <ModalField label="New Password (leave blank to keep)">
              <input type="password" value={form.password}
                onChange={e => set("password", e.target.value)}
                style={inputSt} placeholder="Leave blank to keep current" />
            </ModalField>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding:"14px 24px", borderTop:`1px solid ${C.border}`,
          display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}>
          <button onClick={onClose} style={{ background:"transparent",
            border:`1px solid ${C.border}`, borderRadius:8, padding:"8px 18px",
            color:C.textMuted, fontFamily:"'DM Mono',monospace", fontSize:12, cursor:"pointer" }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ background: saving ? "rgba(251,191,36,0.2)"
              : "linear-gradient(135deg,#fbbf24,#d97706)",
              border:"none", borderRadius:8, padding:"9px 24px",
              color:"#1a0e00", fontFamily:"'DM Mono',monospace",
              fontSize:12, fontWeight:700,
              cursor: saving ? "not-allowed" : "pointer",
              display:"flex", alignItems:"center", gap:8 }}>
            {saving ? <><div style={{ width:13, height:13, borderRadius:"50%",
              border:"2px solid #1a0e00", borderTopColor:"transparent",
              animation:"spin 0.7s linear infinite" }} />Saving…</> : "✓ Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ADD EMPLOYEE MODAL HELPERS ───────────────────────────────────────────────
// Defined at module level (NOT inside a component) so React never re-mounts
// inputs on keystroke — prevents the "loses focus after one char" bug

const MODAL_B = {
  gold:        "#fbbf24",
  goldDim:     "#d97706",
  goldGlow:    "rgba(251,191,36,0.15)",
  border:      "rgba(251,191,36,0.1)",
  borderStrong:"rgba(251,191,36,0.25)",
};

function modalInputStyle(highlighted = false) {
  return {
    width: "100%", boxSizing: "border-box", outline: "none",
    background: highlighted ? "rgba(251,191,36,0.05)" : "rgba(255,255,255,0.02)",
    border: `1px solid ${highlighted ? MODAL_B.borderStrong : C.border}`,
    borderRadius: 10, padding: "10px 14px", color: C.text,
    fontFamily: "'Sora', sans-serif", fontSize: 13, transition: "all 0.2s",
    colorScheme: "dark",
  };
}

function ModalField({ label, children, span = 1 }) {
  return (
    <div style={{ gridColumn: `span ${span}` }}>
      <p style={{ fontFamily: "'DM Mono', monospace", color: C.textMuted, fontSize: 10,
        letterSpacing: 1.2, textTransform: "uppercase", margin: "0 0 6px" }}>{label}</p>
      {children}
    </div>
  );
}

// ─── ADD EMPLOYEE MODAL ───────────────────────────────────────────────────────
function AddEmployeeModal({ office, tenantId, onClose, onAdd }) {
  const B = MODAL_B;

  const [form, setForm] = useState({
    name: "", email: "", password: "", role: "", department: "",
    role_type: "employee", shift_start: "09:00", shift_end: "18:00",
    late_grace_mins: "15", joining_date: "",
    address_line1: "", city: "", state: "", postal_code: "", country: "",
  });
  const [error, setError]     = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep]       = useState(1); // 1=basic, 2=schedule, 3=address

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const DEPTS = ["Engineering","Product","Design","Sales","HR","Analytics","Mobile","Support","Finance","Operations"];

  const handleSubmit = async () => {
    setError("");
    if (!form.name.trim())  { setError("Name is required"); return; }
    if (!form.email.trim()) { setError("Email is required"); return; }
    if (!form.email.includes("@")) { setError("Enter a valid email"); return; }
    if (!form.password || form.password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (!form.role.trim()) { setError("Job title is required"); return; }

    setLoading(true);
    try {
      // Call real API — backend reads tenant_id from JWT, office_id assigns to this office
      const emp = await api.addEmployee({
        name:            form.name.trim(),
        email:           form.email.trim(),
        password:        form.password,
        role:            form.role.trim(),
        department:      form.department  || null,
        role_type:       form.role_type,
        office_id:       office.id,                    // ← assign to current office
        shift_start:     form.shift_start + ":00",
        shift_end:       form.shift_end   + ":00",
        late_grace_mins: parseInt(form.late_grace_mins) >= 0 ? parseInt(form.late_grace_mins) : 15,
        address_line1:   form.address_line1 || null,
        city:            form.city          || null,
        state:           form.state         || null,
        postal_code:     form.postal_code   || null,
        country:         form.country       || null,
        joining_date:    form.joining_date  || null,
      });
      // Map API response to the display shape used by empList
      const newEmp = {
        id:            emp.id,
        tenant_id:     emp.tenant_id,
        name:          emp.name,
        role:          emp.role         || "",
        dept:          emp.department   || "",
        email:         emp.email,
        shift:         `${form.shift_start}–${form.shift_end}`,
        role_type:     emp.role_type,
        status:        "absent",
        checkIn:       null, checkOut: null,
        lateBy:        0,    workingHrs: "0h",
        earlyExitMins: 0,
        lateGraceMins: emp.late_grace_mins ?? 15,
        leaveBalance:  0,
        avatar:        emp.name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2),
        isManager:     emp.role_type === "store_manager",
      };
      onAdd(newEmp);
      onClose();
    } catch (e) {
      setError(e.detail || e.message || "Failed to add employee. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const steps = ["Basic Info", "Schedule", "Address"];

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 2000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
      onClick={onClose}>
      <div style={{ background: C.surface, border: `1px solid ${B.borderStrong}`,
        borderRadius: 20, width: "100%", maxWidth: 640,
        maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${C.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div>
            <h3 style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 16, margin: 0 }}>
              Add Employee
            </h3>
            <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 12, margin: "4px 0 0" }}>
              {office.name} · {office.city}
            </p>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.05)", border: `1px solid ${C.border}`,
            borderRadius: 8, padding: "6px 12px", color: C.textMuted,
            fontFamily: "'DM Mono', monospace", fontSize: 12, cursor: "pointer" }}>✕</button>
        </div>

        {/* Step tabs */}
        <div style={{ display: "flex", borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          {steps.map((s, i) => (
            <button key={s} onClick={() => step > i + 1 && setStep(i + 1)}
              style={{ flex: 1, padding: "12px 8px", background: "transparent", border: "none",
                borderBottom: `2px solid ${step === i + 1 ? B.gold : "transparent"}`,
                color: step === i + 1 ? B.gold : step > i + 1 ? C.green : C.textMuted,
                fontFamily: "'DM Mono', monospace", fontSize: 11, cursor: step > i + 1 ? "pointer" : "default",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              <span style={{ width: 18, height: 18, borderRadius: "50%",
                background: step > i + 1 ? C.green : step === i + 1 ? B.gold : "rgba(255,255,255,0.05)",
                border: `1px solid ${step > i + 1 ? C.green : step === i + 1 ? B.gold : C.border}`,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                fontSize: 9, color: step > i + 1 || step === i + 1 ? "#0a0d12" : C.textMuted,
                fontWeight: 700 }}>
                {step > i + 1 ? "✓" : i + 1}
              </span>
              {s}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>

          {error && (
            <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)",
              borderRadius: 10, padding: "10px 14px", marginBottom: 16,
              display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: C.red }}>⚠</span>
              <span style={{ color: C.red, fontFamily: "'Sora', sans-serif", fontSize: 13 }}>{error}</span>
            </div>
          )}

          {/* Step 1 — Basic Info */}
          {step === 1 && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <ModalField label="Full Name" span={2}>
                <input value={form.name} onChange={e => set("name", e.target.value)}
                  placeholder="e.g. Alex Rivera" style={modalInputStyle(false)} />
              </ModalField>
              <ModalField label="Work Email">
                <input type="email" value={form.email} onChange={e => set("email", e.target.value)}
                  placeholder="alex@company.com" style={modalInputStyle(false)} />
              </ModalField>
              <ModalField label="Temporary Password">
                <input type="password" value={form.password} onChange={e => set("password", e.target.value)}
                  placeholder="Min 6 characters" style={modalInputStyle(false)} />
              </ModalField>
              <ModalField label="Job Title">
                <input value={form.role} onChange={e => set("role", e.target.value)}
                  placeholder="e.g. Software Engineer" style={modalInputStyle(false)} />
              </ModalField>
              <ModalField label="Department">
                <select value={form.department} onChange={e => set("department", e.target.value)}
                  style={{ ...modalInputStyle(false), cursor: "pointer" }}>
                  <option value="">Select department</option>
                  {DEPTS.map(d => <option key={d} value={d} style={{ background: "#111620" }}>{d}</option>)}
                </select>
              </ModalField>

              {/* Role type — KEY FIELD */}
              <ModalField label="Access Role" span={2}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                  {[
                    { val: "employee",      icon: "👤", label: "Employee",      desc: "Can check in/out via app or portal" },
                    { val: "store_manager", icon: "◉",  label: "Store Manager", desc: "Employee + can manage assigned offices" },
                    { val: "hr_admin",      icon: "◈",  label: "HR Admin",      desc: "Full access to all offices and settings" },
                  ].map(({ val, icon, label, desc }) => (
                    <button key={val} onClick={() => set("role_type", val)}
                      style={{ padding: "12px 10px", borderRadius: 12, border: "none", cursor: "pointer",
                        background: form.role_type === val ? "rgba(251,191,36,0.1)" : "rgba(255,255,255,0.02)",
                        borderWidth: 1, borderStyle: "solid",
                        borderColor: form.role_type === val ? B.borderStrong : C.border,
                        display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                        transition: "all 0.2s", textAlign: "center" }}>
                      <span style={{ fontSize: 20 }}>{icon}</span>
                      <span style={{ fontFamily: "'DM Mono', monospace",
                        color: form.role_type === val ? B.gold : C.textMuted,
                        fontSize: 11, fontWeight: 700 }}>{label}</span>
                      <span style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 10 }}>
                        {desc}
                      </span>
                    </button>
                  ))}
                </div>
              </ModalField>

              {form.role_type === "store_manager" && (
                <div style={{ gridColumn: "span 2", background: "rgba(96,165,250,0.08)",
                  border: "1px solid rgba(96,165,250,0.2)", borderRadius: 10, padding: "12px 14px",
                  display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <span style={{ fontSize: 18, flexShrink: 0 }}>ℹ</span>
                  <div>
                    <p style={{ fontFamily: "'DM Mono', monospace", color: C.blue, fontSize: 12,
                      margin: "0 0 4px", fontWeight: 700 }}>Store Manager = Employee + Manager</p>
                    <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 12, margin: 0 }}>
                      This person will mark their own attendance like any employee (via the employee portal or mobile app)
                      AND have access to the HR dashboard to manage their assigned offices.
                      Assign their offices from the Assignments tab after creating.
                    </p>
                  </div>
                </div>
              )}

              <ModalField label="Joining Date">
                <input type="date" value={form.joining_date} onChange={e => set("joining_date", e.target.value)}
                  style={modalInputStyle(false)} />
              </ModalField>
            </div>
          )}

          {/* Step 2 — Schedule */}
          {step === 2 && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <ModalField label="Shift Start">
                <input type="time" value={form.shift_start} onChange={e => set("shift_start", e.target.value)}
                  style={modalInputStyle(false)} />
              </ModalField>
              <ModalField label="Shift End">
                <input type="time" value={form.shift_end} onChange={e => set("shift_end", e.target.value)}
                  style={modalInputStyle(false)} />
              </ModalField>
              <ModalField label="Late Grace Period (minutes)" span={2}>
                <input type="number" value={form.late_grace_mins} min="0" max="60"
                  onChange={e => set("late_grace_mins", e.target.value)}
                  style={modalInputStyle(false)} />
              </ModalField>
              <div style={{ gridColumn: "span 2", background: "rgba(251,191,36,0.05)",
                border: `1px solid ${B.border}`, borderRadius: 10, padding: "14px 16px" }}>
                <p style={{ fontFamily: "'DM Mono', monospace", color: B.gold, fontSize: 11,
                  margin: "0 0 6px", letterSpacing: 0.5 }}>SHIFT PREVIEW</p>
                <p style={{ fontFamily: "'Sora', sans-serif", color: C.text, fontSize: 13, margin: 0 }}>
                  Working hours: <strong>{form.shift_start}</strong> to <strong>{form.shift_end}</strong>
                  &nbsp;· Late if checked in after <strong>
                    {(() => {
                      try {
                        const [h, m] = form.shift_start.split(":").map(Number);
                        const grace = parseInt(form.late_grace_mins) || 0;
                        const total = h * 60 + m + grace;
                        return `${String(Math.floor(total/60)).padStart(2,"0")}:${String(total%60).padStart(2,"0")}`;
                      } catch { return "—"; }
                    })()}
                  </strong>
                </p>
              </div>
            </div>
          )}

          {/* Step 3 — Address */}
          {step === 3 && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <ModalField label="Address Line 1" span={2}>
                <input value={form.address_line1} onChange={e => set("address_line1", e.target.value)}
                  placeholder="Street address" style={modalInputStyle(false)} />
              </ModalField>
              <ModalField label="City">
                <input value={form.city} onChange={e => set("city", e.target.value)}
                  placeholder="City" style={modalInputStyle(false)} />
              </ModalField>
              <ModalField label="State">
                <input value={form.state} onChange={e => set("state", e.target.value)}
                  placeholder="State" style={modalInputStyle(false)} />
              </ModalField>
              <ModalField label="Postal Code">
                <input value={form.postal_code} onChange={e => set("postal_code", e.target.value)}
                  placeholder="Postal code" style={modalInputStyle(false)} />
              </ModalField>
              <ModalField label="Country">
                <input value={form.country} onChange={e => set("country", e.target.value)}
                  placeholder="Country" style={modalInputStyle(false)} />
              </ModalField>
              <div style={{ gridColumn: "span 2", background: "rgba(34,197,94,0.06)",
                border: "1px solid rgba(34,197,94,0.2)", borderRadius: 10, padding: "12px 14px" }}>
                <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 12, margin: 0 }}>
                  ℹ Address is optional — you can add or update it later from the employee detail panel.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "16px 24px", borderTop: `1px solid ${C.border}`,
          display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <button onClick={() => step > 1 ? setStep(step - 1) : onClose()}
            style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8,
              padding: "8px 18px", color: C.textMuted, fontFamily: "'DM Mono', monospace",
              fontSize: 12, cursor: "pointer" }}>
            {step === 1 ? "Cancel" : "← Back"}
          </button>

          {step < 3 ? (
            <button onClick={() => { setError(""); setStep(step + 1); }}
              style={{ background: `linear-gradient(135deg, ${B.gold}, ${B.goldDim})`,
                border: "none", borderRadius: 8, padding: "9px 22px",
                color: "#1a0e00", fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 700,
                cursor: "pointer", boxShadow: `0 4px 14px ${B.goldGlow}` }}>
              Next →
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={loading}
              style={{ background: loading ? "rgba(34,197,94,0.2)" : "linear-gradient(135deg,#22c55e,#16a34a)",
                border: "none", borderRadius: 8, padding: "9px 22px",
                color: "#042a1c", fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 700,
                cursor: loading ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", gap: 8,
                boxShadow: loading ? "none" : "0 4px 14px rgba(34,197,94,0.25)" }}>
              {loading
                ? <><div style={{ width: 13, height: 13, borderRadius: "50%",
                    border: "2px solid #042a1c", borderTopColor: "transparent",
                    animation: "spin 0.7s linear infinite" }} />Creating…</>
                : "✓ Create Employee"}
            </button>
          )}
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ─── ATTENDANCE OVERRIDE MODAL ────────────────────────────────────────────────
// Shown when HR/Manager wants to change a present/late employee's status
// to Leave (SL/CL), Week-Off, or Absent for today.
function AttendanceOverrideModal({ emp, onClose, onSuccess }) {
  const [leaveTypes,   setLeaveTypes]   = useState([]);
  const [newStatus,    setNewStatus]    = useState("");
  const [leaveTypeId,  setLeaveTypeId]  = useState(null);
  const [note,         setNote]         = useState("");
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState("");

  useEffect(() => {
    api.getLeaveTypesForEmployee()
      .then(types => setLeaveTypes((types || []).filter(t => !t.is_flexible)))
      .catch(() => {});
  }, []);

  const handleConfirm = async () => {
    setError("");
    if (!newStatus)              { setError("Please select a new status"); return; }
    if (!note.trim())            { setError("Please enter a note explaining why"); return; }
    if (note.trim().length < 5)  { setError("Note must be at least 5 characters"); return; }
    if (newStatus === "leave" && !leaveTypeId) { setError("Please select a leave type"); return; }
    setLoading(true);
    try {
      await api.overrideAttendanceStatus(
        emp.id,
        newStatus,
        note.trim(),
        newStatus === "leave" ? leaveTypeId : null,
      );
      onSuccess(newStatus);
    } catch (e) {
      setError(e.detail || "Override failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Two modes: forward (present → leave/wo/absent) or reverse (leave/wo/absent → present)
  const isReverse = ["leave", "week_off", "absent"].includes(emp.status);

  const FORWARD_OPTIONS = [
    { val: "leave",    label: "Leave (SL / CL)",   icon: "🌿", color: "#60a5fa",
      desc: "Deducts 1 day from the selected leave balance" },
    { val: "week_off", label: "Week Off",           icon: "◷",  color: "#a78bfa",
      desc: "Counts against their 48 week-off limit" },
    { val: "absent",   label: "Absent",             icon: "✕",  color: "#f87171",
      desc: "No balance deducted — just marks absent" },
  ];

  const REVERSE_OPTIONS = [
    { val: "present", label: "Present (Manual)",   icon: "✓",  color: "#4ade80",
      desc: emp.status === "leave"
        ? "Cancels the leave application and restores leave balance"
        : "Changes status back to present — no balance impact" },
  ];

  const STATUS_OPTIONS = isReverse ? REVERSE_OPTIONS : FORWARD_OPTIONS;

  const inputSt = {
    width: "100%", boxSizing: "border-box", outline: "none",
    background: "rgba(255,255,255,0.03)", border: `1px solid ${C.border}`,
    borderRadius: 10, padding: "10px 12px", color: C.text,
    fontFamily: "'Sora', sans-serif", fontSize: 13, colorScheme: "dark",
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)",
      zIndex: 3500, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
      onClick={onClose}>
      <div style={{ background: C.surface, border: "1px solid rgba(239,68,68,0.25)",
        borderRadius: 18, padding: 28, width: "100%", maxWidth: 460 }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
          <div>
            <p style={{ fontFamily: "'DM Mono', monospace", color: C.red, fontSize: 13,
              margin: "0 0 4px", letterSpacing: 0.3 }}>⚠ Override Attendance</p>
            <p style={{ fontFamily: "'Sora', sans-serif", color: C.text, fontSize: 15,
              fontWeight: 600, margin: 0 }}>{emp.name}</p>
            <p style={{ fontFamily: "'DM Mono', monospace", color: C.textMuted, fontSize: 11, margin: "3px 0 0" }}>
              Current status: <span style={{ color: C.amber, textTransform: "uppercase" }}>{emp.status}</span>
              {emp.checkIn && ` · Checked in at ${emp.checkIn}`}
            </p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none",
            color: C.textMuted, fontSize: 18, cursor: "pointer", padding: 0 }}>✕</button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Status selector */}
          <div>
            <p style={{ fontFamily: "'DM Mono', monospace", color: C.textDim, fontSize: 10,
              textTransform: "uppercase", letterSpacing: 1, margin: "0 0 8px" }}>Change Status To</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {STATUS_OPTIONS.map(opt => (
                <button key={opt.val}
                  onClick={() => { setNewStatus(opt.val); setLeaveTypeId(null); setError(""); }}
                  style={{
                    background: newStatus === opt.val ? `${opt.color}12` : "rgba(255,255,255,0.02)",
                    border: `1px solid ${newStatus === opt.val ? opt.color + "50" : C.border}`,
                    borderRadius: 10, padding: "10px 14px", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 10, textAlign: "left",
                  }}>
                  <span style={{ fontSize: 16 }}>{opt.icon}</span>
                  <div>
                    <p style={{ fontFamily: "'DM Mono', monospace",
                      color: newStatus === opt.val ? opt.color : C.text,
                      fontSize: 12, margin: 0 }}>{opt.label}</p>
                    <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted,
                      fontSize: 10, margin: "2px 0 0" }}>{opt.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Leave type selector — only when Leave is selected */}
          {newStatus === "leave" && (
            <div>
              <p style={{ fontFamily: "'DM Mono', monospace", color: C.textDim, fontSize: 10,
                textTransform: "uppercase", letterSpacing: 1, margin: "0 0 6px" }}>Leave Type</p>
              <select
                value={leaveTypeId || ""}
                onChange={e => { setLeaveTypeId(parseInt(e.target.value)); setError(""); }}
                style={{ ...inputSt, cursor: "pointer" }}>
                <option value="" disabled style={{ background: "#111620" }}>
                  {leaveTypes.length === 0 ? "Loading…" : "Select leave type"}
                </option>
                {leaveTypes.map(t => (
                  <option key={t.id} value={t.id} style={{ background: "#111620" }}>{t.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Note */}
          <div>
            <p style={{ fontFamily: "'DM Mono', monospace", color: C.textDim, fontSize: 10,
              textTransform: "uppercase", letterSpacing: 1, margin: "0 0 6px" }}>
              Note <span style={{ color: C.red, fontSize: 10 }}>*</span>
            </p>
            <input
              type="text"
              value={note}
              placeholder="e.g. Employee called — unable to attend today"
              onChange={e => { setNote(e.target.value); setError(""); }}
              style={inputSt}
            />
            <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 10,
              margin: "4px 0 0" }}>This note is saved for audit purposes</p>
          </div>

          {error && (
            <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)",
              borderRadius: 8, padding: "8px 12px" }}>
              <p style={{ fontFamily: "'Sora', sans-serif", color: C.red, fontSize: 12, margin: 0 }}>
                ⚠ {error}
              </p>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
            <button onClick={onClose}
              style={{ flex: 1, background: "transparent", border: `1px solid ${C.border}`,
                borderRadius: 10, padding: "10px", color: C.textMuted,
                fontFamily: "'DM Mono', monospace", fontSize: 12, cursor: "pointer" }}>
              Cancel
            </button>
            <button onClick={handleConfirm} disabled={loading}
              style={{ flex: 2, background: "rgba(239,68,68,0.12)",
                border: "1px solid rgba(239,68,68,0.35)", borderRadius: 10, padding: "10px",
                color: C.red, fontFamily: "'DM Mono', monospace", fontSize: 12,
                fontWeight: 700, cursor: loading ? "not-allowed" : "pointer" }}>
              {loading ? "⟳ Overriding…" : "✓ Confirm Override"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function OfficeDetailScreen({ office, onBack, MOCK_EMPLOYEES, currentUser, isHR = false, onEmployeeAdded }) {
  const [empList, setEmpList] = useState(MOCK_EMPLOYEES[office.id] || []);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [historyEmp, setHistoryEmp] = useState(null);
  const [proxyEmp, setProxyEmp] = useState(null);
  const [proxyLog, setProxyLog] = useState([]); // audit trail for this session
  const [showAddModal, setShowAddModal]       = useState(false);
  const [editEmp, setEditEmp]                 = useState(null);
  const [deactivateEmp, setDeactivateEmp]     = useState(null);
  const [deactivating, setDeactivating]       = useState(false);
  const [overrideEmp, setOverrideEmp]         = useState(null); // employee whose status to override
  const [overrideConfig, setOverrideConfig]   = useState(null); // { cutoff_time, window_open, is_hr_exempt }
    const [, setLoadingStatus] = useState(true);
    const [officeStats, setOfficeStats] = useState({
      totalEmployees: office.totalEmployees || 0,
      present:        office.presentToday   || 0,
      late:           office.lateToday      || 0,
      onLeave:        office.onLeave        || 0,
      absent:         office.absent         || 0,
      weekOff:        office.weekOff        || 0,
    });
    const [selectedEmpLeave, setSelectedEmpLeave] = useState([]);

    // Load override config once on mount
    useEffect(() => {
      api.getOverrideConfig()
        .then(cfg => setOverrideConfig(cfg))
        .catch(() => setOverrideConfig({ cutoff_time: null, window_open: true, is_hr_exempt: false }));
    }, []); // eslint-disable-line

    // Load today's real attendance from DB
    useEffect(() => {
      const loadToday = async () => {
        setLoadingStatus(true);
        try {
          // Load today's attendance
          const today = await api.getOfficeTodayAttendance(office.id);
          const attMap = {};
          (today || []).forEach(r => { attMap[r.employee_id] = r; });

          // Load leave balances for all employees in parallel
          const empIds = (MOCK_EMPLOYEES[office.id] || []).map(e => e.id);
          const balResults = await Promise.all(
            empIds.map(id => api.getEmployeeLeaveBalance(id).catch(() => []))
          );
          const balMap = {};
          empIds.forEach((id, i) => {
            const bal = balResults[i] || [];
            // Build short summary e.g. "SL:4 AL:12"
            balMap[id] = bal.length > 0
              ? bal.map(b => `${b.short}:${b.remaining_days}`).join(" ")
              : "";
          });

          const fmt = t => t ? new Date(t).toLocaleTimeString("en-IN",
            { hour:"2-digit", minute:"2-digit", hour12:true, timeZone:"Asia/Kolkata" }) : null;

          const updatedList = empList.map(emp => {
            const rec = attMap[emp.id];
            const wh  = fmtHours(rec && rec.working_hours);
            return {
              ...emp,
              status:              rec ? (rec.status || "absent") : "absent",
              checkIn:             rec ? fmt(rec.check_in_time)  : null,
              checkOut:            rec ? fmt(rec.check_out_time) : null,
              lateBy:              rec ? (rec.late_by_mins   || 0) : 0,
              earlyExitMins:       rec ? (rec.early_exit_mins || 0) : 0,
              workingHrs:          wh,
              leaveBalanceSummary: balMap[emp.id] || "",
            };
          });
          setEmpList(updatedList);

          // Recalculate office top-bar stats from real data
          const statCount = { present:0, late:0, absent:0, onLeave:0, weekOff:0 };
          updatedList.forEach(e => {
            if      (e.status === "present")  statCount.present++;
            else if (e.status === "late")     statCount.late++;
            else if (e.status === "leave")    statCount.onLeave++;
            else if (e.status === "week_off") statCount.weekOff++;
            else                              statCount.absent++;
          });
          setOfficeStats({ ...statCount, totalEmployees: updatedList.length });
        } catch(e) { console.error("loadToday:", e); }
        finally { setLoadingStatus(false); }
      };
      loadToday();
    }, [office.id]); // eslint-disable-line

    // Load leave balance when employee selected
    useEffect(() => {
      if (!selectedEmp) { setSelectedEmpLeave([]); return; }
      api.getEmployeeLeaveBalance(selectedEmp.id)
        .then(d => setSelectedEmpLeave(d || []))
        .catch(() => setSelectedEmpLeave([]));
    }, [selectedEmp?.id]); // eslint-disable-line

  const handleAddEmployee = (newEmp) => {
    setEmpList(prev => [newEmp, ...prev]);
    // Keep parent MOCK_EMPLOYEES in sync so navigating away and back shows the new employee
    onEmployeeAdded?.(office.id, newEmp);
  };

  const handleSaveEdit = (updatedEmp) => {
    setEmpList(prev => prev.map(e => e.id === updatedEmp.id ? updatedEmp : e));
    if (selectedEmp?.id === updatedEmp.id) setSelectedEmp(updatedEmp);
  };

  const handleDeactivate = async () => {
    if (!deactivateEmp) return;
    setDeactivating(true);
    try {
      await api.deactivateEmployee(deactivateEmp.id);
      setEmpList(prev => prev.filter(e => e.id !== deactivateEmp.id));
      if (selectedEmp?.id === deactivateEmp.id) setSelectedEmp(null);
      setDeactivateEmp(null);
    } catch(e) {
      alert(e.detail || "Failed to deactivate employee");
    } finally {
      setDeactivating(false);
    }
  };

  const filtered = empList.filter(e => {
    const matchSearch = e.name.toLowerCase().includes(search.toLowerCase()) || e.dept.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === "all" || e.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const rate = Math.round((office.presentToday / office.totalEmployees) * 100);

  // Called when manager confirms proxy attendance
  const handleProxyConfirm = async ({ emp, action, time, reason, markedBy }) => {
    try {
      if (action === "checkin")  { await api.proxyCheckIn(emp.id, reason); }
      else if (action === "checkout") { await api.proxyCheckOut(emp.id, reason); }
      else if (action === "week_off") { await api.proxyWeekOff(emp.id, reason); }
    } catch(e) { alert(e.detail || "Proxy failed"); return; }

    if (action === "checkout") {
      // Re-fetch today's attendance so we get backend-computed working_hours & early_exit_mins
      try {
        const fmt = t => t ? new Date(t).toLocaleTimeString("en-IN",
          { hour:"2-digit", minute:"2-digit", hour12:true, timeZone:"Asia/Kolkata" }) : null;
        const todayRecs = await api.getOfficeTodayAttendance(office.id);
        const attMap = {};
        (todayRecs || []).forEach(r => { attMap[r.employee_id] = r; });
        setEmpList(prev => prev.map(e => {
          if (e.id !== emp.id) return e;
          const rec = attMap[emp.id];
          const wh  = rec && rec.working_hours ? fmtHours(rec.working_hours) : (e.workingHrs || "0h");
          return {
            ...e,
            checkOut:      rec ? fmt(rec.check_out_time) : time,
            status:        rec ? (rec.status || e.status) : e.status,
            workingHrs:    wh,
            earlyExitMins: rec ? (rec.early_exit_mins || 0) : 0,
          };
        }));
        // Update selectedEmp panel too
        if (selectedEmp?.id === emp.id) {
          const rec = attMap[emp.id];
          const wh  = rec && rec.working_hours ? fmtHours(rec.working_hours) : (selectedEmp.workingHrs || "0h");
          setSelectedEmp(prev => ({
            ...prev,
            checkOut:      rec ? fmt(rec.check_out_time) : time,
            status:        rec ? (rec.status || prev.status) : prev.status,
            workingHrs:    wh,
            earlyExitMins: rec ? (rec.early_exit_mins || 0) : 0,
          }));
        }
      } catch {
        // Fallback: just update checkOut time without working hours
        setEmpList(prev => prev.map(e => e.id !== emp.id ? e : { ...e, checkOut: time }));
        if (selectedEmp?.id === emp.id) setSelectedEmp(prev => ({ ...prev, checkOut: time }));
      }
    } else {
      setEmpList(prev => prev.map(e => {
        if (e.id !== emp.id) return e;
        if (action === "checkin")  return { ...e, checkIn: time, status: "present", workingHrs: "0h" };
        if (action === "week_off") return { ...e, status: "week_off", checkIn: null, checkOut: null };
        return e;
      }));
      // Update selectedEmp panel too
      if (selectedEmp?.id === emp.id) {
        setSelectedEmp(prev => action === "checkin"
          ? { ...prev, checkIn: time, status: "present" }
          : { ...prev, status: "week_off", checkIn: null, checkOut: null }
        );
      }
    }
    // Add to session audit log
    setProxyLog(prev => [{
      emp: emp.name, action, time, reason, markedBy,
      at: new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
      id: Date.now()
    }, ...prev]);
  };

  const markerName = currentUser?.name || "Manager";

  return (
    <div style={{ padding: "28px 32px", display: "flex", flexDirection: "column", gap: 20 }}>

      {proxyEmp && (
        <ProxyAttendanceModal
          emp={proxyEmp}
          markedBy={markerName}
          onClose={() => setProxyEmp(null)}
          onConfirm={handleProxyConfirm}
        />
      )}

      {overrideEmp && (
        <AttendanceOverrideModal
          emp={overrideEmp}
          onClose={() => setOverrideEmp(null)}
          onSuccess={(newStatus) => {
            // Update the employee list and detail panel with new status
            setEmpList(prev => prev.map(e =>
              e.id === overrideEmp.id
                ? { ...e, status: newStatus, checkIn: newStatus === "absent" ? null : e.checkIn, checkOut: null }
                : e
            ));
            if (selectedEmp?.id === overrideEmp.id) {
              setSelectedEmp(prev => ({
                ...prev, status: newStatus,
                checkIn: newStatus === "absent" ? null : prev.checkIn, checkOut: null,
              }));
            }
            setOverrideEmp(null);
          }}
        />
      )}

      {showAddModal && (
        <AddEmployeeModal
          office={office}
          tenantId={currentUser?.tenant_id || currentUser?.tenant?.tenant_id || ""}
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddEmployee}
        />
      )}

      {editEmp && (
        <EditEmployeeModal
          emp={editEmp}
          office={office}
          onClose={() => setEditEmp(null)}
          onSave={handleSaveEdit}
        />
      )}

      {/* Deactivate confirmation */}
      {deactivateEmp && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.8)",
          zIndex:3000, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}
          onClick={() => setDeactivateEmp(null)}>
          <div style={{ background:C.surface, border:"1px solid rgba(239,68,68,0.3)",
            borderRadius:16, padding:28, maxWidth:400, width:"100%" }}
            onClick={e => e.stopPropagation()}>
            <p style={{ fontFamily:"'DM Mono',monospace", color:C.text,
              fontSize:15, margin:"0 0 10px" }}>Deactivate Employee</p>
            <p style={{ fontFamily:"'Sora',sans-serif", color:C.textMuted,
              fontSize:13, margin:"0 0 22px" }}>
              Are you sure you want to deactivate <strong style={{ color:C.text }}>
              {deactivateEmp.name}</strong>? They will no longer be able to log in.
              This can be reversed from the database.
            </p>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={() => setDeactivateEmp(null)}
                style={{ flex:1, background:"transparent",
                  border:`1px solid ${C.border}`, borderRadius:8, padding:"10px",
                  color:C.textMuted, fontFamily:"'DM Mono',monospace",
                  fontSize:12, cursor:"pointer" }}>
                Cancel
              </button>
              <button onClick={handleDeactivate} disabled={deactivating}
                style={{ flex:1, background:"rgba(239,68,68,0.15)",
                  border:"1px solid rgba(239,68,68,0.4)", borderRadius:8, padding:"10px",
                  color:C.red, fontFamily:"'DM Mono',monospace", fontSize:12,
                  fontWeight:700, cursor: deactivating ? "not-allowed" : "pointer" }}>
                {deactivating ? "Deactivating…" : "✕ Yes, Deactivate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 18, margin: 0 }}>{office.name}</h2>
          <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 12, margin: "3px 0 0" }}>📍 {office.address} · Geofence: {office.radius}m</p>
        </div>
        <button onClick={onBack} style={{ background: "rgba(255,255,255,0.07)", border: `1px solid rgba(255,255,255,0.22)`, borderRadius: 8, padding: "8px 16px", color: C.text, fontFamily: "'DM Mono', monospace", fontSize: 12, cursor: "pointer", fontWeight: 500 }}>← Back</button>
        <button onClick={() => setShowAddModal(true)}
          style={{ background: `linear-gradient(135deg,#fbbf24,#d97706)`,
            border: "none", borderRadius: 8, padding: "9px 18px",
            color: "#1a0e00", fontFamily: "'DM Mono', monospace", fontSize: 12,
            fontWeight: 700, cursor: "pointer",
            boxShadow: "0 4px 14px rgba(251,191,36,0.3)",
            display: "flex", alignItems: "center", gap: 6 }}>
          + Add Employee
        </button>
        {proxyLog.length > 0 && (
          <div style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: 8, padding: "6px 14px", display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ color: C.amber, fontSize: 13 }}>✎</span>
            <span style={{ fontFamily: "'DM Mono', monospace", color: C.amber, fontSize: 11 }}>{proxyLog.length} proxy mark{proxyLog.length > 1 ? "s" : ""} this session</span>
          </div>
        )}
      </div>

      {/* Office stats — 6 boxes in one row */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {[
          { label: "Total",    value: officeStats.totalEmployees, color: C.gold     },
          { label: "Present",  value: officeStats.present,        color: C.green    },
          { label: "Late",     value: officeStats.late,           color: C.amber    },
          { label: "Leave",    value: officeStats.onLeave,        color: C.blue     },
          { label: "Absent",   value: officeStats.absent,         color: C.red      },
          { label: "Week Off", value: officeStats.weekOff,        color: "#2dd4bf"  },
          { label: "Rate",     value: `${rate}%`,                 color: C.gold     },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ flex: "1 1 auto", minWidth: 70,
            background: "rgba(255,255,255,0.02)", border: `1px solid ${C.border}`,
            borderRadius: 10, padding: "10px 8px", textAlign: "center" }}>
            <p style={{ fontFamily: "'DM Mono',monospace", color, fontSize: 20,
              fontWeight: 700, margin: "0 0 3px" }}>{value}</p>
            <p style={{ fontFamily: "'Sora',sans-serif", color: C.textMuted,
              fontSize: 9, textTransform: "uppercase", letterSpacing: 1, margin: 0 }}>{label}</p>
          </div>
        ))}
      </div>

      {/* Session proxy audit log */}
      {proxyLog.length > 0 && (
        <div style={{ background: "rgba(245,158,11,0.04)", border: "1px solid rgba(245,158,11,0.15)", borderRadius: 14, padding: "14px 18px" }}>
          <p style={{ fontFamily: "'DM Mono', monospace", color: C.amber, fontSize: 11, margin: "0 0 10px", letterSpacing: 0.5 }}>✎ PROXY ATTENDANCE LOG — THIS SESSION</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {proxyLog.map(entry => (
              <div key={entry.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 12px", background: "rgba(255,255,255,0.02)", borderRadius: 8 }}>
                <span style={{ fontFamily: "'DM Mono', monospace", color: entry.action === "checkin" ? C.green : C.red, fontSize: 11, minWidth: 80 }}>
                  {entry.action === "checkin" ? "CHECK IN" : "CHECK OUT"}
                </span>
                <span style={{ fontFamily: "'Sora', sans-serif", color: C.text, fontSize: 12, flex: 1 }}>{entry.emp}</span>
                <span style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 11 }}>{entry.time}</span>
                <span style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 11, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{entry.reason}</span>
                <span style={{ fontFamily: "'DM Mono', monospace", color: C.textMuted, fontSize: 10 }}>by {entry.markedBy} at {entry.at}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 20 }}>
        {/* Employee list */}
        <div style={{ flex: 1 }}>
          {/* Filters */}
          <div style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "center" }}>
            <input placeholder="Search name or department…" value={search} onChange={e => setSearch(e.target.value)}
              style={{ flex: 1, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 12px", color: C.text, fontFamily: "'Sora', sans-serif", fontSize: 13, outline: "none" }}
            />
            <div style={{ display: "flex", gap: 6 }}>
              {["all","present","late","absent","leave"].map(s => (
                <button key={s} onClick={() => setFilterStatus(s)} style={{
                  background: filterStatus === s ? C.goldGlow : "transparent",
                  border: `1px solid ${filterStatus === s ? C.borderStrong : C.border}`,
                  borderRadius: 6, padding: "6px 12px",
                  color: filterStatus === s ? C.gold : C.textMuted,
                  fontFamily: "'DM Mono', monospace", fontSize: 10, cursor: "pointer",
                  textTransform: "capitalize", letterSpacing: 0.5
                }}>{s}</button>
              ))}
            </div>
          </div>

          {/* Table header */}
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1.2fr 1fr 1fr 1fr 0.8fr 36px", gap: 8, padding: "8px 14px", marginBottom: 6 }}>
            {["Employee","Department","Check In","Check Out","Hours","Status",""].map(h => (
              <span key={h} style={{ fontFamily: "'DM Mono', monospace", color: C.textMuted, fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }}>{h}</span>
            ))}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {filtered.map(emp => (
              <Card key={emp.id} onClick={() => setSelectedEmp(emp)}
                style={{ padding: "12px 14px", borderColor: selectedEmp?.id === emp.id ? C.borderStrong : C.border }}>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1.2fr 1fr 1fr 1fr 0.8fr 36px", gap: 8, alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Avatar initials={emp.avatar} size={30} color={emp.status === "present" ? C.green : emp.status === "late" ? C.amber : C.textMuted} />
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <p style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 12, margin: 0 }}>{emp.name}</p>
                        {emp.role_type === "store_manager" && (
                          <span style={{ fontFamily: "'DM Mono', monospace", color: C.blue, fontSize: 9,
                            background: "rgba(96,165,250,0.1)", border: "1px solid rgba(96,165,250,0.25)",
                            borderRadius: 4, padding: "1px 5px", whiteSpace: "nowrap" }}>◉ MGR</span>
                        )}
                        {emp.role_type === "hr_admin" && (
                          <span style={{ fontFamily: "'DM Mono', monospace", color: C.gold, fontSize: 9,
                            background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.25)",
                            borderRadius: 4, padding: "1px 5px", whiteSpace: "nowrap" }}>◈ HR</span>
                        )}
                      </div>
                      <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 10, margin: "1px 0 0" }}>{emp.role}</p>
                    </div>
                  </div>
                  <span style={{ fontFamily: "'Sora', sans-serif", color: C.textDim, fontSize: 12 }}>{emp.dept}</span>
                  <span style={{ fontFamily: "'DM Mono', monospace", color: emp.checkIn ? C.green : C.textMuted, fontSize: 12 }}>{emp.checkIn || "—"}</span>
                  <span style={{ fontFamily: "'DM Mono', monospace", color: emp.checkOut ? C.text : C.textMuted, fontSize: 12 }}>{emp.checkOut || "—"}</span>
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12,
                    display:"flex", flexDirection:"column", gap:1, alignItems:"flex-start" }}>
                    <span style={{ color: emp.workingHrs !== "0h" ? C.gold : C.textMuted }}>{emp.workingHrs}</span>
                    {emp.lateGraceMins === 0 && emp.checkOut && emp.earlyExitMins > 0 && (
                      <span style={{ color:"#f59e0b", fontSize:10 }}>⚠ -{fmtMins(emp.earlyExitMins)}</span>
                    )}
                    {emp.lateGraceMins === 0 && emp.checkOut && emp.earlyExitMins === 0 && emp.workingHrs !== "0h" && (
                      <span style={{ color: C.green, fontSize:10 }}>✓</span>
                    )}
                  </span>
                  <StatusPill status={emp.status} />
                  {/* Quick proxy button — visible on hover concept, always shown for absent/no-checkin */}
                  <button
                    onClick={e => { e.stopPropagation(); if (emp.status !== "week_off" && !(emp.checkIn && emp.checkOut)) setProxyEmp(emp); }}
                    title="Mark attendance on behalf"
                    style={{
                      background: emp.status !== "week_off" && !(emp.checkIn && emp.checkOut) ? "rgba(245,158,11,0.12)" : "rgba(255,255,255,0.04)",
                      border: `1px solid ${emp.status !== "week_off" && !(emp.checkIn && emp.checkOut) ? "rgba(245,158,11,0.3)" : C.border}`,
                      borderRadius: 6, width: 28, height: 28, cursor: "pointer",
                      color: emp.status === "absent" || !emp.checkIn ? C.amber : C.textMuted,
                      fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0
                    }}>✎</button>
                </div>
                {(emp.status === "late" || emp.leaveBalanceSummary) && (
                  <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px solid ${C.border}`,
                    display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                    {emp.status === "late" && (
                      <span style={{ fontFamily: "'DM Mono', monospace", color: C.amber, fontSize: 10 }}>
                        ⚠ Late by {emp.lateBy} minutes
                      </span>
                    )}
                    {emp.leaveBalanceSummary && (
                      <span style={{ fontFamily: "'DM Mono', monospace", color: C.textMuted, fontSize: 10 }}>
                        🗓 {emp.leaveBalanceSummary}
                      </span>
                    )}
                  </div>
                )}
              </Card>
            ))}
            {filtered.length === 0 && (
              <div style={{ textAlign: "center", padding: "40px", color: C.textMuted, fontFamily: "'Sora', sans-serif", fontSize: 13 }}>No employees match your filter</div>
            )}
          </div>
        </div>

        {/* Employee detail panel */}
        {selectedEmp && (
          <div style={{ width: 268, flexShrink: 0 }}>
            <Card style={{ padding: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                <p style={{ fontFamily: "'DM Mono', monospace", color: C.textMuted, fontSize: 10, textTransform: "uppercase", letterSpacing: 1, margin: 0 }}>Employee Detail</p>
                <button onClick={() => setSelectedEmp(null)} style={{ background: "none", border: "none", color: C.textMuted, cursor: "pointer", fontSize: 16, padding: 0 }}>✕</button>
              </div>

              <div style={{ textAlign: "center", marginBottom: 16 }}>
                <Avatar initials={selectedEmp.avatar} size={48} color={selectedEmp.status === "present" ? C.green : selectedEmp.status === "late" ? C.amber : C.textDim} />
                <p style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 14, margin: "10px 0 2px" }}>{selectedEmp.name}</p>
                <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 11, margin: "0 0 8px" }}>{selectedEmp.role}</p>
                <StatusPill status={selectedEmp.status} />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
                {[
                  { label: "Department",    val: selectedEmp.dept },
                  { label: "Email",         val: selectedEmp.email },
                  { label: "Shift",         val: selectedEmp.shift },
                  { label: "Check In",      val: selectedEmp.checkIn  || "Not checked in" },
                  { label: "Check Out",     val: selectedEmp.checkOut || "Still working" },
                  { label: "Working Hours", val: selectedEmp.lateGraceMins === 0 && selectedEmp.checkOut
                      ? `${selectedEmp.workingHrs}${selectedEmp.earlyExitMins > 0 ? `  ⚠ Short ${fmtMins(selectedEmp.earlyExitMins)}` : selectedEmp.workingHrs !== "0h" ? "  ✓" : ""}`
                      : selectedEmp.workingHrs },
                  { label: "Leave Balance", val: selectedEmpLeave.length > 0 ? selectedEmpLeave.map(b => `${b.short}:${b.remaining_days}`).join(" ") : "Loading…" },
                ].map(({ label, val }) => (
                  <div key={label} style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: 7 }}>
                    <p style={{ fontFamily: "'DM Mono', monospace", color: C.textMuted, fontSize: 9, textTransform: "uppercase", letterSpacing: 1.2, margin: "0 0 2px" }}>{label}</p>
                    <p style={{ fontFamily: "'Sora', sans-serif", color: C.text, fontSize: 12, margin: 0 }}>{val}</p>
                  </div>
                ))}
              </div>

              {selectedEmp.status === "late" && (
                <div style={{ marginBottom: 12, background: "#1c0e00", border: "1px solid rgba(245,158,11,0.2)", borderRadius: 8, padding: "10px 12px" }}>
                  <p style={{ fontFamily: "'DM Mono', monospace", color: C.amber, fontSize: 11, margin: "0 0 2px" }}>⚠ Late Arrival</p>
                  <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 11, margin: 0 }}>Arrived {selectedEmp.lateBy} min after shift start</p>
                </div>
              )}

              {/* Proxy attendance — primary action */}
              <button
                  onClick={() => {
                    if (selectedEmp.status === "week_off" || (selectedEmp.checkIn && selectedEmp.checkOut)) return;
                    setProxyEmp(selectedEmp);
                  }}
                  disabled={selectedEmp.status === "week_off" || !!(selectedEmp.checkIn && selectedEmp.checkOut)}
                  style={{
                width: "100%", padding: "11px", borderRadius: 10, marginBottom: 8,
                background: "linear-gradient(135deg, rgba(245,158,11,0.15), rgba(217,119,6,0.1))",
                border: "1px solid rgba(245,158,11,0.35)",
                color: C.amber, fontFamily: "'DM Mono', monospace", fontSize: 11,
                fontWeight: 700, cursor: "pointer", letterSpacing: 0.5,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6
              }}>
                ✎ Mark Attendance on Behalf
              </button>

              {/* Override Status — shown for any status that has a record today */}
              {(() => {
                const hasRecord  = selectedEmp.checkIn || ["present","late","leave","week_off","absent"].includes(selectedEmp.status);
                if (!hasRecord) return null;

                const isExempt  = overrideConfig?.is_hr_exempt;
                const cutoff    = overrideConfig?.cutoff_time; // "12:00" or null
                // Calculate window_open from current time — never goes stale even if
                // the config was fetched before cutoff time passed
                let windowOpen = true;
                if (cutoff && !isExempt) {
                  const [h, m] = cutoff.split(":").map(Number);
                  const now    = new Date();
                  windowOpen   = now.getHours() < h || (now.getHours() === h && now.getMinutes() < m);
                }
                const canClick  = windowOpen || !!isExempt;
                const cutoffStr = cutoff
                  ? (() => {
                      const [h, m] = cutoff.split(":");
                      const d = new Date(); d.setHours(+h, +m, 0);
                      return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
                    })()
                  : null;

                return (
                  <div style={{ marginBottom: 8 }}>
                    <button
                      onClick={() => { if (canClick) setOverrideEmp(selectedEmp); }}
                      disabled={!canClick}
                      title={!canClick && cutoffStr ? `Override window closed at ${cutoffStr}` : "Override attendance status"}
                      style={{
                        width: "100%", padding: "11px", borderRadius: 10,
                        background: canClick ? "rgba(239,68,68,0.08)" : "rgba(255,255,255,0.03)",
                        border: `1px solid ${canClick ? "rgba(239,68,68,0.25)" : C.border}`,
                        color: canClick ? C.red : C.textMuted,
                        fontFamily: "'DM Mono', monospace", fontSize: 11,
                        fontWeight: 700,
                        cursor: canClick ? "pointer" : "not-allowed",
                        opacity: canClick ? 1 : 0.6,
                        letterSpacing: 0.5,
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                      }}>
                      ⚠ Override Status
                    </button>
                    {!canClick && cutoffStr && (
                      <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted,
                        fontSize: 10, textAlign: "center", margin: "4px 0 0" }}>
                        Window closed at {cutoffStr}
                      </p>
                    )}
                  </div>
                );
              })()}

              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                <button onClick={() => setHistoryEmp(selectedEmp)} style={{ width: "100%", background: C.goldGlow, border: `1px solid ${C.borderStrong}`, borderRadius: 8, padding: "9px", color: C.gold, fontFamily: "'DM Mono', monospace", fontSize: 11, cursor: "pointer" }}>
                  📋 View Full History
                </button>
                {/* Edit employee */}
                {isHR && (
                  <button
                    onClick={() => setEditEmp(selectedEmp)}
                    style={{ width:"100%", background:"rgba(96,165,250,0.08)",
                      border:"1px solid rgba(96,165,250,0.25)", borderRadius:8,
                      padding:"9px", color:C.blue,
                      fontFamily:"'DM Mono',monospace", fontSize:11,
                      cursor:"pointer", display:"flex", alignItems:"center",
                      justifyContent:"center", gap:6 }}>
                    ✎ Edit Employee Info
                  </button>
                )}
                {/* Deactivate — only HR admin */}
                {isHR && (
                  <button
                    onClick={() => setDeactivateEmp(selectedEmp)}
                    style={{ width:"100%", background:"rgba(239,68,68,0.06)",
                      border:"1px solid rgba(239,68,68,0.2)", borderRadius:8,
                      padding:"9px", color:C.red,
                      fontFamily:"'DM Mono',monospace", fontSize:11,
                      cursor:"pointer", display:"flex", alignItems:"center",
                      justifyContent:"center", gap:6 }}>
                    ✕ Deactivate Employee
                  </button>
                )}
              </div>
              {historyEmp && <EmployeeHistoryModal emp={historyEmp} onClose={() => setHistoryEmp(null)} />}
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

// ── LEAVE REQUESTS ──

function LeavesScreen() {
  const [leaves,     setLeaves]     = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);  // non-FL types for distribution
  const [loading,    setLoading]    = useState(true);
  const [filter,     setFilter]     = useState("pending");

  // ── Reject modal ────────────────────────────────────────────────────────────
  const [rejecting,  setRejecting]  = useState(null);  // application id
  const [rejectNote, setRejectNote] = useState("");

  // ── Distribution modal ──────────────────────────────────────────────────────
  const [distributing, setDistributing] = useState(null);  // leave object
  const [distInputs,   setDistInputs]   = useState({});    // { [lt_id]: days, week_off, absent }
  const [distError,    setDistError]    = useState("");
  const [distLoading,  setDistLoading]  = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [apps, types] = await Promise.all([
        api.getPendingLeaves().catch(() => []),
        api.getLeaveTypesForEmployee().catch(() => []),
      ]);
      setLeaves(apps || []);
      // Only non-FL types are valid distribution targets
      setLeaveTypes((types || []).filter(t => !t.is_flexible));
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { loadData(); }, []); // eslint-disable-line

  // ── Approve (regular leave) ─────────────────────────────────────────────────
  const handleApprove = async (id) => {
    try {
      await api.approveLeave(id);
      setLeaves(prev => prev.map(l => l.id === id ? { ...l, status: "approved" } : l));
    } catch(err) { alert(err.detail || "Failed to approve"); }
  };

  // ── Reject flow ─────────────────────────────────────────────────────────────
  const openReject = (id) => { setRejecting(id); setRejectNote(""); };
  const confirmReject = async () => {
    try {
      await api.rejectLeave(rejecting, rejectNote);
      setLeaves(prev => prev.map(l => l.id === rejecting ? { ...l, status: "rejected" } : l));
      setRejecting(null);
    } catch(err) { alert(err.detail || "Failed to reject"); }
  };

  // ── Distribution flow ────────────────────────────────────────────────────────
  const openDistribute = (leave) => {
    const inputs = {};
    leaveTypes.forEach(t => { inputs[t.id] = 0; });
    inputs.week_off = 0;
    inputs.absent   = 0;
    setDistInputs(inputs);
    setDistError("");
    setDistributing(leave);
  };

  const totalDistributed = distributing
    ? leaveTypes.reduce((s, t) => s + (parseInt(distInputs[t.id]) || 0), 0)
      + (parseInt(distInputs.week_off) || 0)
      + (parseInt(distInputs.absent)   || 0)
    : 0;

  const confirmDistribute = async () => {
    if (!distributing) return;
    const total = parseInt(distributing.total_days);
    if (totalDistributed !== total) {
      setDistError(`Total must equal ${total}d. Currently ${totalDistributed}d.`);
      return;
    }
    setDistLoading(true);
    setDistError("");
    try {
      const items = leaveTypes
        .filter(t => (parseInt(distInputs[t.id]) || 0) > 0)
        .map(t  => ({ leave_type_id: t.id, days: parseInt(distInputs[t.id]) }));
      await api.distributeFlexibleLeave(distributing.id, {
        items,
        week_off_days: parseInt(distInputs.week_off) || 0,
        absent_days:   parseInt(distInputs.absent)   || 0,
      });
      setLeaves(prev => prev.map(l => l.id === distributing.id ? { ...l, status: "approved" } : l));
      setDistributing(null);
    } catch(err) {
      setDistError(err.detail || "Distribution failed");
    } finally {
      setDistLoading(false);
    }
  };

  const displayed = filter === "all" ? leaves : leaves.filter(l => l.status === filter);
  const pendingCount = leaves.filter(l => l.status === "pending").length;

  const inputNumSt = (accent) => ({
    width: 76, background: "rgba(255,255,255,0.04)",
    border: `1px solid ${accent || C.border}`,
    borderRadius: 8, padding: "8px 10px", color: accent || C.text,
    fontFamily: "'DM Mono', monospace", fontSize: 14,
    textAlign: "center", outline: "none",
  });

  return (
    <div style={{ padding: "28px 32px" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h2 style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 18, margin: 0 }}>Leave Requests</h2>
          <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 12, margin: "4px 0 0" }}>
            {pendingCount} pending approval
          </p>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {["pending","approved","rejected","all"].map(s => (
            <button key={s} onClick={() => setFilter(s)} style={{
              background: filter === s ? C.goldGlow : "transparent",
              border: `1px solid ${filter === s ? C.borderStrong : C.border}`,
              borderRadius: 6, padding: "6px 14px",
              color: filter === s ? C.gold : C.textMuted,
              fontFamily: "'DM Mono', monospace", fontSize: 11,
              cursor: "pointer", textTransform: "capitalize",
            }}>{s}</button>
          ))}
        </div>
      </div>

      {loading && (
        <p style={{ color: C.textMuted, fontFamily: "'Sora', sans-serif", fontSize: 13 }}>Loading…</p>
      )}

      {/* Leave list */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {displayed.map(leave => {
          const initials = (leave.employee_name || leave.employee || "?")
            .split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
          const isFL     = leave.is_flexible;
          const isPending = leave.status === "pending";
          const typeName = leave.leave_type_name || leave.type || "Leave";
          const officeName = leave.employee_office || leave.office || "—";
          const empName = leave.employee_name || leave.employee || "—";
          const fromD   = leave.from_date || leave.from || "";
          const toD     = leave.to_date   || leave.to   || "";
          const days    = leave.total_days || leave.days || 0;
          const applied = (leave.applied_at || "").slice(0, 10);

          return (
            <Card key={leave.id} style={{ padding: "18px 20px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                <Avatar initials={initials} size={40} color={C.blue} />
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
                    <p style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 14, margin: 0 }}>{empName}</p>
                    <span style={{ color: C.textMuted, fontSize: 11 }}>·</span>
                    <span style={{ color: C.textMuted, fontSize: 11 }}>{officeName}</span>
                    <span style={{
                      background: isFL ? "rgba(139,92,246,0.12)" : "#0c1a2e",
                      color: isFL ? "#a78bfa" : C.blue,
                      border: `1px solid ${isFL ? "rgba(139,92,246,0.3)" : "rgba(96,165,250,0.25)"}`,
                      borderRadius: 6, padding: "2px 8px",
                      fontFamily: "'DM Mono', monospace", fontSize: 10,
                    }}>{typeName}{isFL ? " 🔀" : ""}</span>
                  </div>
                  <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                    <span style={{ color: C.textDim, fontSize: 12 }}>
                      📅 {fromD} → {toD} <span style={{ color: C.gold }}>({days}d)</span>
                    </span>
                    {applied && (
                      <span style={{ color: C.textMuted, fontSize: 12 }}>Applied: {applied}</span>
                    )}
                  </div>
                  {leave.reason && (
                    <p style={{ color: C.textMuted, fontSize: 12, margin: "6px 0 0" }}>"{leave.reason}"</p>
                  )}
                </div>

                {/* Action buttons */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                  {isPending ? (
                    isFL ? (
                      <button onClick={() => openDistribute(leave)} style={{
                        background: "rgba(139,92,246,0.12)",
                        border: "1px solid rgba(139,92,246,0.4)",
                        borderRadius: 8, padding: "8px 18px", color: "#a78bfa",
                        fontFamily: "'DM Mono', monospace", fontSize: 12, cursor: "pointer",
                      }}>⊕ Distribute</button>
                    ) : (
                      <>
                        <button onClick={() => handleApprove(leave.id)} style={{
                          background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.3)",
                          borderRadius: 8, padding: "8px 18px", color: C.green,
                          fontFamily: "'DM Mono', monospace", fontSize: 12, cursor: "pointer",
                        }}>✓ Approve</button>
                        <button onClick={() => openReject(leave.id)} style={{
                          background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)",
                          borderRadius: 8, padding: "8px 18px", color: C.red,
                          fontFamily: "'DM Mono', monospace", fontSize: 12, cursor: "pointer",
                        }}>✕ Reject</button>
                      </>
                    )
                  ) : (
                    <span style={{
                      fontFamily: "'DM Mono', monospace", fontSize: 11,
                      color: leave.status === "approved" ? C.green : C.red,
                      background: leave.status === "approved" ? "rgba(34,197,94,0.08)" : "rgba(239,68,68,0.08)",
                      border: `1px solid ${leave.status === "approved" ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.2)"}`,
                      borderRadius: 8, padding: "8px 14px",
                    }}>{leave.status === "approved" ? "✓ Approved" : "✕ Rejected"}</span>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
        {!loading && displayed.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px", color: C.textMuted,
            fontFamily: "'Sora', sans-serif", fontSize: 13 }}>
            No {filter} leave requests
          </div>
        )}
      </div>

      {/* ── Reject modal ──────────────────────────────────────────────────────── */}
      {rejecting && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 1000,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: C.surface, border: `1px solid ${C.borderStrong}`,
            borderRadius: 14, padding: "28px 32px", width: 380, maxWidth: "90vw" }}>
            <h3 style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 15, margin: "0 0 16px" }}>
              Reject Leave
            </h3>
            <label style={{ fontFamily: "'DM Mono', monospace", color: C.textMuted, fontSize: 10,
              letterSpacing: 1.2, textTransform: "uppercase", display: "block", marginBottom: 6 }}>
              Reason (optional)
            </label>
            <textarea value={rejectNote} onChange={e => setRejectNote(e.target.value)}
              rows={3} placeholder="Reason for rejection…"
              style={{ width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.03)",
                border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px",
                color: C.text, fontFamily: "'Sora', sans-serif", fontSize: 13,
                resize: "vertical", outline: "none" }} />
            <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
              <button onClick={confirmReject} style={{ flex: 1, background: "rgba(239,68,68,0.12)",
                border: "1px solid rgba(239,68,68,0.3)", borderRadius: 8, padding: "10px",
                color: C.red, fontFamily: "'DM Mono', monospace", fontSize: 13, cursor: "pointer" }}>
                Confirm Reject
              </button>
              <button onClick={() => setRejecting(null)} style={{ flex: 1, background: "transparent",
                border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px",
                color: C.textMuted, fontFamily: "'DM Mono', monospace", fontSize: 13, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Distribution modal ────────────────────────────────────────────────── */}
      {distributing && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", zIndex: 1000,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: C.surface, border: "1px solid rgba(139,92,246,0.4)",
            borderRadius: 16, padding: "28px 32px", width: 500, maxWidth: "95vw",
            maxHeight: "88vh", overflowY: "auto" }}>

            {/* Modal header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
              <div>
                <h3 style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 16, margin: "0 0 5px" }}>
                  Distribute Flexible Leave
                </h3>
                <p style={{ color: C.textMuted, fontSize: 12, margin: 0 }}>
                  {distributing.employee_name || distributing.employee} &nbsp;·&nbsp;
                  {distributing.from_date || distributing.from} → {distributing.to_date || distributing.to}
                </p>
              </div>
              <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 26, fontWeight: 700,
                color: "#a78bfa", lineHeight: 1 }}>
                {distributing.total_days || distributing.days}d
              </span>
            </div>

            <p style={{ color: C.textMuted, fontSize: 12, margin: "0 0 20px", lineHeight: 1.6 }}>
              Distribute <strong style={{ color: C.text }}>{distributing.total_days || distributing.days} days</strong> across
              leave types below. Total must match exactly before you can confirm.
            </p>

            {/* Leave type rows */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {leaveTypes.map(t => (
                <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ flex: 1, fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 13 }}>
                    {t.name}
                  </span>
                  <input type="number" min={0} max={distributing.total_days || distributing.days}
                    value={distInputs[t.id] || 0}
                    onChange={e => setDistInputs(p => ({ ...p, [t.id]: parseInt(e.target.value) || 0 }))}
                    style={inputNumSt()} />
                  <span style={{ color: C.textMuted, fontSize: 11, width: 18 }}>d</span>
                </div>
              ))}
              {/* Week-off row */}
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ flex: 1, fontFamily: "'DM Mono', monospace", color: "#a78bfa", fontSize: 13 }}>
                  Week Off
                </span>
                <input type="number" min={0} max={distributing.total_days || distributing.days}
                  value={distInputs.week_off || 0}
                  onChange={e => setDistInputs(p => ({ ...p, week_off: parseInt(e.target.value) || 0 }))}
                  style={inputNumSt("rgba(139,92,246,0.8)")} />
                <span style={{ color: C.textMuted, fontSize: 11, width: 18 }}>d</span>
              </div>
              {/* Absent row */}
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ flex: 1, fontFamily: "'DM Mono', monospace", color: C.red, fontSize: 13 }}>
                  Absent (no leave)
                </span>
                <input type="number" min={0} max={distributing.total_days || distributing.days}
                  value={distInputs.absent || 0}
                  onChange={e => setDistInputs(p => ({ ...p, absent: parseInt(e.target.value) || 0 }))}
                  style={inputNumSt("rgba(239,68,68,0.7)")} />
                <span style={{ color: C.textMuted, fontSize: 11, width: 18 }}>d</span>
              </div>
            </div>

            {/* Progress bar */}
            {(() => {
              const total = parseInt(distributing.total_days || distributing.days);
              const exact = totalDistributed === total;
              return (
                <div style={{ marginTop: 18, padding: "12px 14px", background: "rgba(255,255,255,0.03)",
                  borderRadius: 10, border: `1px solid ${exact ? "rgba(34,197,94,0.3)" : C.border}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <span style={{ fontFamily: "'DM Mono', monospace", color: C.textMuted, fontSize: 11 }}>Distributed</span>
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, fontWeight: 700,
                      color: exact ? C.green : C.amber }}>
                      {totalDistributed} / {total} d
                    </span>
                  </div>
                  <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: 4, height: 6, overflow: "hidden" }}>
                    <div style={{ height: "100%",
                      width: `${Math.min(100, (totalDistributed / total) * 100)}%`,
                      background: exact ? C.green : C.amber,
                      borderRadius: 4, transition: "width 0.2s" }} />
                  </div>
                </div>
              );
            })()}

            {distError && (
              <div style={{ marginTop: 12, background: "rgba(239,68,68,0.1)",
                border: "1px solid rgba(239,68,68,0.3)", borderRadius: 8,
                padding: "10px 12px", color: C.red, fontSize: 12,
                fontFamily: "'Sora', sans-serif" }}>
                {distError}
              </div>
            )}

            {/* Modal actions */}
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              {(() => {
                const total = parseInt(distributing.total_days || distributing.days);
                const exact = totalDistributed === total;
                return (
                  <button onClick={confirmDistribute}
                    disabled={distLoading || !exact}
                    style={{ flex: 1, padding: "12px",
                      background: exact ? "rgba(139,92,246,0.18)" : "rgba(255,255,255,0.04)",
                      border: `1px solid ${exact ? "rgba(139,92,246,0.5)" : C.border}`,
                      borderRadius: 10,
                      color: exact ? "#a78bfa" : C.textMuted,
                      fontFamily: "'DM Mono', monospace", fontSize: 13,
                      cursor: exact && !distLoading ? "pointer" : "not-allowed",
                      transition: "all 0.2s",
                    }}>
                    {distLoading ? "Distributing…" : "✓ Confirm Distribution"}
                  </button>
                );
              })()}
              <button onClick={() => setDistributing(null)} style={{ padding: "12px 22px",
                background: "transparent", border: `1px solid ${C.border}`,
                borderRadius: 10, color: C.textMuted,
                fontFamily: "'DM Mono', monospace", fontSize: 13, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── REPORTS ──

function EmployeeAttendanceReport({ MOCK_OFFICES, MOCK_EMPLOYEES }) {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const fmt = (d) => d.toISOString().split("T")[0];

  // All employees flat list with office label — scoped to this tenant's data
  const allEmployees = Object.entries(MOCK_EMPLOYEES).flatMap(([officeId, emps]) => {
    const office = MOCK_OFFICES.find(o => o.id === parseInt(officeId));
    return emps.map(e => ({ ...e, officeName: office ? office.name : "Unknown", officeId: parseInt(officeId) }));
  });

  const [selectedOfficeId, setSelectedOfficeId] = useState("all");
  const [selectedEmpId, setSelectedEmpId] = useState("all");
  const [fromDate, setFromDate] = useState(fmt(today));
  const [toDate, setToDate] = useState(fmt(today));
  const [statusFilter, setStatusFilter] = useState("all");
  const [hasSearched, setHasSearched] = useState(false);
  const [results, setResults] = useState([]);

  const empList = selectedOfficeId === "all"
    ? allEmployees
    : allEmployees.filter(e => e.officeId === parseInt(selectedOfficeId));

  const runReport = async () => {
    setHasSearched(true);
    setResults([]);
    try {
      const params = {
        from_date: fromDate,
        to_date:   toDate,
        ...(selectedEmpId   !== "all" ? { employee_id: selectedEmpId }                 : {}),
        ...(selectedOfficeId !== "all" ? { office_id: parseInt(selectedOfficeId) }      : {}),
        ...(statusFilter    !== "all" ? { status: statusFilter }                        : {}),
      };
      const hist = await api.getAttendanceReport(params);
      const STATUS_MAP = { present:"present", late:"late", absent:"absent", leave:"leave", half_day:"present", week_off:"week_off" };
      const rows = (hist||[]).map(r => {
        const emp = allEmployees.find(e => e.id === r.employee_id) || {};
        return {
          date:          new Date(r.date).toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"}),
          dateObj:       new Date(r.date),
          status:        STATUS_MAP[r.status] || r.status || "absent",
          checkIn:       r.check_in_time  ? new Date(r.check_in_time).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit",hour12:true,timeZone:"Asia/Kolkata"}) : null,
          checkOut:      r.check_out_time ? new Date(r.check_out_time).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit",hour12:true,timeZone:"Asia/Kolkata"}) : null,
          lateBy:        r.late_by_mins    || 0,
          earlyExitMins: r.early_exit_mins || 0,
          isJobHours:    (emp.lateGraceMins ?? 15) === 0,
          working_hours: r.working_hours   || 0,
          hrs:           fmtHours(r.working_hours),
          empName:       emp.name       || "Unknown",
          empAvatar:     emp.avatar     || "??",
          empRole:       emp.role       || "",
          empDept:       emp.dept       || "",
          officeName:    emp.officeName || "",
        };
      });
      rows.sort((a,b) => b.dateObj - a.dateObj || a.empName.localeCompare(b.empName));
      setResults(rows);
    } catch(e) { console.error(e); }
  };

  // summary from results
  const summary = results.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    if (r.lateBy) acc.totalLateMin += r.lateBy;
    return acc;
  }, { present: 0, late: 0, absent: 0, leave: 0, week_off: 0, totalLateMin: 0 });

  const totalDays = results.length;
  const attended = (summary.present || 0) + (summary.late || 0);
  const attendanceRate = totalDays ? Math.round((attended / totalDays) * 100) : 0;

  const inputStyle = { background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 12px", color: C.text, fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none", width: "100%", boxSizing: "border-box", colorScheme: "dark" };
  const selectStyle = { ...inputStyle, cursor: "pointer" };

  // Compute surplus/short mins vs 9h 45m (585 min) required — job-hours mode only
  const jobHrsDiff = (r) => {
    if (!r.isJobHours || !r.checkOut) return null;
    const workedMins = Math.round(parseFloat(r.working_hours || 0) * 60);
    return workedMins - 585; // positive = surplus, negative = short
  };

  // Helpers for CSV/Excel — two separate columns
  const exportHrs = (r) => {
    if (r.status === "week_off") return "Week Off";
    if (!r.checkIn)              return "—";
    return r.hrs;
  };
  const exportSurplusNote = (r) => {
    if (!r.isJobHours || !r.checkIn) return "";
    if (!r.checkOut)                  return "ongoing";
    const diff = jobHrsDiff(r);
    if (diff > 0)  return `+${fmtMins(diff)} surplus`;
    if (diff < 0)  return `-${fmtMins(Math.abs(diff))} short`;
    return "Exact";
  };

  // Helper: returns { hrs, note, hrsColor, noteColor } for two-line cell display
  const jobHrsCell = (r) => {
    if (r.status === "week_off") return { hrs: "Week Off", note: "", hrsColor: "#a78bfa", noteColor: "" };
    if (!r.checkIn)              return { hrs: "—",        note: "", hrsColor: C.textMuted, noteColor: "" };
    if (r.isJobHours) {
      if (!r.checkOut) return { hrs: r.hrs, note: "ongoing", hrsColor: C.gold, noteColor: C.textMuted };
      const diff = jobHrsDiff(r);
      if (diff > 0)  return { hrs: r.hrs, note: `+${fmtMins(diff)} surplus`,        hrsColor: C.gold,  noteColor: C.green };
      if (diff < 0)  return { hrs: r.hrs, note: `-${fmtMins(Math.abs(diff))} short`, hrsColor: C.amber, noteColor: C.amber };
      return           { hrs: r.hrs, note: "✓ Exact",                               hrsColor: C.gold,  noteColor: C.green };
    }
    // Late-tracking mode — no short/surplus shown
    return {
      hrs:       r.hrs,
      note:      r.lateBy > 0 ? `+${r.lateBy}m late` : "✓ On time",
      hrsColor:  C.gold,
      noteColor: r.lateBy > 0 ? C.amber : C.green,
    };
  };

  // CSV export
  const exportCSV = () => {
    const isAll = selectedEmpId === "all";
    const headers = isAll
      ? ["Employee","Department","Office","Date","Check In","Check Out","Working Hours","Surplus / Short","Status"]
      : ["Date","Check In","Check Out","Working Hours","Surplus / Short","Status"];
    const csvRows = results.map(r => isAll
      ? [r.empName, r.empDept, r.officeName, r.date, r.checkIn||"", r.checkOut||"", exportHrs(r), exportSurplusNote(r), r.status]
      : [r.date, r.checkIn||"", r.checkOut||"", exportHrs(r), exportSurplusNote(r), r.status]
    );
    const BOM = "﻿";
    const csv = [headers, ...csvRows].map(row =>
      row.map(v => `"${String(v).replace(/"/g,'""')}"`).join(",")
    ).join("\n");
    const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `attendance_${fromDate}_${toDate}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // Excel export (XML Spreadsheet — no library needed)
  const exportExcel = () => {
    const isAll = selectedEmpId === "all";
    const headers = isAll
      ? ["Employee","Department","Office","Date","Check In","Check Out","Working Hours","Surplus / Short","Status"]
      : ["Date","Check In","Check Out","Working Hours","Surplus / Short","Status"];
    const dataRows = results.map(r => isAll
      ? [r.empName, r.empDept, r.officeName, r.date, r.checkIn||"", r.checkOut||"", exportHrs(r), exportSurplusNote(r), r.status]
      : [r.date, r.checkIn||"", r.checkOut||"", exportHrs(r), exportSurplusNote(r), r.status]
    );
    const esc = (v) => String(v).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
    const toRow = (cells, bold) =>
      `<Row>${cells.map(c=>`<Cell><Data ss:Type="String">${bold?`<B>${esc(c)}</B>`:esc(c)}</Data></Cell>`).join("")}</Row>`;
    const xml = `<?xml version="1.0"?><Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"><Worksheet ss:Name="Attendance Report"><Table>${toRow(headers,true)}${dataRows.map(r=>toRow(r,false)).join("")}</Table></Worksheet></Workbook>`;
    const blob = new Blob([xml], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `attendance_${fromDate}_${toDate}.xls`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Filter panel */}
      <Card style={{ padding: 24 }}>
        <SectionHeader title="Employee Attendance Report" sub="Filter by office, employee, date range and status" />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr auto", gap: 12, alignItems: "end" }}>
          {/* Office */}
          <div>
            <label style={{ fontFamily: "'DM Mono', monospace", color: C.textMuted, fontSize: 10, letterSpacing: 1.2, textTransform: "uppercase", display: "block", marginBottom: 6 }}>Office</label>
            <select value={selectedOfficeId} onChange={e => { setSelectedOfficeId(e.target.value); setSelectedEmpId("all"); }} style={selectStyle}>
              <option value="all">All Offices</option>
              {MOCK_OFFICES.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>

          {/* Employee */}
          <div>
            <label style={{ fontFamily: "'DM Mono', monospace", color: C.textMuted, fontSize: 10, letterSpacing: 1.2, textTransform: "uppercase", display: "block", marginBottom: 6 }}>Employee</label>
            <select value={selectedEmpId} onChange={e => setSelectedEmpId(e.target.value)} style={selectStyle}>
              <option value="all">All Employees</option>
              {empList.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>

          {/* From */}
          <div>
            <label style={{ fontFamily: "'DM Mono', monospace", color: C.textMuted, fontSize: 10, letterSpacing: 1.2, textTransform: "uppercase", display: "block", marginBottom: 6 }}>From Date</label>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={inputStyle} />
          </div>

          {/* To */}
          <div>
            <label style={{ fontFamily: "'DM Mono', monospace", color: C.textMuted, fontSize: 10, letterSpacing: 1.2, textTransform: "uppercase", display: "block", marginBottom: 6 }}>To Date</label>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={inputStyle} />
          </div>

          {/* Status */}
          <div>
            <label style={{ fontFamily: "'DM Mono', monospace", color: C.textMuted, fontSize: 10, letterSpacing: 1.2, textTransform: "uppercase", display: "block", marginBottom: 6 }}>Status</label>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={selectStyle}>
              <option value="all">All Statuses</option>
              <option value="present">Present</option>
              <option value="late">Late</option>
              <option value="absent">Absent</option>
              <option value="leave">On Leave</option>
              <option value="week_off">Week Off</option>
            </select>
          </div>

          {/* Run button */}
          <button onClick={runReport} style={{
            background: `linear-gradient(135deg, ${C.gold}, ${C.goldDim})`,
            border: "none", borderRadius: 8, padding: "9px 22px",
            color: "#1a0e00", fontFamily: "'DM Mono', monospace", fontSize: 12,
            fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
            boxShadow: `0 4px 16px ${C.goldGlow}`
          }}>Run Report →</button>
        </div>

        {/* Quick presets */}
        <div style={{ display: "flex", gap: 8, marginTop: 14, alignItems: "center" }}>
          <span style={{ fontFamily: "'DM Mono', monospace", color: C.textMuted, fontSize: 10, letterSpacing: 1 }}>QUICK:</span>
          {[
            { label: "Today", from: fmt(today), to: fmt(today) },
            { label: "This Week", from: fmt(new Date(today.getTime() - 7*24*60*60*1000)), to: fmt(today) },
            { label: "This Month", from: fmt(firstOfMonth), to: fmt(today) },
            { label: "Last Month", from: fmt(new Date(today.getFullYear(), today.getMonth()-1, 1)), to: fmt(new Date(today.getFullYear(), today.getMonth(), 0)) },
          ].map(({ label, from, to }) => (
            <button key={label} onClick={() => { setFromDate(from); setToDate(to); }} style={{
              background: "transparent", border: `1px solid ${C.border}`,
              borderRadius: 6, padding: "4px 12px",
              color: C.textDim, fontFamily: "'DM Mono', monospace", fontSize: 10,
              cursor: "pointer", transition: "all 0.15s"
            }}>{label}</button>
          ))}
        </div>
      </Card>

      {/* Results */}
      {!hasSearched && (
        <div style={{ textAlign: "center", padding: "60px 0", color: C.textMuted, fontFamily: "'Sora', sans-serif", fontSize: 13 }}>
          <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.3 }}>📊</div>
          Select filters above and click <span style={{ color: C.gold, fontFamily: "'DM Mono', monospace" }}>Run Report</span> to view results
        </div>
      )}

      {hasSearched && (
        <>
          {/* Summary strip */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 12 }}>
            {[
              { label: "Total Records", val: totalDays,              color: C.textDim },
              { label: "Present",       val: summary.present || 0,   color: C.green },
              { label: "Late",          val: summary.late    || 0,   color: C.amber },
              { label: "Absent",        val: summary.absent  || 0,   color: C.red },
              { label: "On Leave",      val: summary.leave   || 0,   color: C.blue },
              { label: "Week Off",      val: summary.week_off|| 0,   color: "#2dd4bf" },
              { label: "Attendance Rate", val: `${attendanceRate}%`, color: attendanceRate >= 80 ? C.gold : attendanceRate >= 60 ? C.amber : C.red },
            ].map(({ label, val, color }) => (
              <Card key={label} style={{ padding: "14px 16px", textAlign: "center" }}>
                <p style={{ fontFamily: "'DM Mono', monospace", color, fontSize: 20, fontWeight: 700, margin: 0 }}>{val}</p>
                <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 10, margin: "4px 0 0", textTransform: "uppercase", letterSpacing: 1 }}>{label}</p>
              </Card>
            ))}
          </div>

          {/* Table */}
          <Card style={{ padding: 0, overflow: "hidden" }}>
            {/* Table toolbar */}
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <p style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 13, margin: 0 }}>
                {results.length} record{results.length !== 1 ? "s" : ""} found
              </p>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={exportCSV} style={{ background: C.goldGlow, border: `1px solid ${C.borderStrong}`, borderRadius: 8, padding: "7px 16px", color: C.gold, fontFamily: "'DM Mono', monospace", fontSize: 11, cursor: "pointer" }}>↓ CSV</button>
                <button onClick={exportExcel} style={{ background: "rgba(34,197,94,0.08)", border: `1px solid rgba(34,197,94,0.3)`, borderRadius: 8, padding: "7px 16px", color: "#4ade80", fontFamily: "'DM Mono', monospace", fontSize: 11, cursor: "pointer" }}>↓ Excel</button>
              </div>
            </div>

            {/* Table header */}
            <div style={{ display: "grid", gridTemplateColumns: selectedEmpId === "all" ? "1.6fr 1fr 1fr 1fr 1fr 1fr 1fr" : "1.4fr 1fr 1fr 1fr 1fr", gap: 8, padding: "10px 20px", background: C.bg, borderBottom: `1px solid ${C.border}` }}>
              {(selectedEmpId === "all" ? ["Employee", "Office", "Date", "Check In", "Check Out", "Working Hours", "Status"] : ["Date", "Check In", "Check Out", "Working Hours", "Status"]).map(h => (
                <span key={h} style={{ fontFamily: "'DM Mono', monospace", color: C.textMuted, fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }}>{h}</span>
              ))}
            </div>

            {/* Rows */}
            <div style={{ maxHeight: 420, overflowY: "auto" }}>
              {results.length === 0 && (
                <div style={{ textAlign: "center", padding: "48px", color: C.textMuted, fontFamily: "'Sora', sans-serif", fontSize: 13 }}>No records match your filters</div>
              )}
              {results.map((r, i) => {
                const { hrs, note, hrsColor, noteColor } = jobHrsCell(r);
                return (
                  <div key={i} style={{
                    display: "grid",
                    gridTemplateColumns: selectedEmpId === "all" ? "1.6fr 1fr 1fr 1fr 1fr 1fr 1fr" : "1.4fr 1fr 1fr 1fr 1fr",
                    gap: 8, padding: "11px 20px", alignItems: "center",
                    background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.012)",
                    borderBottom: `1px solid ${C.border}`,
                    transition: "background 0.15s"
                  }}>
                    {selectedEmpId === "all" && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Avatar initials={r.empAvatar} size={26} color={C.gold} />
                        <div>
                          <p style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 11, margin: 0 }}>{r.empName}</p>
                          <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 10, margin: 0 }}>{r.empDept}</p>
                        </div>
                      </div>
                    )}
                    {selectedEmpId === "all" && (
                      <span style={{ fontFamily: "'Sora', sans-serif", color: C.textDim, fontSize: 11 }}>{r.officeName.split("—")[0].trim()}</span>
                    )}
                    <span style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 11 }}>{r.date}</span>
                    <span style={{ fontFamily: "'DM Mono', monospace", color: r.checkIn ? C.text : C.textMuted, fontSize: 11 }}>{r.checkIn || "—"}</span>
                    <span style={{ fontFamily: "'DM Mono', monospace", color: r.checkOut ? C.text : C.textMuted, fontSize: 11 }}>{r.checkOut || "—"}</span>
                    <div>
                      <p style={{ fontFamily: "'DM Mono', monospace", color: hrsColor, fontSize: 11, margin: 0, fontWeight: 600 }}>{hrs}</p>
                      {note && <p style={{ fontFamily: "'Sora', sans-serif", color: noteColor, fontSize: 10, margin: "2px 0 0" }}>{note}</p>}
                    </div>
                    <StatusPill status={r.status} />
                  </div>
                );
              })}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function ReportsScreen({ MOCK_OFFICES, MOCK_EMPLOYEES, hr }) {
  const [activeTab, setActiveTab] = useState("employee"); // "employee" | "trends" | "late"

  // ── Weekly Trends state ────────────────────────────────────────────────────
  const [weekData,      setWeekData]      = useState([]);
  const [deptData,      setDeptData]      = useState([]);
  const [trendsLoading, setTrendsLoading] = useState(false);

  // ── Late Arrivals state ───────────────────────────────────────────────────
  const [lateList,      setLateList]      = useState([]);
  const [lateLoading,   setLateLoading]   = useState(false);

  const fmtDate = d => d.toISOString().split("T")[0];

  // Build a flat employee lookup map: { empId (string) -> { name, dept, avatar } }
  const empLookup = useMemo(() => {
    const map = {};
    Object.values(MOCK_EMPLOYEES).flat().forEach(e => {
      map[String(e.id)] = {
        name:   e.name   || "Unknown",
        dept:   e.dept   || "—",
        avatar: e.avatar || (e.name || "?").split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase(),
      };
    });
    return map;
  }, [MOCK_EMPLOYEES]);

  // Fetch weekly trends (last 7 days) when tab opens
  useEffect(() => {
    if (activeTab !== "trends") return;
    const load = async () => {
      setTrendsLoading(true);
      try {
        const today = new Date();
        const from  = new Date(today); from.setDate(today.getDate() - 6);
        const rows  = await api.getAttendanceReport({ from_date: fmtDate(from), to_date: fmtDate(today) });
        if (!rows || !rows.length) { setWeekData([]); setDeptData([]); return; }

        // ── Group by calendar date ──────────────────────────────────────────
        const DAY_ABBR = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
        const byDate = {};
        rows.forEach(r => {
          const d = String(r.date);              // "YYYY-MM-DD"
          if (!byDate[d]) byDate[d] = { present: 0, late: 0, absent: 0, leave: 0 };
          const s = r.status;
          if (s === "present")    byDate[d].present++;
          else if (s === "late") { byDate[d].present++; byDate[d].late++; }
          else if (s === "leave") byDate[d].leave++;
          else                    byDate[d].absent++;
        });
        // Build ordered array for last 7 days (oldest → newest)
        const days = [];
        for (let i = 6; i >= 0; i--) {
          const d = new Date(today); d.setDate(today.getDate() - i);
          const key = fmtDate(d);
          const isToday = i === 0;
          const info = byDate[key] || { present: 0, late: 0, absent: 0 };
          days.push({ day: isToday ? "Today" : DAY_ABBR[d.getDay()], ...info, isToday });
        }
        setWeekData(days);

        // ── Group by department (today only) ───────────────────────────────
        const todayKey = fmtDate(today);
        const todayRows = rows.filter(r => String(r.date) === todayKey);
        const deptMap = {};
        todayRows.forEach(r => {
          const emp  = empLookup[String(r.employee_id)] || {};
          const dept = emp.dept || "Other";
          if (!deptMap[dept]) deptMap[dept] = { name: dept, present: 0, late: 0, absent: 0, leave: 0, total: 0 };
          deptMap[dept].total++;
          const s = r.status;
          if (s === "present")    deptMap[dept].present++;
          else if (s === "late") { deptMap[dept].present++; deptMap[dept].late++; }
          else if (s === "leave") deptMap[dept].leave++;
          else                    deptMap[dept].absent++;
        });
        setDeptData(Object.values(deptMap).sort((a, b) => b.total - a.total));
      } catch(e) { console.error(e); }
      finally { setTrendsLoading(false); }
    };
    load();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch late arrivals for current month when tab opens
  useEffect(() => {
    if (activeTab !== "late") return;
    const load = async () => {
      setLateLoading(true);
      try {
        const today = new Date();
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const rows = await api.getAttendanceReport({
          from_date: fmtDate(monthStart),
          to_date:   fmtDate(today),
          status:    "late",
        });
        if (!rows || !rows.length) { setLateList([]); return; }

        // Count late arrivals per employee, enrich with name/dept from local lookup
        const empMap = {};
        rows.forEach(r => {
          const key = String(r.employee_id);
          if (!empMap[key]) {
            const info = empLookup[key] || {};
            empMap[key] = {
              name:   info.name   || "Unknown",
              dept:   info.dept   || "—",
              avatar: info.avatar || "??",
              count:  0,
            };
          }
          empMap[key].count++;
        });
        const sorted = Object.values(empMap).sort((a, b) => b.count - a.count);
        setLateList(sorted);
      } catch(e) { console.error(e); }
      finally { setLateLoading(false); }
    };
    load();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const maxPresent = weekData.length ? Math.max(...weekData.map(d => d.present + d.absent), 1) : 1;

  const tabs = [
    { key: "employee", label: "Employee Report" },
    { key: "trends",   label: "Weekly Trends" },
    { key: "late",     label: "Late Arrivals" },
  ];

  return (
    <div style={{ padding: "28px 32px", display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Header + tabs */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2 style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 18, margin: 0 }}>Reports</h2>
          <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 12, margin: "4px 0 0" }}>Analyse attendance data across your organisation</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 6, borderBottom: `1px solid ${C.border}`, paddingBottom: 0 }}>
        {tabs.map(({ key, label }) => (
          <button key={key} onClick={() => setActiveTab(key)} style={{
            background: "transparent", border: "none",
            borderBottom: `2px solid ${activeTab === key ? C.gold : "transparent"}`,
            padding: "10px 18px", marginBottom: -1,
            color: activeTab === key ? C.gold : C.textMuted,
            fontFamily: "'DM Mono', monospace", fontSize: 12,
            cursor: "pointer", transition: "all 0.2s", letterSpacing: 0.3
          }}>{label}</button>
        ))}
      </div>

      {/* Employee Report tab */}
      {activeTab === "employee" && <EmployeeAttendanceReport MOCK_OFFICES={MOCK_OFFICES} MOCK_EMPLOYEES={MOCK_EMPLOYEES} />}

      {/* Weekly Trends tab */}
      {activeTab === "trends" && (
        trendsLoading
          ? <div style={{ textAlign: "center", padding: "60px 0", color: C.textMuted, fontFamily: "'Sora', sans-serif", fontSize: 13 }}>Loading trends…</div>
          : weekData.length === 0
            ? <div style={{ textAlign: "center", padding: "60px 0", color: C.textMuted, fontFamily: "'Sora', sans-serif", fontSize: 13 }}>No data for the past 7 days</div>
            : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            {/* Bar chart — last 7 days */}
            <Card style={{ padding: 24 }}>
              <SectionHeader title="7-Day Trend" sub="Attendance counts — last 7 days" />
              <div style={{ display: "flex", alignItems: "flex-end", gap: 10, height: 140, padding: "0 4px" }}>
                {weekData.map(({ day, present, late, absent, isToday }) => (
                  <div key={day} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                    <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 2, alignItems: "stretch" }}>
                      <div style={{ height: `${(absent / maxPresent) * 120}px`, background: C.red, borderRadius: "3px 3px 0 0", opacity: 0.7, minHeight: absent > 0 ? 2 : 0 }} />
                      <div style={{ height: `${(late  / maxPresent) * 120}px`, background: C.amber, opacity: 0.8, minHeight: late > 0 ? 2 : 0 }} />
                      <div style={{ height: `${(present / maxPresent) * 120}px`, background: isToday ? C.gold : C.green, borderRadius: isToday ? "3px 3px 0 0" : 0, minHeight: present > 0 ? 4 : 0 }} />
                    </div>
                    <span style={{ fontFamily: "'DM Mono', monospace", color: isToday ? C.gold : C.textMuted, fontSize: 9 }}>{day}</span>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 14, marginTop: 12 }}>
                {[{ label: "Present", color: C.green }, { label: "Late (incl.)", color: C.amber }, { label: "Absent", color: C.red }].map(({ label, color }) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <div style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
                    <span style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 10 }}>{label}</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Department breakdown — today */}
            <Card style={{ padding: 24 }}>
              <SectionHeader title="By Department" sub="Today's attendance breakdown" />
              {deptData.length === 0
                ? <p style={{ fontFamily: "'Sora',sans-serif", color: C.textMuted, fontSize: 12, textAlign: "center", marginTop: 40 }}>No data for today</p>
                : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {deptData.map(d => {
                    const rate = d.total ? Math.round((d.present / d.total) * 100) : 0;
                    return (
                      <div key={d.name}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                          <span style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 12 }}>{d.name}</span>
                          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                            <span style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 11 }}>{d.present}/{d.total}</span>
                            <span style={{ fontFamily: "'DM Mono', monospace", color: rate >= 80 ? C.green : rate >= 60 ? C.amber : C.red, fontSize: 11 }}>{rate}%</span>
                          </div>
                        </div>
                        <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: 3, height: 5, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${rate}%`, background: rate >= 80 ? C.green : rate >= 60 ? C.amber : C.red, borderRadius: 3, transition: "width 0.5s" }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>
        )
      )}

      {/* Late Arrivals tab */}
      {activeTab === "late" && (
        <Card style={{ padding: 24 }}>
          <SectionHeader title="Monthly Late Arrivals" sub="Employees with most late arrivals this month" />
          {lateLoading
            ? <div style={{ textAlign: "center", padding: "48px 0", color: C.textMuted, fontFamily: "'Sora', sans-serif", fontSize: 13 }}>Loading…</div>
            : lateList.length === 0
              ? <div style={{ textAlign: "center", padding: "48px 0", color: C.textMuted, fontFamily: "'Sora', sans-serif", fontSize: 13 }}>
                  <div style={{ fontSize: 28, marginBottom: 10, opacity: 0.3 }}>🎉</div>
                  No late arrivals this month
                </div>
              : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
              {lateList.map(({ name, count, dept, avatar }) => (
                <div key={name} style={{ background: "rgba(245,158,11,0.04)", border: "1px solid rgba(245,158,11,0.12)", borderRadius: 12, padding: "14px 16px", display: "flex", gap: 12, alignItems: "center" }}>
                  <Avatar initials={avatar} size={36} color={C.amber} />
                  <div>
                    <p style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 12, margin: 0 }}>{name}</p>
                    <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 11, margin: "2px 0" }}>{dept}</p>
                    <p style={{ fontFamily: "'DM Mono', monospace", color: C.amber, fontSize: 13, fontWeight: 700, margin: 0 }}>
                      {count} <span style={{ color: C.textMuted, fontSize: 10, fontWeight: 400 }}>time{count !== 1 ? "s" : ""}</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

// ─── ASSIGNMENTS SCREEN (HR Admin only) ──────────────────────────────────────

function AssignmentsScreen({ MOCK_OFFICES, MOCK_MANAGERS, tenant, onManagerUpdated }) {
  const B = makeBrand(tenant);

  // Single source of truth: managers state only
  // selectedMgrId is just an ID — we always read data from managers[]
  const [managers,      setManagers]      = useState(
    MOCK_MANAGERS.map(m => ({ ...m, assignedOffices: [...(m.assignedOffices || [])] }))
  );
  const [selectedMgrId, setSelectedMgrId] = useState(null);
  const [flash,         setFlash]         = useState("");
  const [saving,        setSaving]        = useState(false);

  // Always derive selectedMgr from managers state — never stale
  const selectedMgr = managers.find(m => String(m.id) === String(selectedMgrId)) || null;

  const toggleOffice = (mgrId, officeId) => {
    setManagers(prev => prev.map(m => {
      if (String(m.id) !== String(mgrId)) return m;
      const officeIdNum = Number(officeId);
      const has = m.assignedOffices.map(Number).includes(officeIdNum);
      return {
        ...m,
        assignedOffices: has
          ? m.assignedOffices.filter(id => Number(id) !== officeIdNum)
          : [...m.assignedOffices, officeIdNum],
      };
    }));
  };

  const saveAssignment = async () => {
    if (!selectedMgr) return;
    setSaving(true);
    try {
      await api.assignOffices(selectedMgr.id, selectedMgr.assignedOffices);

      // Re-fetch from server to get exactly what was committed
      const confirmed   = await api.getManagerAssignments(selectedMgr.id);
      const savedIds    = confirmed.office_ids || [];

      // Update local state with server-confirmed values
      setManagers(prev => prev.map(m =>
        String(m.id) === String(selectedMgr.id) ? { ...m, assignedOffices: savedIds } : m
      ));

      // Propagate to parent so remounting the screen sees fresh data
      onManagerUpdated?.(selectedMgr.id, savedIds);

      setFlash("Assignments saved successfully");
      setTimeout(() => setFlash(""), 2500);
    } catch(e) {
      setFlash("Error: " + (e.detail || "Failed to save"));
      setTimeout(() => setFlash(""), 3000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: "28px 32px", display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2 style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 18, margin: 0 }}>Office Assignments</h2>
          <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 12, margin: "4px 0 0" }}>
            Assign store managers to offices · HR Admin only
          </p>
        </div>
        <button onClick={saveAssignment}
          disabled={saving || !selectedMgr}
          style={{ background: saving ? "rgba(251,191,36,0.2)" : `linear-gradient(135deg, ${B.gold}, ${B.goldDim})`,
            border: "none", borderRadius: 8, padding: "9px 20px", color: "#1a0e00",
            fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 700,
            cursor: saving || !selectedMgr ? "not-allowed" : "pointer",
            opacity: !selectedMgr ? 0.5 : 1 }}>
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>

      {flash && (
        <div style={{ background: flash.startsWith("Error") ? "rgba(239,68,68,0.1)" : "#042a1c",
          border: `1px solid ${flash.startsWith("Error") ? "rgba(239,68,68,0.3)" : "rgba(34,197,94,0.3)"}`,
          borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 16 }}>{flash.startsWith("Error") ? "⚠" : "✅"}</span>
          <span style={{ fontFamily: "'Sora', sans-serif",
            color: flash.startsWith("Error") ? C.red : C.green, fontSize: 13 }}>{flash}</span>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: selectedMgr ? "1fr 1.1fr" : "1fr", gap: 20 }}>
        {/* Manager list */}
        <div>
          <p style={{ fontFamily: "'DM Mono', monospace", color: C.textMuted, fontSize: 10,
            letterSpacing: 1.5, textTransform: "uppercase", margin: "0 0 12px" }}>
            Store Managers · {managers.length}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {managers.map(mgr => {
              const isSelected = String(selectedMgrId) === String(mgr.id);
              const assignedNames = mgr.assignedOffices
                .map(id => MOCK_OFFICES.find(o => Number(o.id) === Number(id))?.name)
                .filter(Boolean);
              return (
                <Card key={mgr.id}
                  onClick={() => setSelectedMgrId(isSelected ? null : String(mgr.id))}
                  style={{ padding: "14px 18px", cursor: "pointer",
                    borderColor: isSelected ? B.borderStrong : C.border }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <Avatar initials={mgr.avatar} size={38} color={C.blue} />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <p style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 13, margin: 0 }}>{mgr.name}</p>
                        <span style={{ fontFamily: "'DM Mono', monospace", color: C.blue, fontSize: 9,
                          background: "rgba(96,165,250,0.1)", border: "1px solid rgba(96,165,250,0.2)",
                          borderRadius: 4, padding: "1px 7px" }}>
                          {mgr.assignedOffices.length === 0 ? "No offices" : `${mgr.assignedOffices.length} office${mgr.assignedOffices.length > 1 ? "s" : ""}`}
                        </span>
                      </div>
                      <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 11, margin: 0 }}>
                        {mgr.email}
                      </p>
                      {assignedNames.length > 0 && (
                        <p style={{ fontFamily: "'Sora', sans-serif", color: C.textDim, fontSize: 10, margin: "4px 0 0" }}>
                          {assignedNames.join(" · ")}
                        </p>
                      )}
                      {mgr.assignedOffices.length === 0 && (
                        <p style={{ fontFamily: "'Sora', sans-serif", color: C.red, fontSize: 10, margin: "4px 0 0" }}>
                          ⚠ No offices assigned
                        </p>
                      )}
                    </div>
                    <span style={{ color: C.textMuted, fontSize: 14 }}>{isSelected ? "✕" : "›"}</span>
                  </div>
                </Card>
              );
            })}
            {managers.length === 0 && (
              <div style={{ textAlign: "center", padding: "32px", color: C.textMuted,
                fontFamily: "'Sora', sans-serif", fontSize: 13 }}>
                No store managers found
              </div>
            )}
          </div>
        </div>

        {/* Office assignment panel */}
        {selectedMgr && (
          <div>
            <p style={{ fontFamily: "'DM Mono', monospace", color: C.textMuted, fontSize: 10,
              letterSpacing: 1.5, textTransform: "uppercase", margin: "0 0 12px" }}>
              Assign offices to {selectedMgr.name.split(" ")[0]}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {MOCK_OFFICES.map(office => {
                // Read isAssigned from selectedMgr which is derived from managers state
                const isAssigned = selectedMgr.assignedOffices.map(Number).includes(Number(office.id));
                const assignedToOther = managers.find(m =>
                  String(m.id) !== String(selectedMgrId) &&
                  m.assignedOffices.map(Number).includes(Number(office.id))
                );
                return (
                  <div key={office.id}
                    onClick={() => toggleOffice(selectedMgr.id, office.id)}
                    style={{
                      background: isAssigned ? `${B.gold}12` : "rgba(255,255,255,0.02)",
                      border: `1px solid ${isAssigned ? B.borderStrong : C.border}`,
                      borderRadius: 12, padding: "14px 16px", cursor: "pointer",
                      display: "flex", alignItems: "center", gap: 12,
                      transition: "all 0.15s"
                    }}>
                    {/* Checkbox */}
                    <div style={{
                      width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                      border: `2px solid ${isAssigned ? B.gold : C.textMuted}`,
                      background: isAssigned ? B.gold : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      transition: "all 0.15s"
                    }}>
                      {isAssigned && <span style={{ color: "#1a0e00", fontSize: 12, fontWeight: 900 }}>✓</span>}
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 13, margin: 0 }}>
                        {office.name}
                      </p>
                      <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 11, margin: "2px 0 0" }}>
                        📍 {office.city} · {office.employeeCount || 0} employees
                      </p>
                      {assignedToOther && (
                        <p style={{ fontFamily: "'Sora', sans-serif", color: C.amber, fontSize: 10, margin: "3px 0 0" }}>
                          Also assigned to {assignedToOther.name.split(" ")[0]}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: 14, background: "rgba(255,255,255,0.02)",
              border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 16px" }}>
              <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 11, margin: 0 }}>
                <span style={{ color: B.gold, fontWeight: 600 }}>{selectedMgr.assignedOffices.length}</span>
                {" "}office{selectedMgr.assignedOffices.length !== 1 ? "s" : ""} assigned to {selectedMgr.name.split(" ")[0]}.
                {" "}Click Save Changes to apply.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ─── MANAGER MY LEAVE SCREEN ─────────────────────────────────────────────────
// Shown only to store_manager role. Lets managers apply leave to HR Admin,
// view their own balance, and track their own applications.
function ManagerMyLeaveScreen({ hr }) {
  const B = makeBrand(hr?.tenant);

  const [leaveBalance,   setLeaveBalance]   = useState([]);
  const [leaveTypes,     setLeaveTypes]     = useState([]);
  const [applications,   setApplications]   = useState([]);
  const [loadingData,    setLoadingData]    = useState(true); // eslint-disable-line

  // Apply form state
  const [showForm,    setShowForm]    = useState(false);
  const [leaveTypeId, setLeaveTypeId] = useState(null);
  const [fromDate,    setFromDate]    = useState("");
  const [toDate,      setToDate]      = useState("");
  const [reason,      setReason]      = useState("");
  const [loading,     setLoading]     = useState(false);  // eslint-disable-line
  const [submitted,   setSubmitted]   = useState(false);
  const [formError,   setFormError]   = useState("");

  const LEAVE_COLORS = [B.gold, C.blue, C.purple, C.green];

  const loadData = useCallback(async () => {
    setLoadingData(true);
    try {
      const [bal, apps, types] = await Promise.all([
        api.getMyLeaveBalance(),
        api.getMyLeaveApplications(),
        api.getLeaveTypesForEmployee().catch(() => []),
      ]);

      const typeMap = {};
      (types || []).forEach(t => { typeMap[t.id] = t.name; });

      const balMapped = (bal || []).map((b, i) => ({
        id:      b.leave_type_id,
        type:    typeMap[b.leave_type_id] || b.leave_type?.name || `Leave ${b.leave_type_id}`,
        total:   b.total_days,
        used:    parseFloat(b.used_days)    || 0,
        pending: parseFloat(b.pending_days) || 0,
        color:   LEAVE_COLORS[i % LEAVE_COLORS.length],
      }));
      setLeaveBalance(balMapped);

      setLeaveTypes(
        (types || []).length > 0
          ? (types || []).map(t => ({ id: t.id, type: t.name, is_flexible: !!t.is_flexible }))
          : balMapped.map(b => ({ id: b.id, type: b.type, is_flexible: false }))
      );

      setApplications((apps || []).map(a => ({
        id:     a.id,
        type:   a.leave_type?.name || typeMap[a.leave_type_id] || "Leave",
        from:   new Date(a.from_date).toLocaleDateString("en-IN", { day: "numeric", month: "short" }),
        to:     new Date(a.to_date).toLocaleDateString("en-IN",   { day: "numeric", month: "short" }),
        days:   parseFloat(a.total_days),
        status: a.status,
        reason: a.reason || "",
      })));
    } catch (e) { console.error(e); }
    finally { setLoadingData(false); }
  }, []); // eslint-disable-line

  useEffect(() => { loadData(); }, []); // eslint-disable-line

  const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate  = tomorrow.toISOString().split("T")[0];

  const handleFromDateChange = val => {
    setFromDate(val); setFormError("");
    if (toDate && val > toDate) setToDate("");
  };
  const handleToDateChange = val => {
    setFormError("");
    if (val < fromDate) { setFormError("End date cannot be before start date"); return; }
    setToDate(val);
  };

  const handleApply = async () => {
    setFormError("");
    if (!leaveTypeId)             { setFormError("Please select a leave type"); return; }
    if (!fromDate || !toDate)     { setFormError("Please select dates"); return; }
    if (!reason.trim())           { setFormError("Please provide a reason"); return; }
    if (fromDate < minDate)       { setFormError("Leave must start from tomorrow — backdating not allowed"); return; }
    if (reason.trim().length < 5) { setFormError("Please provide a proper reason (min 5 characters)"); return; }
    setLoading(true);
    try {
      await api.applyLeave({
        leave_type_id: leaveTypeId,
        from_date:     fromDate,
        to_date:       toDate,
        reason:        reason.trim(),
      });
      setSubmitted(true);
      await loadData();
      setTimeout(() => {
        setSubmitted(false); setShowForm(false);
        setFromDate(""); setToDate(""); setReason(""); setLeaveTypeId(null); setFormError("");
      }, 2500);
    } catch (err) {
      setFormError(err.detail || "Failed to submit application");
    } finally {
      setLoading(false);
    }
  };

  const inputSt = {
    width: "100%", boxSizing: "border-box", outline: "none",
    background: "rgba(255,255,255,0.03)", border: `1px solid ${C.border}`,
    borderRadius: 10, padding: "10px 12px", color: C.text,
    fontFamily: "'Sora', sans-serif", fontSize: 13, colorScheme: "dark",
  };

  const selectedFL = leaveTypeId && leaveTypes.find(t => t.id === leaveTypeId)?.is_flexible;

  return (
    <div style={{ padding: "28px 32px", display: "flex", flexDirection: "column", gap: 18 }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 20, margin: "0 0 4px" }}>My Leave</h2>
          <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 13, margin: 0 }}>
            Apply and track your personal leave — approved by <span style={{ color: B.gold }}>HR Admin</span>
          </p>
        </div>
        <button
          onClick={() => { setShowForm(!showForm); setFormError(""); }}
          style={{
            background: showForm ? "rgba(239,68,68,0.10)" : B.goldGlow,
            border: `1px solid ${showForm ? "rgba(239,68,68,0.3)" : B.borderStrong}`,
            borderRadius: 10, padding: "9px 20px",
            color: showForm ? C.red : B.gold,
            fontFamily: "'DM Mono', monospace", fontSize: 12, cursor: "pointer",
          }}>
          {showForm ? "✕ Cancel" : "+ Apply Leave"}
        </button>
      </div>

      {/* Leave Balance Cards */}
      <div>
        <p style={{ fontFamily: "'DM Mono', monospace", color: C.textDim, fontSize: 11,
          textTransform: "uppercase", letterSpacing: 1, margin: "0 0 10px" }}>Leave Balance</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
          {leaveBalance.map(({ type, total, used, pending, color }) => {
            const avail = total - used - pending;
            return (
              <div key={type} style={{
                background: C.surface, border: `1px solid ${C.border}`,
                borderRadius: 12, padding: "14px 16px",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 12, margin: 0 }}>{type}</p>
                  <span style={{ fontFamily: "'DM Mono', monospace", color, fontSize: 20, fontWeight: 700 }}>{avail}</span>
                </div>
                <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: 3, height: 4, overflow: "hidden", marginBottom: 6 }}>
                  <div style={{ height: "100%", width: `${total > 0 ? (avail / total) * 100 : 0}%`, background: color, borderRadius: 3 }} />
                </div>
                <p style={{ fontFamily: "'DM Mono', monospace", color: C.textMuted, fontSize: 10, margin: 0 }}>
                  {avail} avail · {used} used
                </p>
                {pending > 0 && (
                  <p style={{ fontFamily: "'DM Mono', monospace", color: C.blue, fontSize: 10, margin: "3px 0 0" }}>
                    {pending}d pending
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Apply Form */}
      {showForm && (
        <div style={{
          background: C.surface, border: `1px solid ${B.borderStrong}`,
          borderRadius: 14, padding: "20px 22px",
        }}>
          <p style={{ fontFamily: "'DM Mono', monospace", color: B.gold, fontSize: 13,
            margin: "0 0 16px", letterSpacing: 0.3 }}>Apply for Leave</p>

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {submitted && (
              <div style={{ background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.25)",
                borderRadius: 10, padding: "10px 14px", display: "flex", alignItems: "center", gap: 8 }}>
                <span>✅</span>
                <span style={{ fontFamily: "'Sora', sans-serif", color: C.green, fontSize: 13 }}>
                  Application submitted! HR Admin will review it.
                </span>
              </div>
            )}

            <ErrorBox msg={formError} />

            {/* Flexible Leave info banner */}
            {selectedFL && (
              <div style={{ background: "rgba(139,92,246,0.08)", border: "1px solid rgba(139,92,246,0.25)",
                borderRadius: 10, padding: "10px 14px", display: "flex", gap: 10, alignItems: "flex-start" }}>
                <span style={{ fontSize: 16 }}>🔀</span>
                <p style={{ fontFamily: "'Sora', sans-serif", color: "#a78bfa", fontSize: 12, margin: 0, lineHeight: 1.5 }}>
                  <strong>Flexible Leave:</strong> HR Admin will distribute your days as SL / CL / Week-Off / Absent.
                  No balance is deducted until HR Admin distributes the leave.
                </p>
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <p style={{ fontFamily: "'DM Mono', monospace", color: C.textDim, fontSize: 10,
                  textTransform: "uppercase", letterSpacing: 1, margin: "0 0 6px" }}>Leave Type</p>
                <select
                  value={leaveTypeId || ""}
                  onChange={e => { setLeaveTypeId(parseInt(e.target.value)); setFormError(""); }}
                  style={{ ...inputSt, cursor: "pointer" }}>
                  <option value="" disabled style={{ background: "#111620" }}>
                    {leaveTypes.length === 0 ? "Loading…" : "Select leave type"}
                  </option>
                  {leaveTypes.map(l => (
                    <option key={l.id} value={l.id} style={{ background: "#111620" }}>
                      {l.type}{l.is_flexible ? " (Flexible)" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <p style={{ fontFamily: "'DM Mono', monospace", color: C.textDim, fontSize: 10,
                  textTransform: "uppercase", letterSpacing: 1, margin: "0 0 6px" }}>
                  From Date <span style={{ color: C.textMuted, textTransform: "none", fontSize: 9 }}>(tomorrow+)</span>
                </p>
                <input type="date" value={fromDate} min={minDate}
                  onChange={e => handleFromDateChange(e.target.value)} style={inputSt} />
              </div>
              <div>
                <p style={{ fontFamily: "'DM Mono', monospace", color: C.textDim, fontSize: 10,
                  textTransform: "uppercase", letterSpacing: 1, margin: "0 0 6px" }}>To Date</p>
                <input type="date" value={toDate} min={fromDate || minDate}
                  onChange={e => handleToDateChange(e.target.value)} style={inputSt} />
              </div>
              <div>
                <p style={{ fontFamily: "'DM Mono', monospace", color: C.textDim, fontSize: 10,
                  textTransform: "uppercase", letterSpacing: 1, margin: "0 0 6px" }}>Reason</p>
                <input type="text" value={reason} placeholder="Brief reason for leave"
                  onChange={e => { setReason(e.target.value); setFormError(""); }}
                  style={inputSt} />
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 4 }}>
              <button
                onClick={handleApply}
                style={{
                  background: B.goldGlow, border: `1px solid ${B.borderStrong}`,
                  borderRadius: 10, padding: "10px 24px",
                  color: B.gold, fontFamily: "'DM Mono', monospace",
                  fontSize: 13, cursor: "pointer", letterSpacing: 0.3,
                }}>
                Submit Application
              </button>
              <span style={{ fontFamily: "'DM Mono', monospace", color: C.textMuted, fontSize: 11 }}>
                ⓘ No backdated applications
              </span>
            </div>
          </div>
        </div>
      )}

      {/* My Applications History */}
      <div>
        <p style={{ fontFamily: "'DM Mono', monospace", color: C.textDim, fontSize: 11,
          textTransform: "uppercase", letterSpacing: 1, margin: "0 0 10px" }}>My Applications</p>

        {applications.length === 0 ? (
          <div style={{ background: C.surface, border: `1px solid ${C.border}`,
            borderRadius: 12, padding: "32px", textAlign: "center" }}>
            <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 13, margin: 0 }}>
              No leave applications yet
            </p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {applications.map(leave => (
              <div key={leave.id} style={{
                background: C.surface, border: `1px solid ${C.border}`,
                borderRadius: 12, padding: "14px 16px",
                display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
                    <span style={{ fontFamily: "'DM Mono', monospace", color: C.text, fontSize: 13 }}>
                      {leave.type}
                    </span>
                    <span style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 12 }}>
                      {leave.from} – {leave.to} &nbsp;·&nbsp; {leave.days}d
                    </span>
                  </div>
                  <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 12, margin: 0 }}>
                    "{leave.reason}"
                  </p>
                </div>
                <StatusPill status={leave.status} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// ─── MAIN APP ─────────────────────────────────────────────────────────────────

// ─── MOCK MANAGERS & ASSIGNMENTS ─────────────────────────────────────────────




// ── TENANT RESOLVER ──
// In production this reads window.location.hostname and hits GET /tenant/config
// // e.g. "acme.attendtrack.com" → fetches tenant config for "acme"
// // Here we simulate it: localhost/demo resolves to "acme" by default
// function resolveCurrentTenant() {
//   const host = window.location.hostname;            // e.g. "acme.attendtrack.com"
//   const slug = host.split(".attendtrack.com")[0];   // "acme"
//   return { tenant_id: slug, name: "AttendTrack", primary: "#fbbf24" };
// }

function HRDashboardApp({ initialUser = null, onLogout = null }) {
  // Works in two modes:
  // 1. Standalone (/hr URL) — restores session from localStorage
  // 2. Embedded (inside EmployeePortal after HR login) — receives initialUser prop

  const buildUser = (u) => {
    if (!u) return null;
    const email = u.email || "";
    return {
      name:      u.name || email.split("@")[0].replace(/[._]/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
      email,
      role:      u.role_type === "hr_admin" ? "HR Admin" : "Store Manager",
      role_type: u.role_type,
      avatar:    u.avatar || email.slice(0, 2).toUpperCase(),
      plan:      u.plan || TokenStore.getPlan(),
      assignedOffices: u.assignedOffices || [],
      tenant:    u.tenant || { name: "Your Organisation", tenant_id: "" },
    };
  };

  const [authed, setAuthed] = useState(!!initialUser);
  const [hr, setHr]         = useState(() => buildUser(initialUser));

  const [activeView, setActiveView]   = useState("overview");
  const [selectedOffice, setSelectedOffice] = useState(null);

  // Real data from API
  const [MOCK_OFFICES,   setOffices]   = useState([]);
  const [MOCK_EMPLOYEES, setEmployees] = useState({});
  const [PENDING_LEAVES, setPendingLeaves] = useState([]);
  const [LATE_ALERTS,    setLateAlerts]    = useState([]);
  const [ALL_MANAGERS,   setManagers]      = useState([]);

  const loadData = useCallback(async () => {
    if (!authed) return;
    try {
      const [offices, employees, leaves] = await Promise.all([
        api.getOffices(),
        api.getEmployees(),
        api.getPendingLeaves().catch(() => []),
      ]);

      // Build offices array
      const officeList = (offices||[]).map(o => ({
        id:            o.id,
        tenant_id:     o.tenant_id,
        name:          o.name,
        city:          o.city || "",
        country:       o.country || "",
        address:       o.address || "",
        radius:        o.radius_meters || 100,
        lat:           parseFloat(o.latitude),
        lng:           parseFloat(o.longitude),
        employeeCount: (employees||[]).filter(e => e.office_id === o.id).length,
        presentToday:  0,
        lateToday:     0,
      }));
      // Load today's attendance stats for each office
      const officeStats = await Promise.all(
        officeList.map(async (o) => {
          try {
            const today = await api.getOfficeTodayAttendance(o.id);
            const stats = { present:0, late:0, absent:0, onLeave:0, weekOff:0 };
            const totalEmp = (employees||[]).filter(e => e.office_id === o.id).length;
            // Count statuses
            (today||[]).forEach(r => {
              if (r.status === "present")  stats.present++;
              else if (r.status === "late")     stats.late++;
              else if (r.status === "leave")    stats.onLeave++;
              else if (r.status === "week_off") stats.weekOff++;
              else if (r.status === "half_day") stats.present++;
            });
            // Absent = total - all accounted for
            const accounted = stats.present + stats.late + stats.onLeave + stats.weekOff;
            stats.absent = Math.max(0, totalEmp - accounted);
            return { ...o, ...stats,
              totalEmployees: totalEmp,
              presentToday: stats.present,
              lateToday: stats.late,
            };
          } catch(e) {
            return { ...o,
              totalEmployees: (employees||[]).filter(e => e.office_id === o.id).length,
              presentToday:0, lateToday:0, absent:0, onLeave:0, weekOff:0,
            };
          }
        })
      );
      setOffices(officeStats);

      // Build employees map by office
      const empMap = {};
      (offices||[]).forEach(o => {
        empMap[o.id] = (employees||[])
          .filter(e => e.office_id === o.id)
          .map(e => ({
            id:        e.id,
            tenant_id: e.tenant_id,
            name:      e.name,
            role:      e.role  || "",
            dept:      e.department || "",
            email:     e.email,
            shift:     `${e.shift_start?.slice(0,5)||"09:00"}–${e.shift_end?.slice(0,5)||"18:00"}`,
            role_type: e.role_type,
            status:    "absent",
            checkIn:   null, checkOut: null,
            lateBy:        0,    workingHrs: "0h",
            earlyExitMins: 0,
            lateGraceMins: e.late_grace_mins ?? 15,
            leaveBalance:  0,
            avatar:        e.name.split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2),
            isManager:     e.role_type === "store_manager",
          }));
      });
      setEmployees(empMap);

      // Managers list
      const managerList = (employees||[])
        .filter(e => e.role_type === "store_manager")
        .map(e => ({
          id:        e.id,
          name:      e.name,
          email:     e.email,
          role_type: e.role_type,
          avatar:    e.name.split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2),
          assignedOffices: [],
        }));

      // Load existing assignments for each manager
      const managersWithAssignments = await Promise.all(
        managerList.map(async (m) => {
          try {
            const res = await api.getManagerAssignments(m.id);
            return { ...m, assignedOffices: res.office_ids || [] };
          } catch {
            return m;
          }
        })
      );
      setManagers(managersWithAssignments);

      // Pending leaves — use enriched fields from the real API
      setPendingLeaves((leaves||[]).map(l => ({
        id:          l.id,
        employee:    l.employee_name   || l.employee_id || "—",
        avatar:      (l.employee_name  || "?").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2),
        type:        l.leave_type_name || "Leave",
        from:        l.from_date,
        to:          l.to_date,
        days:        parseFloat(l.total_days),
        status:      l.status,
        reason:      l.reason          || "",
        office:      l.employee_office || "",
        is_flexible: l.is_flexible     || false,
        // keep raw fields for LeavesScreen
        employee_name:   l.employee_name,
        employee_office: l.employee_office,
        leave_type_name: l.leave_type_name,
        from_date:       l.from_date,
        to_date:         l.to_date,
        total_days:      parseFloat(l.total_days),
        applied_at:      l.applied_at  || "",
      })));

      setLateAlerts([]);
    } catch(e) {
      console.error("HR data load error:", e);
    }
  }, [authed]);

  useEffect(() => { loadData(); }, []); // eslint-disable-line

  const handleSignIn = (user) => { setHr(user); setAuthed(true); };
  const handleSignOut = () => {
    setHr(null); setAuthed(false);
    setActiveView("overview"); setSelectedOffice(null);
    if (onLogout) onLogout(); // parent handles api.logout() and session clear
  };
  const handleSelectOffice = (office) => { setSelectedOffice(office); setActiveView("office-detail"); };
  const handleBackFromOffice = () => { setSelectedOffice(null); setActiveView("offices"); };

  const isHR  = hr?.role_type === "hr_admin";
  const isMgr = hr?.role_type === "store_manager";

  if (!authed) {
    // If accessed directly via /hr URL without session, show sign in
    // If inside EmployeePortal (onLogout provided), parent handles login
    if (onLogout) return null;
    return (
      <>
        <link href="https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Sora:wght@400;500;600&display=swap" rel="stylesheet" />
        <SignInScreen onSignIn={handleSignIn} />
      </>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", flexDirection: "column" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Sora:wght@400;500;600&display=swap" rel="stylesheet" />
      <HRTopNav hr={hr} activeView={activeView} setActiveView={(v) => { setActiveView(v); setSelectedOffice(null); }} onSignOut={handleSignOut} />

      {/* Manager scope banner — shown only to store managers */}
      {isMgr && (
        <div style={{ background: "rgba(96,165,250,0.06)", borderBottom: "1px solid rgba(96,165,250,0.15)", padding: "8px 32px", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ color: C.blue, fontSize: 13 }}>◉</span>
          <span style={{ fontFamily: "'Sora', sans-serif", color: C.textDim, fontSize: 12 }}>
            You are viewing <strong style={{ color: C.blue }}>{MOCK_OFFICES.length} office{MOCK_OFFICES.length !== 1 ? "s" : ""}</strong> assigned to you
            {MOCK_OFFICES.length > 0 && ": " + MOCK_OFFICES.map(o => o.name).join(", ")}
          </span>
          {MOCK_OFFICES.length === 0 && (
            <span style={{ fontFamily: "'DM Mono', monospace", color: C.amber, fontSize: 11, marginLeft: 8 }}>
              ⚠ No offices assigned — contact your HR Admin
            </span>
          )}
        </div>
      )}

      <div style={{ flex: 1, overflowY: "auto" }}>
        {activeView === "overview"      && <OverviewScreen onGoToOffice={handleSelectOffice} MOCK_OFFICES={MOCK_OFFICES} LATE_ALERTS={LATE_ALERTS} PENDING_LEAVES={PENDING_LEAVES} hr={hr} />}
        {activeView === "offices"       && !selectedOffice && <OfficesScreen onSelectOffice={handleSelectOffice} MOCK_OFFICES={MOCK_OFFICES} />}
        {activeView === "office-detail" && selectedOffice  && <OfficeDetailScreen office={selectedOffice} onBack={handleBackFromOffice} MOCK_EMPLOYEES={MOCK_EMPLOYEES} isHR={isHR}
          onEmployeeAdded={(officeId, newEmp) =>
            setEmployees(prev => ({ ...prev, [officeId]: [newEmp, ...(prev[officeId] || [])] }))
          } />}
        {activeView === "leaves"        && <LeavesScreen />}
        {activeView === "my-leave"      && isMgr && <ManagerMyLeaveScreen hr={hr} />}
        {activeView === "reports"       && <ReportsScreen MOCK_OFFICES={MOCK_OFFICES} MOCK_EMPLOYEES={MOCK_EMPLOYEES} hr={hr} />}
        {/* Assignments: HR Admin only — managers cannot access this view */}
        {activeView === "assignments"   && isHR && <AssignmentsScreen MOCK_OFFICES={MOCK_OFFICES} MOCK_MANAGERS={ALL_MANAGERS} tenant={hr?.tenant || { tenant_id: "", name: "" }}
          onManagerUpdated={(mgrId, officeIds) =>
            setManagers(prev => prev.map(m =>
              String(m.id) === String(mgrId) ? { ...m, assignedOffices: officeIds } : m
            ))
          }
        />}
        {activeView === "assignments"   && !isHR && (
          <div style={{ padding: "80px 32px", textAlign: "center" }}>
            <p style={{ fontFamily: "'DM Mono', monospace", color: C.red, fontSize: 14 }}>⛔ Access denied</p>
            <p style={{ fontFamily: "'Sora', sans-serif", color: C.textMuted, fontSize: 13 }}>Only HR Admins can manage office assignments.</p>
          </div>
        )}
      </div>
    </div>
  );
}


// ─── MAIN UNIFIED APP ────────────────────────────────────────────────────────
export default function AttendTrackApp({ defaultTab = "employee" }) {
  const savedPortal = TokenStore.getPortal();
  const savedToken  = TokenStore.get();
  const savedUser   = TokenStore.getUser();

  // Restore session if exists
  const [activePortal, setActivePortal] = useState(
    savedToken && savedPortal ? savedPortal : null
  );
  const [sessionUser, setSessionUser] = useState(() => {
    if (!savedToken || !savedUser) return null;
    return savedUser;
  });
  const [loginTab, setLoginTab] = useState(
    savedToken ? savedPortal || defaultTab : defaultTab
  );

  // Handle session expired event from api.js
  useEffect(() => {
    const handler = () => { setActivePortal(null); setSessionUser(null); };
    window.addEventListener("at_session_expired", handler);
    return () => window.removeEventListener("at_session_expired", handler);
  }, []);

  // ── Login handlers ──────────────────────────────────────────────────────────
  const handleEmployeeLogin = (user) => {
    TokenStore.saveUser({ email: user.email, name: user.name,
                          role_type: user.role_type, avatar: user.avatar });
    TokenStore.savePortal("employee");
    setSessionUser(user);
    setActivePortal("employee");
  };

  const handleHRLogin = (user) => {
    TokenStore.saveUser({ email: user.email, name: user.name,
                          role_type: user.role_type, avatar: user.avatar });
    TokenStore.savePortal("hr");
    setSessionUser(user);
    setActivePortal("hr");
  };

  // ── Logout handlers ─────────────────────────────────────────────────────────
  const handleEmployeeLogout = async () => {
    await api.logout();
    setActivePortal(null);
    setSessionUser(null);
    setLoginTab("employee"); // return to employee tab
  };

  const handleHRLogout = async () => {
    await api.logout();
    setActivePortal(null);
    setSessionUser(null);
    setLoginTab("hr"); // return to HR tab
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  // Show employee dashboard
  if (activePortal === "employee") {
    return (
      <>
        <link href="https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Sora:wght@400;500;600&display=swap" rel="stylesheet" />
        <EmployeePortalApp
          initialUser={sessionUser}
          onLogout={handleEmployeeLogout}
        />
      </>
    );
  }

  // Show HR dashboard
  if (activePortal === "hr") {
    return (
      <>
        <link href="https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Sora:wght@400;500;600&display=swap" rel="stylesheet" />
        <HRDashboardApp
          initialUser={sessionUser}
          onLogout={handleHRLogout}
        />
      </>
    );
  }

  // Show login page
  return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Sora:wght@400;500;600&display=swap" rel="stylesheet" />
      <LoginPage
        defaultTab={loginTab}
        onEmployeeLogin={handleEmployeeLogin}
        onHRLogin={handleHRLogin}
      />
    </>
  );
}
